import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { flushSync } from 'react-dom'
import {
  MousePointer2,
  MoveUpRight,
  Type,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Eye,
  LandPlot,
  Goal,
  Circle as CircleIcon,
  Hexagon,
  MapPin,
  Satellite,
  Crosshair,
  Pencil,
  X,
  Search,
  Undo2,
  Redo2,
  ZoomIn,
  ZoomOut,
  Maximize2,
  Copy,
  Trash2,
  Check,
  Loader2,
  Castle,
  MapPinned,
  Grid3x3,
  Ruler,
} from 'lucide-react'
import { createPortal } from 'react-dom'
import { POINT_ICONS, pointIconLabel } from '../strategic-points/pointIcons'
import MarkersSidebar from './MarkersSidebar'
import MarkerPropertiesPanel from './MarkerPropertiesPanel'
import { FOB_ZONE_M, fobOf } from './markers'
import ArrowPropertiesPanel from './ArrowPropertiesPanel'
import { Canvas, IText, Line, Group, Shadow, Point, Circle as FabricCircle, Polygon as FabricPolygon, ActiveSelection, FabricObject } from 'fabric'

/**
 * Nesne önbelleği kapalı. Fabric her nesneyi zoom'a bağlı bir ara tuvalde
 * önbelleğe alıyor ve zoom değiştikçe o tuvali yeniden üretiyor: zoom'un her
 * karesinde her ok, çokgen ve yazı için yeni bir ara tuval. Editördeki birkaç
 * düzine basit vektörü doğrudan çizmek bundan ucuz.
 */
FabricObject.ownDefaults.objectCaching = false
import { useAuth, isOperator } from '@/contexts/AuthContext'
import { useLanguage } from '@/contexts/LanguageContext'
import MapTileLayer from './MapTileLayer'
import { useMapLabel } from './MapSelector'
import { fetchMaps, type MapInfo } from '@/lib/maps'
import {
  MAP_WORLD_M, TOWER_ICON, ZONE_COLOR, basesOf, findZone, towerLabel, towersOf, zonesOf,
} from '@/lib/mapZones'
import { factionOf } from '@/lib/factions'
import { mapFilter, useMapViewMode } from '@/lib/mapView'
import { DRAW_SCALE } from '@/lib/drawScale'
import CalloutLayer from '@/components/callouts/CalloutLayer'
import GridLayer from './GridLayer'
import { drawGrid } from './gridCanvas'
import { liveLen, pinAt, visibleFrom } from '@/lib/liveViewport'
import RulerLayer, { type RulerHandle } from './RulerLayer'
import { DEFAULT_CALIBRATION, HOTZONE_DIAMETER_M, HOTZONE_STYLE, gameXToWorld, gameYToWorld, metersPerWorld } from '@/lib/mapScale'
import { fetchCallouts, type Callout } from '@/lib/callouts'
import {
  ChoiceField, ColorField, SliderField, editorIconButton, editorInput,
  editorLabel, editorMenu, editorMenuItem, editorMenuItemDanger, editorSurface,
  lineStyleOptions,
} from './editorUi'

// ── Arrow / draw utility functions (outside component, no closures needed) ────

/** Custom Arrow Group — subclass used only for instanceof checks and serialization.
 *  We never mutate its children; instead, we create a fresh instance on each update.
 *  Children are passed to the constructor with ABSOLUTE world coordinates — Fabric's
 *  Group constructor computes the bounding box and normalizes children internally. */
type LineStyle = 'solid' | 'dashed' | 'dotted'
type ArrowHead = 'none' | 'arrow' | 'flat'
type TextBg = 'none' | 'solid' | 'speech'

/** Convert line style to Fabric strokeDashArray.
 *  Dash boyu çizgi kalınlığına oranlıdır — ince çizgide kısa, kalın çizgide uzun.
 *  Katsayılar eski sabit değerleri sw=3'te birebir verir ([12,6] / [3,6]). */
function dashArray(ls: LineStyle, sw = 3): number[] | undefined {
  if (ls === 'dashed') return [sw * 4, sw * 2]
  if (ls === 'dotted') return [sw, sw * 2]
  return undefined
}

interface ArrowMeta {
  title: string
  text: string
  squads: { name: string; color: string }[]
  members: { id: string; name: string; avatar_url: string }[]
  startMarkerSrc: string | null
  vehicle: { id: number; name: string; speed: number } | null
}

function emptyArrowMeta(): ArrowMeta {
  return { title: '', text: '', squads: [], members: [], startMarkerSrc: null, vehicle: null }
}

class ArrowGroup extends Group {
  __isArrow = true
  __pts: { x: number; y: number }[] = []
  __clr: string = '#ef4444'
  __sw: number = 4
  __ls: LineStyle = 'solid'
  __ah: ArrowHead = 'arrow'
  __title: string = ''
  __text: string = ''
  __squads: { name: string; color: string }[] = []
  __members: { id: string; name: string; avatar_url: string }[] = []
  __startMarkerSrc: string | null = null
  __vehicle: { id: number; name: string; speed: number } | null = null

  toObject(propertiesToInclude?: any[]) {
    return {
      ...super.toObject(propertiesToInclude),
      __isArrow: true,
      __pts: this.__pts,
      __clr: this.__clr,
      __sw: this.__sw,
      __ls: this.__ls,
      __ah: this.__ah,
      __title: this.__title,
      __text: this.__text,
      __squads: this.__squads,
      __members: this.__members,
      __startMarkerSrc: this.__startMarkerSrc,
      __vehicle: this.__vehicle,
    } as any
  }
}

/** Build a fresh ArrowGroup from an array of absolute world points.
 *  Lines use absolute coords — Fabric's Group constructor handles bounds/positioning. */
function buildArrowGroup(pts: {x:number;y:number}[], color: string, sw: number, ls: LineStyle = 'solid', ah: ArrowHead = 'arrow', meta?: Partial<ArrowMeta>): ArrowGroup {
  // `sw` arayüz değeridir (`__sw` olarak saklanır); haritaya DRAW_SCALE ile basılır.
  const uiSw = sw
  sw = sw * DRAW_SCALE
  const da = dashArray(ls, sw)
  const cap = ls === 'dotted' ? 'round' : 'round'
  const objs: (Line | FabricPolygon)[] = []
  for (let i = 0; i < pts.length - 1; i++) {
    objs.push(new Line([pts[i].x, pts[i].y, pts[i+1].x, pts[i+1].y], {
      stroke: color, strokeWidth: sw, strokeLineCap: cap, strokeDashArray: da ? [...da] : undefined,
    }))
  }
  const last = pts[pts.length - 1]
  const prev = pts[pts.length - 2]
  const angle = Math.atan2(last.y - prev.y, last.x - prev.x)
  const h = Math.max(7 * DRAW_SCALE, sw * 5)

  if (ah === 'arrow') {
    objs.push(
      new Line([last.x, last.y, last.x - h * Math.cos(angle - Math.PI/6), last.y - h * Math.sin(angle - Math.PI/6)], { stroke: color, strokeWidth: sw, strokeLineCap: 'round', strokeDashArray: da ? [...da] : undefined }),
      new Line([last.x, last.y, last.x - h * Math.cos(angle + Math.PI/6), last.y - h * Math.sin(angle + Math.PI/6)], { stroke: color, strokeWidth: sw, strokeLineCap: 'round', strokeDashArray: da ? [...da] : undefined }),
    )
  } else if (ah === 'flat') {
    // Flat end cap — a perpendicular line at the tip like a road ending: --------|
    const d = Math.max(5 * DRAW_SCALE, sw * 4)
    const perpX = Math.sin(angle)
    const perpY = -Math.cos(angle)
    objs.push(new Line([
      last.x - perpX * d, last.y - perpY * d,
      last.x + perpX * d, last.y + perpY * d,
    ], { stroke: color, strokeWidth: sw, strokeLineCap: 'round' }))
  }
  // ah === 'none' → no arrowhead, just the shaft lines

  const g = new ArrowGroup(objs as any, {
    selectable: false,
    evented: true,
    hasControls: false,
    hasBorders: false,
    padding: 0,
  })
  g.__pts = pts.map(p => ({ ...p }))
  g.__clr = color
  g.__sw = uiSw
  g.__ls = ls
  g.__ah = ah
  const m = { ...emptyArrowMeta(), ...(meta ?? {}) }
  g.__title = m.title
  g.__text = m.text
  g.__squads = m.squads
  g.__members = m.members
  g.__startMarkerSrc = m.startMarkerSrc
  g.__vehicle = m.vehicle
  return g
}

/** Custom Polygon Shape — subclass used for instanceof checks and serialization.
 *  Stores absolute world-coordinate points in __pts so handle overlays can use them
 *  directly, without reverse-engineering from Fabric's internal pathOffset-based points. */
class PolygonShape extends FabricPolygon {
  __isPoly = true
  __pts: { x: number; y: number }[] = []
  __clr: string = '#ef4444'
  __sw: number = 3
  __fo: number = 0.2
  __ls: LineStyle = 'solid'

  toObject(propertiesToInclude?: any[]) {
    return {
      ...super.toObject(propertiesToInclude),
      __isPoly: true,
      __pts: this.__pts,
      __clr: this.__clr,
      __sw: this.__sw,
      __fo: this.__fo,
      __ls: this.__ls,
    } as any
  }
}

/** Build a fresh PolygonShape from absolute world points. */
function buildPolygonShape(
  pts: { x: number; y: number }[],
  color: string,
  sw: number,
  fillOpacity: number,
  ls: LineStyle = 'solid',
): PolygonShape {
  const w = sw * DRAW_SCALE
  const fillHex = Math.round(fillOpacity * 255).toString(16).padStart(2, '0')
  const poly = new PolygonShape(pts, {
    stroke: color,
    strokeWidth: w,
    fill: color + fillHex,
    strokeDashArray: dashArray(ls, w),
    strokeLineCap: ls === 'dotted' ? 'round' : undefined,
    selectable: false,
    evented: true,
    hasControls: false,
    hasBorders: false,
  })
  poly.__pts = pts.map(p => ({ ...p }))
  poly.__clr = color
  poly.__sw = sw
  poly.__fo = fillOpacity
  poly.__ls = ls
  return poly
}

/**
 * Çokgenler bir alanı işaretler; okun, çemberin ya da yazının üstüne binince
 * onları dolgusuyla örtüyordu. Hepsi tuvalin en altına iner, kendi
 * aralarındaki sıra (hangisi önce çizildi) korunur.
 */
function polygonsToBack(fc: Canvas) {
  fc.getObjects()
    .filter((o): o is PolygonShape => o instanceof PolygonShape)
    .forEach((p, i) => fc.moveObjectTo(p, i))
}

// ── Canlı viewport ─────────────────────────────────────────────────────────
//
// Zoom/pan sırasında React'e dokunulmaz: Fabric'in viewport'u her karede
// kapsayıcıya CSS değişkeni olarak yazılır (`--va`, `--ve`, `--vf`; ekran =
// dünya × va + ve/vf) ve DOM katmanları konumunu oradan alır. React state'i
// (`markerVpt`) yalnızca hareket durunca güncellenir. Eskiden her kare
// `setMarkerVpt` çağırıyordu ve 4.5k satırlık editörün tamamı, bütün simgeler,
// tile ve ızgara katmanları saniyede 60 kez yeniden hesaplanıyordu.
//
// İki tür katman var:
// - **İğneler** (simge, baloncuk, nokta, üs, ping): `pinAt` ile canlı konum,
//   boyları zoom'la değişmez.
// - **Alanlar** (ızgara, mevki, bölge, cetvel): çizgiler dünya koordinatında,
//   CSS dönüşümlü tek bir SVG grubunda (`WORLD_GROUP_STYLE`, kalınlık
//   `non-scaling-stroke`); adlar ve tutamaçlar iğne.
// - "Yeterince yakınsa göster" kararları da CSS'te (`visibleFrom`).
// Hiçbir katman zoom sırasında render olmaz (bkz. `lib/liveViewport.ts`).

/** Şekil tutamacı — iğne: konum canlı viewport'tan, boy ekran pikselinde. */
function HandleDot({ x, y, r, fill, stroke, sw, opacity = 1, cursor, onPointerDown }: {
  x: number; y: number; r: number; fill: string; stroke: string; sw: number
  opacity?: number; cursor: string; onPointerDown: (e: React.PointerEvent) => void
}) {
  const d = 2 * r + sw
  return (
    <div
      onPointerDown={onPointerDown}
      style={{
        position: 'absolute', left: 0, top: 0, width: d, height: d, boxSizing: 'border-box',
        borderRadius: '50%', background: fill, border: `${sw}px solid ${stroke}`,
        opacity, cursor, pointerEvents: 'auto',
        transform: `${pinAt(x, y)} translate(-50%, -50%)`,
      }}
    />
  )
}

/**
 * Açılış kadrajı: verilen dünya dikdörtgeni ekrana sığar, ortalanır. Kenarda
 * `FIT_PAD` px pay kalır — ızgara açıkken etiketler çerçevenin dışına düşer.
 */
const FIT_PAD = 36
function fitTransform(
  rect: { x: number; y: number; w: number; h: number },
  w: number,
  h: number,
): { scale: number; e: number; f: number } {
  const pad = Math.min(FIT_PAD, w / 8, h / 8)
  const scale = Math.max(1e-3, Math.min((w - 2 * pad) / rect.w, (h - 2 * pad) / rect.h))
  return {
    scale,
    e: (w - scale * rect.w) / 2 - scale * rect.x,
    f: (h - scale * rect.h) / 2 - scale * rect.y,
  }
}

/**
 * Hotzone her zaman çizimlerin en üstünde durur: bir planın asıl hedefi odur,
 * sonradan çizilen bir ok ya da çokgen onu örtmemeli. Kendi aralarındaki sıra
 * korunur. Tuvale her ekleme bunu çağırır; yeni eklenen hotzone zaten tepede
 * olduğu için onun eklenmesi atlanır.
 *
 * Simgeler (marker) bunun da üstündedir: onlar tuvalin değil, üstündeki DOM
 * katmanının parçası.
 */
function hotzonesToFront(fc: Canvas) {
  const zones = fc.getObjects().filter(o => o instanceof CircleShape && o.__hz)
  zones.forEach(z => fc.moveObjectTo(z, fc.getObjects().length - 1))
}

/** Çokgeni yeniden kurarken eskisinin katmanını koru (sürükleme, özellik değişimi). */
function replacePolygon(fc: Canvas, old: PolygonShape | null, fresh: PolygonShape) {
  const idx = old ? fc.getObjects().indexOf(old) : -1
  if (old) fc.remove(old)
  if (idx >= 0) fc.insertAt(idx, fresh)
  else { fc.add(fresh); polygonsToBack(fc) }
}

/** Custom Circle Shape — subclass used for instanceof checks and serialization.
 *  Stores center/radius in absolute world coordinates for handle overlay positioning. */
class CircleShape extends FabricCircle {
  __isCircleShape = true
  __cx: number = 0
  __cy: number = 0
  __r: number = 0
  __clr: string = '#ef4444'
  __sw: number = 3
  __fo: number = 0.2
  __ls: LineStyle = 'solid'
  /** Hotzone: çapı sabit (`HOTZONE_DIAMETER_M`), yalnızca taşınır. */
  __hz: boolean = false

  toObject(propertiesToInclude?: any[]) {
    return {
      ...super.toObject(propertiesToInclude),
      __isCircleShape: true,
      __hz: this.__hz,
      __cx: this.__cx,
      __cy: this.__cy,
      __r: this.__r,
      __clr: this.__clr,
      __sw: this.__sw,
      __fo: this.__fo,
      __ls: this.__ls,
    } as any
  }
}

/** Build a fresh CircleShape from center + radius in world coordinates. */
function buildCircleShape(
  cx: number,
  cy: number,
  radius: number,
  color: string,
  sw: number,
  fillOpacity: number,
  ls: LineStyle = 'solid',
  hz = false,
): CircleShape {
  // Hotzone'un görünüşü sabittir; kayıttan ne gelirse gelsin oyunun sarısı.
  if (hz) {
    color = HOTZONE_STYLE.color; sw = HOTZONE_STYLE.sw
    fillOpacity = HOTZONE_STYLE.fillOpacity; ls = 'solid'
  }
  const w = sw * DRAW_SCALE
  const fillHex = Math.round(fillOpacity * 255).toString(16).padStart(2, '0')
  const circle = new CircleShape({
    left: cx,
    top: cy,
    radius,
    originX: 'center',
    originY: 'center',
    stroke: color,
    strokeWidth: w,
    fill: color + fillHex,
    strokeDashArray: dashArray(ls, w),
    strokeLineCap: ls === 'dotted' ? 'round' : undefined,
    selectable: false,
    evented: true,
    hasControls: false,
    hasBorders: false,
  })
  circle.__cx = cx
  circle.__cy = cy
  circle.__r = radius
  circle.__clr = color
  circle.__sw = sw
  circle.__fo = fillOpacity
  circle.__ls = ls
  circle.__hz = hz
  return circle
}

/** Custom TextShape — subclass used for instanceof checks and serialization.
 *  Extends IText to preserve inline editing while adding custom handle system
 *  and background/alignment properties. */
class TextShape extends IText {
  __isTextShape = true
  __bg: TextBg = 'none'
  __clr: string = '#ef4444'
  __fs: number = 20

  toObject(propertiesToInclude?: any[]) {
    return {
      ...super.toObject(propertiesToInclude),
      __isTextShape: true,
      __bg: this.__bg,
      __clr: this.__clr,
      __fs: this.__fs,
    } as any
  }

  /** Draw background/speech bubble behind the text */
  _render(ctx: CanvasRenderingContext2D) {
    if (this.__bg !== 'none') {
      const w = this.width ?? 0
      const h = this.height ?? 0
      const padding = 8 * DRAW_SCALE
      const pw = w + padding * 2
      const ph = h + padding * 2
      const x = -w / 2 - padding
      const y = -h / 2 - padding
      const r = 6 * DRAW_SCALE
      const tailH = this.__bg === 'speech' ? 14 * DRAW_SCALE : 0

      ctx.save()
      ctx.fillStyle = 'rgba(0, 0, 0, 0.75)'
      ctx.strokeStyle = this.__clr
      ctx.lineWidth = 2 * DRAW_SCALE

      // Rounded rect path
      ctx.beginPath()
      ctx.moveTo(x + r, y)
      ctx.lineTo(x + pw - r, y)
      ctx.quadraticCurveTo(x + pw, y, x + pw, y + r)
      ctx.lineTo(x + pw, y + ph - r)
      ctx.quadraticCurveTo(x + pw, y + ph, x + pw - r, y + ph)

      if (this.__bg === 'speech') {
        // Tail at bottom center
        const tailX = 0 // center of text
        const tailW = 8 * DRAW_SCALE
        ctx.lineTo(tailX + tailW, y + ph)
        ctx.lineTo(tailX, y + ph + tailH)
        ctx.lineTo(tailX - tailW, y + ph)
      }

      ctx.lineTo(x + r, y + ph)
      ctx.quadraticCurveTo(x, y + ph, x, y + ph - r)
      ctx.lineTo(x, y + r)
      ctx.quadraticCurveTo(x, y, x + r, y)
      ctx.closePath()
      ctx.fill()
      ctx.stroke()
      ctx.restore()
    }
    super._render(ctx)
  }
}

/** Yazının zeminsiz gölgesi — ölçüleri dünya biriminde, yazıyla birlikte ölçeklenir. */
function textShadow(): Shadow {
  return new Shadow({ color: 'rgba(0,0,0,0.8)', blur: 3 * DRAW_SCALE, offsetX: DRAW_SCALE, offsetY: DRAW_SCALE })
}

/** Build a fresh TextShape from parameters. `fontSize` arayüz değeridir (`__fs`). */
function buildTextShape(
  text: string,
  x: number,
  y: number,
  color: string,
  fontSize: number,
  bg: TextBg = 'none',
): TextShape {
  const ts = new TextShape(text, {
    left: x,
    top: y,
    fontSize: fontSize * DRAW_SCALE,
    fill: color,
    fontFamily: 'Arial',
    fontWeight: 'bold',
    textAlign: 'center',
    shadow: bg === 'none' ? textShadow() : undefined,
    selectable: true,
    evented: true,
    hasControls: false,
    hasBorders: false,
  })
  ts.__bg = bg
  ts.__clr = color
  ts.__fs = fontSize
  return ts
}

type Tool = 'select' | 'arrow' | 'circle' | 'polygon' | 'text' | 'ping' | 'marker' | 'ruler' | 'hotzone'

const CANVAS_SIZE = 1080
/** Hareket durduktan sonra viewport'un React'e yazılması için bekleme, ms. */
const VPT_COMMIT_MS = 90
/** Zoom animasyonunun 60 Hz'lik kare başına hedefe yaklaşma oranı. */
const ZOOM_EASE = 0.22
// Yakınlaştırma tavanı. Piramit 32768 px'de bitiyor (Bakurani'de 16384), dünya
// ise 1080 birim — yani gerçek detay 1x bir ekranda ~30x'e, 2x ekranda ~15x'e
// kadar sürer, sonrası mevcut detayın büyütülmesidir. Marker'ı hassas
// yerleştirmek için o noktanın ötesi de faydalı olduğundan tavan yukarıda
// kalıyor. Seviye seçimi (ve DPR'ın rolü): bkz. MapTileLayer.
const MAX_ZOOM = 48

// Uzaklaştırma tabanı sabit bir sayı değil, "tüm harita ekrana sığar" ölçeğinin
// katı: pencere/sidebar boyutu değişse de haritayı kaybedecek kadar
// uzaklaşılamaz, ama kenarları rahat görmek için biraz pay kalır.
const MIN_ZOOM_FIT_FACTOR = 0.9

// ── Minimal SP type for strategy editor overlay ───────────────────────────────
interface SPItem {
  id: number
  x: number
  y: number
  label: string
  description: string
  color: string
  icon_type: string
  media: { id: number; media_type: 'image' | 'video'; url: string; filename?: string }[]
}

function getYouTubeId(url: string): string | null {
  try {
    const u = new URL(url)
    if (u.hostname === 'youtu.be') return u.pathname.slice(1).split('?')[0] || null
    if (u.hostname === 'youtube.com' || u.hostname === 'www.youtube.com') {
      if (u.pathname === '/watch') return u.searchParams.get('v')
      const m = u.pathname.match(/\/(?:embed|shorts|v)\/([^/?]+)/)
      if (m) return m[1]
    }
  } catch { /* invalid URL */ }
  return null
}

interface Props {
  strategyId: number
  initialTitle: string
  initialMapName: string
  initialCanvasJson: string
  parentStrategyTitle?: string | null
  /**
   * Bu stratejiye çizilebilir mi. Karar **backend'in** `can_edit`'inden gelir
   * (rol + bölüm + sahiplik orada birleşir) ve buraya hazır iner: editör
   * artık rolü kendi yorumlamaz, çünkü taktik defterinde yönetim olmayan biri
   * de kendi satırının sahibidir.
   */
  canEdit?: boolean
  onTitleChange: (title: string) => void
  onNavigateHome?: () => void
  renderTopbar?: (topbar: React.ReactNode) => React.ReactNode
}

interface PingMarker   { id: string; x: number; y: number; user_name: string; color: string }
interface MarkerMember { id: string; name: string; avatar_url: string }
interface MarkerSquad  { name: string; color: string }
interface PlacedMarker {
  id: string; src: string; worldX: number; worldY: number
  color: string; title: string; text: string
  squads: MarkerSquad[]; members: MarkerMember[]
  /** Yalnızca FOB'da anlamlı: alanın dönüşü, derece (bkz. `fobOf`). */
  rotation?: number
}

function normalizePlacedMarker(m: any): PlacedMarker {
  const squads = Array.isArray(m.squads)
    ? m.squads.map((s: any) => typeof s === 'string' ? { name: s, color: '#3b82f6' } : s)
    : []
  return {
    color: '#ffffff', title: '', text: '', ...m,
    squads,
    members: m.members ?? [],
  }
}


export default function StrategyEditor({
  strategyId,
  initialTitle,
  initialMapName,
  initialCanvasJson,
  parentStrategyTitle,
  canEdit,
  onTitleChange,
  onNavigateHome,
  renderTopbar,
}: Props) {
  const { user } = useAuth()
  const { T } = useLanguage()

  // Mobilde editör salt görüntülemedir: araç yok, çizim yok. Harita iki parmakla
  // yakınlaştırılır, tek parmakla kaydırılır (aşağıdaki dokunma dinleyicileri).
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches)
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)')
    const on = () => setIsMobile(mq.matches)
    mq.addEventListener('change', on)
    return () => mq.removeEventListener('change', on)
  }, [])

  // Prop verilmediyse eski davranış: yalnızca yönetim çizer. Mobilde hiç kimse
  // çizemez; `canDraw` araç rayını, çizimi, seçimi ve başlık düzenlemeyi geçitler.
  const canDraw = (canEdit ?? isOperator(user)) && !isMobile
  const canvasRef    = useRef<HTMLCanvasElement>(null)
  const fabricRef    = useRef<Canvas | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  const [tool, setTool]         = useState<Tool>('select')
  const [color, setColor]       = useState('#ef4444')
  const colorRef               = useRef('#ef4444')
  const mapName                 = initialMapName
  /** Kırıntıdaki harita adı. Katalog sonradan gelirse etiket kendini düzeltir. */
  const mapLabel = useMapLabel(mapName)
  const [title, setTitle]       = useState(initialTitle)
  const [editingTitle, setEditingTitle] = useState(false)
  const titleInputRef = useRef<HTMLInputElement>(null)

  // Sync topbar title when external rename updates the prop
  useEffect(() => { setTitle(initialTitle) }, [initialTitle])

  const [rightPanelOpen, setRightPanelOpen] = useState(true)
  const [strokeWidth, setStrokeWidth] = useState(1.5)
  const [fillOpacity, setFillOpacity] = useState(0.2)
  const [fontSize, setFontSize] = useState(20)
  const [lineStyle, setLineStyle] = useState<LineStyle>('solid')
  const [arrowHead, setArrowHead] = useState<ArrowHead>('arrow')
  const strokeWidthRef = useRef(1.5)
  const fillOpacityRef = useRef(0.2)
  const fontSizeRef    = useRef(20)
  const lineStyleRef   = useRef<LineStyle>('solid')
  const arrowHeadRef   = useRef<ArrowHead>('arrow')
  const [selectedMarker, setSelectedMarker] = useState<{ id: string; src: string } | null>(null)
  /**
   * Simge aracının imleci izleyen önizlemesi. Konum state'te değil: her fare
   * hareketi editörün tamamını render ediyordu. Önizleme DOM'da doğrudan
   * taşınır (`placeMarkerPreview`).
   */
  const markerPreviewRef = useRef<HTMLDivElement | null>(null)
  const markerPreviewPosRef = useRef<{ x: number; y: number } | null>(null)
  const placeMarkerPreview = useCallback(() => {
    const el = markerPreviewRef.current, p = markerPreviewPosRef.current
    if (!el) return
    if (!p) { el.style.visibility = 'hidden'; return }
    el.style.transform = `${pinAt(p.x, p.y)} translate(-12px, -36px)`
    el.style.visibility = 'visible'
  }, [])
  const [mapReady, setMapReady] = useState(false)

  // Salt görüntülemede (mobil ya da yetkisiz) Fabric hiçbir nesneyi hedef almaz:
  // dokunma/tıklama seçim ya da sürükleme başlatmaz, yalnızca harita kaydırılır/
  // yakınlaştırılır. Dokunma dinleyicileri viewportTransform'u doğrudan sürer.
  useEffect(() => {
    const fc = fabricRef.current
    if (!fc) return
    fc.skipTargetFind = !canDraw
    if (!canDraw) {
      fc.selection = false
      fc.discardActiveObject()
      fc.requestRenderAll()
    }
  }, [canDraw, mapReady])

  // Viewport transform for screen-space marker positioning (avoids CSS nested-transform position drift)
  const markerVptRef = useRef({ a: 1, e: 0, f: 0 })
  const [markerVpt, setMarkerVpt] = useState({ a: 1, e: 0, f: 0 })
  const vptCommitRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  /** Canlı viewport'u React'siz izleyen katmanlar (tile seçimi). */
  const vptSubsRef = useRef(new Set<(v: { a: number; e: number; f: number }) => void>())
  const subscribeVpt = useCallback((fn: (v: { a: number; e: number; f: number }) => void) => {
    vptSubsRef.current.add(fn)
    return () => { vptSubsRef.current.delete(fn) }
  }, [])
  useEffect(() => () => { if (vptCommitRef.current !== null) clearTimeout(vptCommitRef.current) }, [])

  // Placed markers — stored as DOM overlay, NOT in Fabric canvas.
  const [placedMarkers, setPlacedMarkers] = useState<PlacedMarker[]>([])
  const placedMarkersRef = useRef<PlacedMarker[]>([])
  useEffect(() => { placedMarkersRef.current = placedMarkers }, [placedMarkers])
  const [activeMarkerId, setActiveMarkerId] = useState<string | null>(null)

  /**
   * Seçili kontrol bölgesi — **slaytın kendisinde** durur, bakan kişide değil.
   *
   * Bir maç planı belli bir bölge rotasyonu için çizilir (hangi kuleler
   * oynanıyor, savaş nerede olacak); bunu herkesin ayrı ayrı seçtiği bir
   * görünüm tercihi yapmak, aynı planı iki farklı harita üstünde okumak olurdu.
   * Bu yüzden `canvas_json` ile kaydedilir ve WS'te `sp_update` ile yayılır —
   * pinlenen noktalarla aynı aile: slaytın katman durumu.
   */
  const [controlZoneId, setControlZoneId] = useState<string | null>(null)
  const controlZoneRef = useRef<string | null>(null)

  /**
   * Taraf üsleri (HQ) bakan kişinin görünüm tercihidir, slaytın değil — kontrol
   * bölgesinin aksine bir plana ait değil, haritanın sabit gerçeği. Varsayılan
   * açık; tercih tarayıcıda kalır.
   */
  const [showBases, setShowBases] = useState(() => {
    try { return localStorage.getItem('tll_map_bases') !== '0' } catch { return true }
  })
  const toggleBases = () => setShowBases(v => {
    try { localStorage.setItem('tll_map_bases', v ? '0' : '1') } catch { /* yok say */ }
    return !v
  })

  // Strategic Points overlay
  const [showStrategicPoints, setShowStrategicPoints] = useState(false)

  /**
   * Mevkiler: stratejik noktalar bölümünde çizilen, adı konmuş bölgeler.
   * Noktaların düğmesiyle aynı davranış (bakan kişinin anlık görünümü,
   * varsayılan kapalı), ama ayrı bir katman: ikisini tek düğmeye bağlamak
   * yalnızca birine bakmak isteyeni ikisine birden bakmaya zorlardı.
   */
  const [showCallouts, setShowCallouts] = useState(false)

  /**
   * Izgara her açılışta kapalı başlar ve seçim hatırlanmaz: bir kez açan kişi
   * her slaytı ızgaralı açıyordu. Haritayı okumanın bir yolu, planın parçası
   * değil; ihtiyaç anında üst çubuktan açılır.
   */
  const [showGrid, setShowGrid] = useState(false)
  const toggleGrid = () => setShowGrid(v => !v)
  /**
   * Izgara Fabric tuvaline çizilir (`before:render`, bkz. `gridCanvas.ts`);
   * çizici tuvalin init'inde bağlanır, açık/kapalı ve kalibrasyonu ref'ten okur.
   */
  const showGridRef = useRef(showGrid)
  const gridCalRef = useRef(DEFAULT_CALIBRATION)
  /** Cetvelin ölçümü kendi katmanında durur (bkz. RulerLayer). */
  const rulerRef = useRef<RulerHandle>(null)
  const [callouts, setCallouts] = useState<Callout[]>([])
  useEffect(() => {
    if (!mapName) return
    let cancelled = false
    fetchCallouts(mapName).then(rows => { if (!cancelled) setCallouts(rows) }).catch(() => {})
    return () => { cancelled = true }
  }, [mapName])
  const [mapView, , toggleMapView] = useMapViewMode()
  const [strategicPoints, setStrategicPoints] = useState<SPItem[]>([])
  const [pinnedSPIds, setPinnedSPIds] = useState<number[]>([])
  const pinnedSPIdsRef = useRef<number[]>([])
  const [selectedSPId, setSelectedSPId] = useState<number | null>(null)
  const [spLightboxUrl, setSpLightboxUrl] = useState<string | null>(null)
  const [spLightboxType, setSpLightboxType] = useState<'image' | 'video' | 'youtube'>('image')
  type SPAnnotation = { note: string; players: { id: string; name: string; avatar_url: string }[] }
  const [spAnnotations, setSpAnnotations] = useState<Record<number, SPAnnotation>>({})
  const spAnnotationsRef = useRef<Record<number, SPAnnotation>>({})
  const [spEditMode, setSpEditMode] = useState(false)
  const [spMemberSearch, setSpMemberSearch] = useState('')
  const [showSpMemberDropdown, setShowSpMemberDropdown] = useState(false)
  const [spMemberDropdownPos, setSpMemberDropdownPos] = useState({ top: 0, left: 0, width: 0 })
  const spMemberInputRef = useRef<HTMLInputElement>(null)

  // Marker search / filter
  type MarkerFilterItem =
    | { type: 'member'; id: string; name: string; avatar_url: string }
    | { type: 'squad'; name: string; color: string }
  const [markerFilter, setMarkerFilter] = useState<MarkerFilterItem | null>(null)
  const [markerSearchQuery, setMarkerSearchQuery] = useState('')
  const [showMarkerSearch, setShowMarkerSearch] = useState(false)
  const markerSearchRef = useRef<HTMLInputElement>(null)
  /** Bakanların listesi: hover ile değil tıklamayla açılır (bkz. topbar). */
  const [showViewers, setShowViewers] = useState(false)

  const currentMarkers = placedMarkers

  // Unique members & squads across all markers
  const allMarkerMembersMap = new Map<string, { id: string; name: string; avatar_url: string }>()
  const allMarkerSquadsMap  = new Map<string, { name: string; color: string }>()
  for (const m of currentMarkers) {
    for (const mem of m.members) if (mem.id) allMarkerMembersMap.set(mem.id, mem)
    for (const sq  of m.squads)  if (sq.name) allMarkerSquadsMap.set(sq.name, sq)
  }
  const q = markerSearchQuery.toLowerCase()
  const filteredMembers = [...allMarkerMembersMap.values()].filter(m => !q || m.name.toLowerCase().includes(q))
  const filteredSquads  = [...allMarkerSquadsMap.values()].filter(s => !q || s.name.toLowerCase().includes(q))
  const visibleMarkers  = markerFilter
    ? currentMarkers.filter(m =>
        markerFilter.type === 'member'
          ? m.members.some(mem => mem.id === markerFilter.id)
          : m.squads.some(sq => sq.name === markerFilter.name)
      )
    : currentMarkers

  // Ref is updated synchronously so broadcastCanvas() reads the latest value
  const updateCurrentMarkers = useCallback((updater: (prev: PlacedMarker[]) => PlacedMarker[]) => {
    const updated = updater(placedMarkersRef.current)
    placedMarkersRef.current = updated
    setPlacedMarkers(updated)
  }, [])
  const markersOverlayRef    = useRef<HTMLDivElement>(null)
  const selectionRectRef     = useRef<HTMLDivElement>(null)
  const zoomTargetRef        = useRef(1)
  // "Tüm harita sığar" ölçeği — resize'da güncellenir, uzaklaştırma tabanı buna bağlı
  const fitScaleRef          = useRef(1)
  /**
   * Sığdırılan alan: oynanabilir alan (kalibrasyon gelince), yoksa piramidin
   * tamamı. Açılış, "tümünü sığdır" ve uzaklaşma tabanı buna bakar; eskiden
   * hepsi 16 km'lik kareye göreydi ve oynanan alan ekranın ortasında küçük
   * kalıyordu.
   */
  const fitRectRef           = useRef({ x: 0, y: 0, w: CANVAS_SIZE, h: CANVAS_SIZE })
  const zoomPointRef         = useRef({ x: 0, y: 0 })
  const zoomRafRef           = useRef<number | null>(null)
  const zoomLastTsRef        = useRef<number | null>(null)
  const triggerZoomAnimRef   = useRef<() => void>(() => {})
  // Polygon tool state
  const polygonPointsRef = useRef<{ x: number; y: number }[]>([])
  const polygonTempObjectsRef = useRef<any[]>([])
  // Arrow handle overlay state
  const selectedArrowGroupRef = useRef<Group | null>(null)
  const arrowDraggingRef      = useRef(false)
  const [selectedArrowState, setSelectedArrowState] = useState<{
    pts: {x:number;y:number}[]; color: string; sw: number; ls: LineStyle; ah: ArrowHead
    title: string; text: string
    squads: { name: string; color: string }[]
    members: { id: string; name: string; avatar_url: string }[]
    startMarkerSrc: string | null
    vehicle: { id: number; name: string; speed: number } | null
  } | null>(null)
  // Arrow metadata defaults — applied to newly-drawn arrows, editable before drawing
  const [arrowMetaDefaults, setArrowMetaDefaults] = useState<ArrowMeta>(() => emptyArrowMeta())
  const arrowMetaDefaultsRef = useRef<ArrowMeta>(arrowMetaDefaults)
  useEffect(() => { arrowMetaDefaultsRef.current = arrowMetaDefaults }, [arrowMetaDefaults])
  // Polygon handle overlay state
  const selectedPolygonGroupRef = useRef<PolygonShape | null>(null)
  const polygonDraggingRef = useRef(false)
  const [selectedPolygonState, setSelectedPolygonState] = useState<{
    pts: {x:number;y:number}[]; color: string; sw: number; fillOpacity: number; ls: LineStyle
  } | null>(null)
  // Circle handle overlay state
  const selectedCircleGroupRef = useRef<CircleShape | null>(null)
  const circleDraggingRef = useRef(false)
  const [selectedCircleState, setSelectedCircleState] = useState<{
    cx: number; cy: number; radius: number; color: string; sw: number; fillOpacity: number; ls: LineStyle
  } | null>(null)
  // TextShape selection state for sidebar UI
  const [selectedTextShapeState, setSelectedTextShapeState] = useState<{
    color: string; fontSize: number; bg: TextBg
  } | null>(null)
  // Ref to track which TextShape was "first-click selected" (marker-style: click to select, then drag)
  const manuallySelectedTextShapeRef = useRef<TextShape | null>(null)

  // Undo/Redo
  const historyRef    = useRef<string[]>([])
  const historyIdxRef = useRef(-1)
  const isLoadingRef  = useRef(false)
  /**
   * Geri/ileri alma bir zamanlar **yalnızca klavyedeydi** (Ctrl+Z / Ctrl+Y):
   * düğmesi olmayan bir hareket, olmayan bir hareket gibi okunur. Düğmenin
   * sönük mü parlak mı olacağını söyleyen tek şey geçmişin indeksi, o da bir
   * ref — o yüzden ekrana inen bir kopyası tutulur.
   */
  const [historyState, setHistoryState] = useState({ canUndo: false, canRedo: false })
  const syncHistoryState = useCallback(() => {
    setHistoryState({
      canUndo: historyIdxRef.current > 0,
      canRedo: historyIdxRef.current < historyRef.current.length - 1,
    })
  }, [])

  /**
   * Kaydetme durumu. Editör 3 saniyede bir sessizce kaydediyor; sessizlik
   * "kaydedildi mi" sorusunu cevaplamaz, o yüzden üst çubukta tek bir satır
   * durur. `saved` bir sonraki değişikliğe kadar kalır — kaybolan bir onay,
   * hiç olmamış onaydır.
   */
  const [saveState, setSaveState] = useState<'idle' | 'pending' | 'saving' | 'saved'>('idle')

  // Pan / cursor refs
  const currentToolRef      = useRef<Tool>('select')
  const toolCursorRef       = useRef<string>('default')
  const rightButtonPannedRef = useRef(false)

  // WebSocket
  const wsRef = useRef<WebSocket | null>(null)
  const [connectedUsers, setConnectedUsers] = useState<{ user_id: string; user_name: string; avatar: string; color: string }[]>([])
  const connectedUsersRef = useRef(connectedUsers)
  connectedUsersRef.current = connectedUsers
  const [pings, setPings]                   = useState<PingMarker[]>([])
  const isRemoteUpdateRef = useRef(false)

  // Save timer
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Keep pinnedSPIdsRef in sync
  useEffect(() => { pinnedSPIdsRef.current = pinnedSPIds }, [pinnedSPIds])
  useEffect(() => { spAnnotationsRef.current = spAnnotations }, [spAnnotations])

  // Members list for SP player assignment
  const [memberOptions, setMemberOptions] = useState<{ id: string; username: string; display_name: string; avatar_url: string; team: string }[]>([])
  useEffect(() => {
    fetch('/api/members', { credentials: 'include' })
      .then(r => r.ok ? r.json() : [])
      .then(setMemberOptions)
      .catch(() => {})
  }, [])

  // Fetch strategic points for this map (once on mount)
  useEffect(() => {
    fetch(`/api/strategic-points?map_name=${mapName}`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : [])
      .then(setStrategicPoints)
      .catch(() => {})
  }, [mapName])

  // Keep currentToolRef in sync
  useEffect(() => { currentToolRef.current = tool }, [tool])
  // Ölçüm cetvel aracına aittir: başka araca geçince düşer.
  useEffect(() => { if (tool !== 'ruler') rulerRef.current?.set(null) }, [tool])

  // Keep drawing setting refs in sync (used inside canvas event closures)
  useEffect(() => { strokeWidthRef.current = strokeWidth }, [strokeWidth])
  useEffect(() => { fillOpacityRef.current = fillOpacity }, [fillOpacity])
  useEffect(() => { fontSizeRef.current    = fontSize    }, [fontSize])
  useEffect(() => { colorRef.current       = color       }, [color])

  // ── Overlay transform sync ─────────────────────────────────────────────────
  // Called after any zoom/pan/resize so the CSS overlay stays in sync with fabric.
  const syncOverlay = useCallback(() => {
    const fc = fabricRef.current
    if (!fc) return
    const vpt = fc.viewportTransform!
    const [a,,,, e, f] = vpt
    if (markerVptRef.current.a === a && markerVptRef.current.e === e && markerVptRef.current.f === f) return
    markerVptRef.current = { a, e, f }
    // Canlı kanal: her kare, React'siz (bkz. `pinAt`).
    const host = containerRef.current
    if (host) {
      host.style.setProperty('--va', String(a))
      host.style.setProperty('--ve', String(e))
      host.style.setProperty('--vf', String(f))
    }
    vptSubsRef.current.forEach(fn => fn({ a, e, f }))
    // Kaydedilen kanal: hareket durunca tek bir render.
    if (vptCommitRef.current !== null) clearTimeout(vptCommitRef.current)
    vptCommitRef.current = setTimeout(() => {
      vptCommitRef.current = null
      setMarkerVpt(markerVptRef.current)
    }, VPT_COMMIT_MS)
  }, [])

  // ── Marker wheel → zoom (marker elementleri pointer-events:auto olduğundan
  //    wheel eventi canvas'a ulaşamaz; buradan manuel olarak iletiyoruz) ────────
  const handleMarkerWheel = useCallback((e: React.WheelEvent) => {
    const container = containerRef.current
    if (!container) return
    e.preventDefault()
    e.stopPropagation()
    const rect = container.getBoundingClientRect()
    const clampedDelta = Math.sign(e.deltaY) * Math.min(Math.abs(e.deltaY), 100)
    zoomTargetRef.current *= 0.9965 ** clampedDelta
    zoomTargetRef.current = Math.max(
      fitScaleRef.current * MIN_ZOOM_FIT_FACTOR,
      Math.min(MAX_ZOOM, zoomTargetRef.current),
    )
    zoomPointRef.current = { x: e.clientX - rect.left, y: e.clientY - rect.top }
    triggerZoomAnimRef.current()
  }, [])

  /**
   * Düğmeyle yakınlaştırma. Tekerlek zaten var ama dizüstünde ve dokunmatik
   * yüzeyde tekerlek hareketi ölçülü değil; ayrıca "tüm haritayı gör" için
   * hangi yöne ne kadar çevrileceğini kimse bilmiyordu.
   *
   * Hedef, animasyon sürerken `zoomTargetRef`'ten, dururken **tuvalin kendi
   * ölçeğinden** okunur: ref yalnızca tekerlekle güncelleniyor, dolayısıyla
   * hiç tekerlek çevrilmemiş bir tuvalde 1 kalır ve ilk tıklamada görüntü
   * zıplardı.
   */
  const zoomBy = useCallback((factor: number) => {
    const fc = fabricRef.current
    if (!fc) return
    const base = zoomRafRef.current !== null ? zoomTargetRef.current : fc.getZoom()
    zoomTargetRef.current = Math.max(
      fitScaleRef.current * MIN_ZOOM_FIT_FACTOR,
      Math.min(MAX_ZOOM, base * factor),
    )
    zoomPointRef.current = { x: fc.getWidth() / 2, y: fc.getHeight() / 2 }
    triggerZoomAnimRef.current()
  }, [])

  /** Oynanabilir alan ekrana (bkz. `fitRectRef`) — açılıştaki kadraj. */
  const zoomFit = useCallback(() => {
    const fc = fabricRef.current
    if (!fc) return
    const { scale, e, f } = fitTransform(fitRectRef.current, fc.getWidth(), fc.getHeight())
    fitScaleRef.current = scale
    zoomTargetRef.current = scale
    fc.setViewportTransform([scale, 0, 0, scale, e, f])
    syncOverlay()
  }, [syncOverlay])

  // ── Marker imleci: marker seçiliyken cursor gizle, bırakınca geri getir ──────
  useEffect(() => {
    const fc = fabricRef.current
    if (!fc) return
    if (tool !== 'marker') return
    const cursor = selectedMarker ? 'none' : 'default'
    fc.defaultCursor = cursor
    toolCursorRef.current = cursor
  }, [selectedMarker, tool])

  // Canvas'ta artık overlay nesnesi yok — harita zeminini MapTileLayer DOM'da
  // çiziyor, ızgara da kaldırıldı. Canvas'taki her nesne kullanıcı çizimidir.

  // ── Restore arrow groups after any loadFromJSON ─────────────────────────────
  const restoreArrowGroups = useCallback((fc: Canvas, sourceJson?: string) => {
    // Parse serialized arrow data — match by __isArrow flag (type may be 'Group' or 'ArrowGroup')
    const serializedArrows: any[] = sourceJson
      ? (() => {
          try {
            return (JSON.parse(sourceJson).objects || []).filter((s: any) => s.__isArrow === true)
          } catch { return [] }
        })()
      : []
    let arrowIdx = 0

    // Collect arrow-like groups from canvas (avoid mutating while iterating)
    const groupsToProcess: Group[] = []
    fc.getObjects().forEach(obj => {
      // Skip non-group objects (no getObjects method)
      if (typeof (obj as any).getObjects !== 'function') return
      const group = obj as Group
      // Arrow signature: __isArrow flag (from deserialization) OR 3+ line children
      const hasFlag = (group as any).__isArrow === true
      const children = group.getObjects()
      const hasLineSignature = children.length >= 3 && children[0]?.type === 'line'
      if (hasFlag || hasLineSignature) {
        groupsToProcess.push(group)
      }
    })

    groupsToProcess.forEach(group => {
      const children = group.getObjects()
      const sData = serializedArrows[arrowIdx++]

      let pts: { x: number; y: number }[]
      let color: string
      let sw: number
      let ls: LineStyle
      let ah: ArrowHead
      let meta: Partial<ArrowMeta> = {}

      const readMeta = (src: any): Partial<ArrowMeta> => ({
        title: src.__title ?? '',
        text: src.__text ?? '',
        squads: Array.isArray(src.__squads) ? src.__squads : [],
        members: Array.isArray(src.__members) ? src.__members : [],
        startMarkerSrc: src.__startMarkerSrc ?? null,
        vehicle: src.__vehicle ?? null,
      })

      if (sData?.__pts?.length >= 2) {
        // Use serialized __pts directly — world coordinates, preserves intermediate waypoints
        pts = sData.__pts.map((p: any) => ({ x: p.x, y: p.y }))
        color = sData.__clr || '#ef4444'
        sw = sData.__sw || 4
        ls = sData.__ls || 'solid'
        ah = sData.__ah || 'arrow'
        meta = readMeta(sData)
      } else if ((group as any).__pts?.length >= 2) {
        // Fallback: __pts preserved on the object by Fabric's deserialization
        pts = (group as any).__pts.map((p: any) => ({ x: p.x, y: p.y }))
        color = (group as any).__clr || '#ef4444'
        sw = (group as any).__sw || 4
        ls = (group as any).__ls || 'solid'
        ah = (group as any).__ah || 'arrow'
        meta = readMeta(group as any)
      } else if (children[0]?.type === 'line') {
        // Legacy fallback: reverse-engineer from first line geometry
        const firstLine = children[0] as Line
        const p1 = new Point(firstLine.x1!, firstLine.y1!).transform(group.calcTransformMatrix())
        const p2 = new Point(firstLine.x2!, firstLine.y2!).transform(group.calcTransformMatrix())
        pts = [{ x: p1.x, y: p1.y }, { x: p2.x, y: p2.y }]
        color = (firstLine.stroke as string) || '#ef4444'
        sw = (firstLine.strokeWidth as number) || 4
        ls = 'solid'
        ah = 'arrow'
      } else {
        return
      }

      // Always rebuild as a fresh ArrowGroup with correct positioning.
      // Suppress object:added/removed side effects during the swap.
      const arrowGroup = buildArrowGroup(pts, color, sw, ls, ah, meta)
      const wasRemote = isRemoteUpdateRef.current
      isRemoteUpdateRef.current = true
      fc.remove(group)
      fc.add(arrowGroup)
      isRemoteUpdateRef.current = wasRemote
    })
  }, [])

  // ── Restore polygon shapes after any loadFromJSON ────────────────────────────
  const restorePolygonShapes = useCallback((fc: Canvas, sourceJson?: string) => {
    const serializedPolys: any[] = sourceJson
      ? (() => {
          try {
            return (JSON.parse(sourceJson).objects || []).filter((s: any) => s.__isPoly === true)
          } catch { return [] }
        })()
      : []

    const polysToProcess: FabricPolygon[] = []
    fc.getObjects().forEach(obj => {
      if (obj.type !== 'polygon') return
      polysToProcess.push(obj as FabricPolygon)
    })

    polysToProcess.forEach((poly, idx) => {
      const sData = serializedPolys[idx]

      let pts: { x: number; y: number }[]
      let color: string
      let sw: number
      let fillOpacity: number
      let ls: LineStyle

      if (sData?.__pts?.length >= 3) {
        // Use serialized __pts directly — absolute world coordinates
        pts = sData.__pts.map((p: any) => ({ x: p.x, y: p.y }))
        color = sData.__clr || '#ef4444'
        sw = sData.__sw || 3
        fillOpacity = sData.__fo ?? 0.2
        ls = sData.__ls || 'solid'
      } else if ((poly as any).__pts?.length >= 3) {
        // Fallback: __pts preserved on the object by Fabric's deserialization
        pts = (poly as any).__pts.map((p: any) => ({ x: p.x, y: p.y }))
        color = (poly as any).__clr || '#ef4444'
        sw = (poly as any).__sw || 3
        fillOpacity = (poly as any).__fo ?? 0.2
        ls = (poly as any).__ls || 'solid'
      } else {
        // Legacy fallback: reverse-engineer from Fabric's internal polygon data
        // Fabric stores points relative to pathOffset; world coords = left + width/2 + points[i]
        const rawPts = poly.points
        if (!rawPts || rawPts.length < 3) return
        const left = poly.left || 0
        const top = poly.top || 0
        const w = poly.width || 0
        const h = poly.height || 0
        pts = rawPts.map((p: any) => ({
          x: p.x + left + w / 2,
          y: p.y + top + h / 2,
        }))
        color = (poly.stroke as string) || '#ef4444'
        sw = (poly.strokeWidth as number) || 3
        const fill = poly.fill as string
        fillOpacity = fill && fill.length > 7 ? parseInt(fill.slice(-2), 16) / 255 : 0.2
        ls = 'solid'
      }

      // Rebuild as a fresh PolygonShape
      const fresh = buildPolygonShape(pts, color, sw, fillOpacity, ls)
      const wasRemote = isRemoteUpdateRef.current
      isRemoteUpdateRef.current = true
      replacePolygon(fc, poly as PolygonShape, fresh)
      isRemoteUpdateRef.current = wasRemote
    })
    // Bu değişiklikten önce kaydedilmiş slaytlarda çokgen başka şekillerin
    // üstünde durabilir; yüklenirken de alta inerler.
    polygonsToBack(fc)
  }, [])

  // ── Restore circle shapes after any loadFromJSON ─────────────────────────────
  const restoreCircleShapes = useCallback((fc: Canvas, sourceJson?: string) => {
    const serializedCircles: any[] = sourceJson
      ? (() => {
          try {
            return (JSON.parse(sourceJson).objects || []).filter((s: any) => s.__isCircleShape === true)
          } catch { return [] }
        })()
      : []

    const circlesToProcess: FabricCircle[] = []
    fc.getObjects().forEach(obj => {
      if (obj.type !== 'circle') return
      circlesToProcess.push(obj as FabricCircle)
    })

    circlesToProcess.forEach((circ, idx) => {
      const sData = serializedCircles[idx]

      let cx: number, cy: number, radius: number, color: string, sw: number, fillOpacity: number, ls: LineStyle
      let hz = false

      if (sData?.__isCircleShape) {
        cx = sData.__cx
        cy = sData.__cy
        radius = sData.__r
        color = sData.__clr || '#ef4444'
        sw = sData.__sw || 3
        fillOpacity = sData.__fo ?? 0.2
        ls = sData.__ls || 'solid'
        hz = !!sData.__hz
      } else if ((circ as any).__isCircleShape) {
        cx = (circ as any).__cx
        cy = (circ as any).__cy
        radius = (circ as any).__r
        color = (circ as any).__clr || '#ef4444'
        sw = (circ as any).__sw || 3
        fillOpacity = (circ as any).__fo ?? 0.2
        ls = (circ as any).__ls || 'solid'
        hz = !!(circ as any).__hz
      } else {
        // Legacy fallback: reverse-engineer from Fabric circle position
        // With originX/originY = 'center', left/top = center position
        cx = circ.left || 0
        cy = circ.top || 0
        radius = circ.radius || 0
        color = (circ.stroke as string) || '#ef4444'
        sw = (circ.strokeWidth as number) || 3
        const fill = circ.fill as string
        fillOpacity = fill && fill.length > 7 ? parseInt(fill.slice(-2), 16) / 255 : 0.2
        ls = 'solid'
      }

      if (radius < 1) return

      // Rebuild as a fresh CircleShape
      const fresh = buildCircleShape(cx, cy, radius, color, sw, fillOpacity, ls, hz)
      const wasRemote = isRemoteUpdateRef.current
      isRemoteUpdateRef.current = true
      fc.remove(circ)
      fc.add(fresh)
      isRemoteUpdateRef.current = wasRemote
    })
  }, [])

  // ── Restore text shapes after any loadFromJSON ──────────────────────────────
  const restoreTextShapes = useCallback((fc: Canvas, sourceJson?: string) => {
    const serializedTexts: any[] = sourceJson
      ? (() => {
          try {
            return (JSON.parse(sourceJson).objects || []).filter((s: any) => s.__isTextShape === true)
          } catch { return [] }
        })()
      : []

    const textsToProcess: IText[] = []
    fc.getObjects().forEach(obj => {
      if (obj.type !== 'i-text') return
      textsToProcess.push(obj as IText)
    })

    textsToProcess.forEach((itext, idx) => {
      const sData = serializedTexts[idx]

      let text: string, x: number, y: number, color: string, fontSize: number, bg: TextBg

      if (sData?.__isTextShape) {
        text = sData.text || 'Text'
        x = sData.left || 0
        y = sData.top || 0
        color = sData.__clr || '#ef4444'
        fontSize = sData.__fs || 20
        bg = sData.__bg || 'none'
      } else if ((itext as any).__isTextShape) {
        text = itext.text || 'Text'
        x = itext.left || 0
        y = itext.top || 0
        color = (itext as any).__clr || '#ef4444'
        fontSize = (itext as any).__fs || 20
        bg = (itext as any).__bg || 'none'
      } else {
        // Not a TextShape — skip (regular IText objects are left as-is)
        return
      }

      // Rebuild as a fresh TextShape
      const fresh = buildTextShape(text, x, y, color, fontSize, bg)
      const wasRemote = isRemoteUpdateRef.current
      isRemoteUpdateRef.current = true
      fc.remove(itext)
      fc.add(fresh)
      isRemoteUpdateRef.current = wasRemote
    })
  }, [])

  // ── History helpers ────────────────────────────────────────────────────────
  const pushHistory = useCallback(() => {
    const fc = fabricRef.current
    if (!fc || isLoadingRef.current) return
    const json = JSON.stringify(fc.toJSON())
    historyRef.current = historyRef.current.slice(0, historyIdxRef.current + 1)
    historyRef.current.push(json)
    historyIdxRef.current = historyRef.current.length - 1
    syncHistoryState()
    scheduleSave()
  }, [])  // eslint-disable-line react-hooks/exhaustive-deps

  const undo = useCallback(() => {
    const fc = fabricRef.current
    if (!fc || historyIdxRef.current <= 0) return
    selectedArrowGroupRef.current = null
    setSelectedArrowState(null)
    selectedPolygonGroupRef.current = null
    setSelectedPolygonState(null)
    selectedCircleGroupRef.current = null
    setSelectedCircleState(null)
    setSelectedTextShapeState(null)
    manuallySelectedTextShapeRef.current = null
    historyIdxRef.current--
    syncHistoryState()
    const json = historyRef.current[historyIdxRef.current]
    isLoadingRef.current = true
    fc.loadFromJSON(json).then(() => {
      fc.getObjects().filter(obj => obj.type === 'image').forEach(img => fc.remove(img))
      restoreArrowGroups(fc, json)
      restorePolygonShapes(fc, json)
      restoreCircleShapes(fc, json)
      restoreTextShapes(fc, json)
      fc.renderAll()
      scheduleSave()
      broadcastCanvas()
      setTimeout(() => { isLoadingRef.current = false }, 0)
    })
  }, [])  // eslint-disable-line react-hooks/exhaustive-deps

  const redo = useCallback(() => {
    const fc = fabricRef.current
    if (!fc || historyIdxRef.current >= historyRef.current.length - 1) return
    selectedArrowGroupRef.current = null
    setSelectedArrowState(null)
    selectedPolygonGroupRef.current = null
    setSelectedPolygonState(null)
    selectedCircleGroupRef.current = null
    setSelectedCircleState(null)
    setSelectedTextShapeState(null)
    manuallySelectedTextShapeRef.current = null
    historyIdxRef.current++
    syncHistoryState()
    const json = historyRef.current[historyIdxRef.current]
    isLoadingRef.current = true
    fc.loadFromJSON(json).then(() => {
      fc.getObjects().filter(obj => obj.type === 'image').forEach(img => fc.remove(img))
      restoreArrowGroups(fc, json)
      restorePolygonShapes(fc, json)
      restoreCircleShapes(fc, json)
      restoreTextShapes(fc, json)
      fc.renderAll()
      scheduleSave()
      broadcastCanvas()
      setTimeout(() => { isLoadingRef.current = false }, 0)
    })
  }, [])  // eslint-disable-line react-hooks/exhaustive-deps

  // ── Auto-save ──────────────────────────────────────────────────────────────
  const doSave = useCallback(async () => {
    const fc = fabricRef.current
    if (!fc) return
    setSaveState('saving')
    try {
      const canvasData = {
        ...fc.toJSON(),
        markers: placedMarkersRef.current,
        pinnedSPs: pinnedSPIdsRef.current,
        spAnnotations: spAnnotationsRef.current,
        controlZone: controlZoneRef.current,
      }
      await fetch(`/api/strategies/${strategyId}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ canvas_json: JSON.stringify(canvasData), map_name: mapName, title }),
      })
      setSaveState('saved')
    } catch { setSaveState('idle') }
  }, [strategyId, mapName, title])

  const scheduleSave = useCallback(() => {
    setSaveState('pending')
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
    saveTimerRef.current = setTimeout(() => doSave(), 3000)
  }, [doSave])

  // ── WebSocket ──────────────────────────────────────────────────────────────
  // Throttled canvas broadcast — max once per second, excludes overlay objects
  const broadcastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const broadcastPendingRef = useRef(false)
  const broadcastCanvas = useCallback(() => {
    if (isLoadingRef.current || isRemoteUpdateRef.current) return
    const ws = wsRef.current
    const fc = fabricRef.current
    if (!ws || ws.readyState !== WebSocket.OPEN || !fc) return
    if (broadcastTimerRef.current) {
      broadcastPendingRef.current = true
      return
    }
    // Canvas'ta yalnızca kullanıcı çizimleri var — filtrelenecek overlay yok
    ws.send(JSON.stringify({
      type: 'canvas_update',
      data: fc.toJSON(),
      markers: placedMarkersRef.current,
    }))
    broadcastTimerRef.current = setTimeout(() => {
      broadcastTimerRef.current = null
      if (broadcastPendingRef.current) {
        broadcastPendingRef.current = false
        broadcastCanvas()
      }
    }, 1000)
  }, [])

  // Lightweight SP-only broadcast — sends pinned SP IDs and annotations without
  // touching the canvas JSON. Receiver applies only SP state, no canvas replace.
  const broadcastSPState = useCallback(() => {
    const ws = wsRef.current
    if (!ws || ws.readyState !== WebSocket.OPEN) return
    ws.send(JSON.stringify({
      type: 'sp_update',
      pinnedSPs: pinnedSPIdsRef.current,
      spAnnotations: spAnnotationsRef.current,
      controlZone: controlZoneRef.current,
    }))
  }, [])

  // Lightweight marker-only broadcast — doesn't touch the canvas JSON.
  // Receiver updates only markers, no canvas replace.
  const broadcastMarkerState = useCallback(() => {
    const ws = wsRef.current
    if (!ws || ws.readyState !== WebSocket.OPEN) return
    ws.send(JSON.stringify({
      type: 'marker_update',
      markers: placedMarkersRef.current,
    }))
  }, [])


  /**
   * Bölge seçimi: yaz, kaydet, yay. Üçü birlikte olmalı — yalnızca state'e
   * yazmak seçimi bu sekmede bırakır, kaydetmeden yaymak ise yenilemede geri
   * alır.
   */
  const applyControlZone = useCallback((id: string | null) => {
    controlZoneRef.current = id
    setControlZoneId(id)
    scheduleSave()
    broadcastSPState()
  }, [scheduleSave, broadcastSPState])

  useEffect(() => {
    if (!user?.id) return
    const proto = location.protocol === 'https:' ? 'wss:' : 'ws:'
    const ws = new WebSocket(`${proto}//${location.host}/api/strategies/${strategyId}/ws`)
    wsRef.current = ws

    ws.onclose = () => { wsRef.current = null; setConnectedUsers([]) }

    ws.onmessage = (e) => {
      try {
        const msg = JSON.parse(e.data)
        if (msg.type === 'users_list') {
          setConnectedUsers(msg.data)
        } else if (msg.type === 'marker_update') {
          // Lightweight marker-only update — leave canvas alone
          if (Array.isArray(msg.markers)) {
            const updated = msg.markers.map(normalizePlacedMarker)
            placedMarkersRef.current = updated
            setPlacedMarkers(updated)
          }
        } else if (msg.type === 'sp_update') {
          // Lightweight SP-only update — apply pinned IDs and annotations, leave canvas alone
          if (Array.isArray(msg.pinnedSPs)) {
            setPinnedSPIds(msg.pinnedSPs)
            pinnedSPIdsRef.current = msg.pinnedSPs
          }
          if (msg.spAnnotations && typeof msg.spAnnotations === 'object') {
            setSpAnnotations(msg.spAnnotations)
            spAnnotationsRef.current = msg.spAnnotations
          }
          // `null` geçerli bir değer (bölge kapalı), o yüzden alanın **varlığına**
          // bakılır: `msg.controlZone || ...` kapatmayı hiç uygulamazdı.
          if ('controlZone' in msg) {
            const z = typeof msg.controlZone === 'string' ? msg.controlZone : null
            setControlZoneId(z)
            controlZoneRef.current = z
          }
        } else if (msg.type === 'canvas_update' && msg.sender !== user?.id) {
          const fc = fabricRef.current
          if (!fc) return
          selectedArrowGroupRef.current = null
          setSelectedArrowState(null)
          setSelectedPolygonState(null)
          setSelectedCircleState(null)
          setSelectedTextShapeState(null)
          manuallySelectedTextShapeRef.current = null
          isRemoteUpdateRef.current = true
          isLoadingRef.current = true
          if (Array.isArray(msg.markers)) {
            updateCurrentMarkers(() => msg.markers.map(normalizePlacedMarker))
          }
          // Replace all shapes — no loadFromJSON, zero flicker
          fc.renderOnAddRemove = false
          fc.getObjects().forEach(o => fc.remove(o))
          try {
            const parsed = typeof msg.data === 'string' ? JSON.parse(msg.data) : msg.data
            const objs: any[] = parsed.objects || []
            for (const s of objs) {
              if (s.__isArrow) {
                const pts = (s.__pts || []).map((p: any) => ({ x: p.x, y: p.y }))
                if (pts.length >= 2) {
                  isRemoteUpdateRef.current = true
                  const meta: Partial<ArrowMeta> = {
                    title: s.__title ?? '',
                    text: s.__text ?? '',
                    squads: Array.isArray(s.__squads) ? s.__squads : [],
                    members: Array.isArray(s.__members) ? s.__members : [],
                    startMarkerSrc: s.__startMarkerSrc ?? null,
                    vehicle: s.__vehicle ?? null,
                  }
                  fc.add(buildArrowGroup(pts, s.__clr || '#ef4444', s.__sw || 4, s.__ls || 'solid', s.__ah || 'arrow', meta))
                }
              } else if (s.__isPoly) {
                const pts = (s.__pts || []).map((p: any) => ({ x: p.x, y: p.y }))
                if (pts.length >= 3) {
                  isRemoteUpdateRef.current = true
                  fc.add(buildPolygonShape(pts, s.__clr || '#ef4444', s.__sw || 3, s.__fo ?? 0.2, s.__ls || 'solid'))
                }
              } else if (s.__isCircleShape) {
                if ((s.__r || 0) >= 1) {
                  isRemoteUpdateRef.current = true
                  fc.add(buildCircleShape(s.__cx || 0, s.__cy || 0, s.__r || 0, s.__clr || '#ef4444', s.__sw || 3, s.__fo ?? 0.2, s.__ls || 'solid', !!s.__hz))
                }
              } else if (s.__isTextShape) {
                isRemoteUpdateRef.current = true
                fc.add(buildTextShape(s.text || 'Text', s.left || 0, s.top || 0, s.__clr || '#ef4444', s.__fs || 20, s.__bg || 'none'))
              }
            }
            polygonsToBack(fc)
          } catch { /* ignore parse errors */ }
          fc.renderOnAddRemove = true
          fc.renderAll()
          setTimeout(() => {
            isLoadingRef.current = false
            isRemoteUpdateRef.current = false
          }, 100)
        } else if (msg.type === 'cursor_move') {
          // cursor sharing disabled
        } else if (msg.type === 'ping') {
          const pingId = `${msg.sender_name}-${Date.now()}`
          const { x, y } = msg.data
          const color = connectedUsersRef.current.find(u => u.user_id === msg.sender_id)?.color ?? '#f59e0b'
          setPings(prev => [...prev, { id: pingId, x, y, user_name: msg.sender_name, color }])
          setTimeout(() => setPings(prev => prev.filter(p => p.id !== pingId)), 3000)
        }
      } catch { /* ignore */ }
    }
    return () => { ws.close(); wsRef.current = null }
  }, [strategyId, user?.id])

  // ── Canvas init ────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!canvasRef.current) return
    const fc = new Canvas(canvasRef.current, {
      width: CANVAS_SIZE, height: CANVAS_SIZE,
      selection: true,
      perPixelTargetFind: true,
      selectionColor: 'rgba(0,0,0,0)',
      selectionBorderColor: 'rgba(0,0,0,0)',
      selectionLineWidth: 0,
      // Retina ölçekleme kapalı: yüksek DPI ekranda tuval 2× çözünürlükte
      // (4× piksel) tutuluyor ve her zoom karesinde baştan çiziliyordu.
      // Ölçüldü (DPR 2, 1920×1080): 26 → 38 FPS. Bedeli retina ekranda
      // çizimlerin biraz yumuşak görünmesi; deneme aşamasında.
      enableRetinaScaling: false,
    })

    fabricRef.current = fc

    // Izgara: çizimlerden önce, viewport dönüşümü uygulanmamış bağlamda.
    fc.on('before:render', ({ ctx }) => {
      if (!showGridRef.current) return
      drawGrid(ctx, fc.viewportTransform!, fc.getWidth(), fc.getHeight(), gridCalRef.current, CANVAS_SIZE)
    })

    // ── Zoom animation (ease-out toward target) ────────────────────────────
    const animateZoom = () => {
      const canvas = fabricRef.current
      if (!canvas) { zoomRafRef.current = null; return }
      // Logaritmik uzayda yaklaşılır ve eşik görelidir: mutlak 0.001 eşiği
      // yakın zoomda (×40) hedefe ~90 karede varıyordu, yani tek bir tekerlek
      // tıklaması 1.5 sn boyunca her kare çizim demekti. Adım kare süresine
      // bağlı, 60 ve 144 Hz ekranda aynı hızda.
      const now = performance.now()
      const dt = Math.min(64, now - (zoomLastTsRef.current ?? now - 16))
      zoomLastTsRef.current = now
      const current = canvas.getZoom()
      const ratio = Math.log(zoomTargetRef.current / current)
      if (Math.abs(ratio) < 0.002) {
        canvas.zoomToPoint(new Point(zoomPointRef.current.x, zoomPointRef.current.y), zoomTargetRef.current)
        syncOverlay()
        zoomRafRef.current = null
        zoomLastTsRef.current = null
        return
      }
      const t = 1 - Math.pow(1 - ZOOM_EASE, dt / 16.67)
      canvas.zoomToPoint(new Point(zoomPointRef.current.x, zoomPointRef.current.y), current * Math.exp(ratio * t))
      syncOverlay()
      zoomRafRef.current = requestAnimationFrame(animateZoom)
    }
    triggerZoomAnimRef.current = () => {
      if (zoomRafRef.current === null) zoomRafRef.current = requestAnimationFrame(animateZoom)
    }

    if (initialCanvasJson && initialCanvasJson !== '{}') {
      const fabricLoadSource: string = initialCanvasJson

      try {
        const parsed = JSON.parse(initialCanvasJson)

        if (Array.isArray(parsed.markers)) {
          const loaded = parsed.markers.map(normalizePlacedMarker)
          setPlacedMarkers(loaded)
          placedMarkersRef.current = loaded
        }

        if (typeof parsed.controlZone === 'string' || parsed.controlZone === null) {
          setControlZoneId(parsed.controlZone)
          controlZoneRef.current = parsed.controlZone
        }

        if (Array.isArray(parsed.pinnedSPs)) {
          const loaded = parsed.pinnedSPs.filter((n: any) => typeof n === 'number')
          setPinnedSPIds(loaded)
          pinnedSPIdsRef.current = loaded
        }

        if (parsed.spAnnotations && typeof parsed.spAnnotations === 'object') {
          const loaded: Record<number, SPAnnotation> = {}
          for (const [k, v] of Object.entries(parsed.spAnnotations)) {
            const id = Number(k)
            if (isNaN(id) || !v || typeof v !== 'object') continue
            const rawPlayers = Array.isArray((v as any).players) ? (v as any).players : []
            loaded[id] = {
              note: typeof (v as any).note === 'string' ? (v as any).note : '',
              players: rawPlayers.filter((p: any) => p && typeof p === 'object' && p.id).map((p: any) => ({
                id: String(p.id), name: String(p.name || ''), avatar_url: String(p.avatar_url || ''),
              })),
            }
          }
          setSpAnnotations(loaded)
          spAnnotationsRef.current = loaded
        }
      } catch { /* ignore */ }

      isLoadingRef.current = true
      fc.loadFromJSON(fabricLoadSource).then(() => {
        // Remove any stale image objects (old grid/SP/map baked into JSON)
        fc.getObjects().filter(obj => obj.type === 'image').forEach(img => fc.remove(img))

        // Restore arrow groups (convert generic Groups back to ArrowGroups)
        restoreArrowGroups(fc, fabricLoadSource)
        restorePolygonShapes(fc, fabricLoadSource)
        restoreCircleShapes(fc, fabricLoadSource)
        restoreTextShapes(fc, fabricLoadSource)

        isLoadingRef.current = false
        historyRef.current = [JSON.stringify(fc.toJSON())]
        historyIdxRef.current = 0
        fc.renderAll()
      })
    } else {
      historyRef.current = [JSON.stringify(fc.toJSON())]
      historyIdxRef.current = 0
    }

    const onChange = (opt: any) => {
      if (isRemoteUpdateRef.current || isLoadingRef.current) return
      const target = opt?.target
      // Ignore polygon temp objects during drawing
      if (polygonTempObjectsRef.current.includes(target)) return
      pushHistory(); broadcastCanvas()
    }
    const onModified = () => {
      if (isRemoteUpdateRef.current || isLoadingRef.current) return
      pushHistory(); broadcastCanvas()
    }
    // Hotzone her eklemeden sonra tepeye çıkar (bkz. `hotzonesToFront`).
    // `onChange`'den önce: kaydedilen ve yayılan sıra da bu olsun.
    fc.on('object:added',    (opt: any) => { if (!(opt?.target instanceof CircleShape && opt.target.__hz)) hotzonesToFront(fc) })
    fc.on('object:added',    onChange)
    fc.on('object:removed',  onChange)
    fc.on('object:modified', onModified)

    // ── Arrow selection via manual mouse:down click detection ────────────────
    const onArrowClick = (opt: any) => {
      if (arrowDraggingRef.current) return
      const t = currentToolRef.current
      if (t !== 'select') return
      const target = opt.target
      if (target && target instanceof ArrowGroup) {
        // Clear other selections
        selectedPolygonGroupRef.current = null; setSelectedPolygonState(null)
        selectedCircleGroupRef.current = null; setSelectedCircleState(null)
        setSelectedTextShapeState(null)
        if (manuallySelectedTextShapeRef.current) { manuallySelectedTextShapeRef.current.lockMovementX = true; manuallySelectedTextShapeRef.current.lockMovementY = true; manuallySelectedTextShapeRef.current = null }
        selectedArrowGroupRef.current = target
        setSelectedArrowState({
          pts: [...target.__pts],
          color: target.__clr,
          sw: target.__sw,
          ls: target.__ls,
          ah: target.__ah,
          title: target.__title ?? '',
          text: target.__text ?? '',
          squads: target.__squads ?? [],
          members: target.__members ?? [],
          startMarkerSrc: target.__startMarkerSrc ?? null,
          vehicle: target.__vehicle ?? null,
        })
      } else if (!target || !(target instanceof PolygonShape || target instanceof CircleShape || target instanceof TextShape)) {
        if (selectedArrowGroupRef.current) {
          selectedArrowGroupRef.current = null
          setSelectedArrowState(null)
        }
      }
    }
    fc.on('mouse:down', onArrowClick)

    // ── Polygon selection via manual mouse:down click detection ──────────────
    const onPolygonClick = (opt: any) => {
      if (polygonDraggingRef.current) return
      const t = currentToolRef.current
      if (t !== 'select') return
      const target = opt.target
      if (target && target instanceof PolygonShape) {
        // Clear other selections
        selectedArrowGroupRef.current = null; setSelectedArrowState(null)
        selectedCircleGroupRef.current = null; setSelectedCircleState(null)
        setSelectedTextShapeState(null)
        if (manuallySelectedTextShapeRef.current) { manuallySelectedTextShapeRef.current.lockMovementX = true; manuallySelectedTextShapeRef.current.lockMovementY = true; manuallySelectedTextShapeRef.current = null }
        selectedPolygonGroupRef.current = target
        setSelectedPolygonState({
          pts: [...target.__pts],
          color: target.__clr,
          sw: target.__sw,
          fillOpacity: target.__fo,
          ls: target.__ls,
        })
      } else if (!target || !(target instanceof ArrowGroup || target instanceof CircleShape || target instanceof TextShape)) {
        if (selectedPolygonGroupRef.current) {
          selectedPolygonGroupRef.current = null
          setSelectedPolygonState(null)
        }
      }
    }
    fc.on('mouse:down', onPolygonClick)

    // ── Circle selection via manual mouse:down click detection ──────────────
    const onCircleClick = (opt: any) => {
      if (circleDraggingRef.current) return
      const t = currentToolRef.current
      if (t !== 'select') return
      const target = opt.target
      if (target && target instanceof CircleShape) {
        // Clear other selections
        selectedArrowGroupRef.current = null; setSelectedArrowState(null)
        selectedPolygonGroupRef.current = null; setSelectedPolygonState(null)
        setSelectedTextShapeState(null)
        if (manuallySelectedTextShapeRef.current) { manuallySelectedTextShapeRef.current.lockMovementX = true; manuallySelectedTextShapeRef.current.lockMovementY = true; manuallySelectedTextShapeRef.current = null }
        selectedCircleGroupRef.current = target
        setSelectedCircleState({
          cx: target.__cx,
          cy: target.__cy,
          radius: target.__r,
          color: target.__clr,
          sw: target.__sw,
          fillOpacity: target.__fo,
          ls: target.__ls,
        })
      } else if (!target || !(target instanceof ArrowGroup || target instanceof PolygonShape || target instanceof TextShape)) {
        if (selectedCircleGroupRef.current) {
          selectedCircleGroupRef.current = null
          setSelectedCircleState(null)
        }
      }
    }
    fc.on('mouse:down', onCircleClick)

    // ── TextShape double-click to edit text ──────────────────────────────────
    const onTextShapeDblClick = (opt: any) => {
      if (currentToolRef.current !== 'select') return
      const target = opt.target
      if (target && target instanceof TextShape) {
        target.enterEditing()
      }
    }
    fc.on('mouse:dblclick', onTextShapeDblClick)

    // ── TextShape selection tracking for sidebar UI ───────────────────────────
    const updateTextShapeSelection = () => {
      const active = fc.getActiveObject()
      if (active && active instanceof TextShape) {
        // If switching to a different TextShape, clear the old manual selection
        if (manuallySelectedTextShapeRef.current && manuallySelectedTextShapeRef.current !== active) {
          manuallySelectedTextShapeRef.current.lockMovementX = true
          manuallySelectedTextShapeRef.current.lockMovementY = true
          manuallySelectedTextShapeRef.current = null
        }
        setSelectedTextShapeState({
          color: active.__clr,
          fontSize: active.__fs,
          bg: active.__bg,
        })
      } else {
        setSelectedTextShapeState(null)
        // Clear manual selection when a non-TextShape is selected
        if (manuallySelectedTextShapeRef.current) {
          manuallySelectedTextShapeRef.current.lockMovementX = true
          manuallySelectedTextShapeRef.current.lockMovementY = true
          manuallySelectedTextShapeRef.current = null
        }
      }
    }
    fc.on('selection:created', updateTextShapeSelection)
    fc.on('selection:updated', updateTextShapeSelection)
    fc.on('selection:cleared', () => {
      setSelectedTextShapeState(null)
      // Clear manual selection ref and re-lock movement so next click is "first click"
      if (manuallySelectedTextShapeRef.current) {
        manuallySelectedTextShapeRef.current.lockMovementX = true
        manuallySelectedTextShapeRef.current.lockMovementY = true
        manuallySelectedTextShapeRef.current = null
      }
    })

    // ── TextShape editing:exited — update state after inline editing ──────────
    const handleEditingExited = (opt: any) => {
      const target = opt.target
      if (target && target instanceof TextShape) {
        setSelectedTextShapeState({
          color: target.__clr,
          fontSize: target.__fs,
          bg: target.__bg,
        })
        // After exiting edit mode, mark as manually selected so next click allows drag
        manuallySelectedTextShapeRef.current = target
        pushHistory(); broadcastCanvas()
        target.lockMovementX = false
        target.lockMovementY = false
      }
    }
    fc.on('text:editing:exited', handleEditingExited)

    // Cursor broadcast + marker preview tracking (throttled to ~100ms)
    let lastCursorSent = 0
    fc.on('mouse:move', (opt) => {
      const t = currentToolRef.current
      if (t === 'marker') {
        const pointer = fc.getScenePoint(opt.e)
        markerPreviewPosRef.current = { x: pointer.x, y: pointer.y }
        placeMarkerPreview()
      }

      const now = Date.now()
      if (now - lastCursorSent < 100) return
      lastCursorSent = now
    })

    // ── Scroll wheel → animated zoom toward cursor ────────────────────────
    fc.on('mouse:wheel', (opt) => {
      const clampedDelta = Math.sign(opt.e.deltaY) * Math.min(Math.abs(opt.e.deltaY), 100)
      zoomTargetRef.current *= 0.9965 ** clampedDelta
      zoomTargetRef.current = Math.max(
      fitScaleRef.current * MIN_ZOOM_FIT_FACTOR,
      Math.min(MAX_ZOOM, zoomTargetRef.current),
    )
      zoomPointRef.current = { x: opt.e.offsetX, y: opt.e.offsetY }
      triggerZoomAnimRef.current()
      opt.e.preventDefault()
      opt.e.stopPropagation()
    })

    // ── Pan (left on empty canvas in select mode, or middle button) ────────
    // Registered in capture phase so we set fc.selection=false before Fabric
    // processes the event and starts its own rubber-band selection.
    let isPanning = false
    let panMoved  = false  // true if mouse actually moved during this pan gesture
    let panSkipPrev: boolean | null = null  // kaydırma öncesi skipTargetFind
    let panButton = -1
    let panLastX  = 0, panLastY = 0

    // ── Right-drag rectangle selection ──────────────────────────────────────
    let rightDragActive  = false
    let rightDragStartCX = 0, rightDragStartCY = 0
    let rightDragMoved   = false

    const clientToScene = (clientX: number, clientY: number) => {
      const contRect = container.getBoundingClientRect()
      const vpt = fc.viewportTransform!
      return {
        x: (clientX - contRect.left - vpt[4]) / vpt[0],
        y: (clientY - contRect.top  - vpt[5]) / vpt[3],
      }
    }

    const onNativeMouseDown = (e: MouseEvent) => {
      panMoved = false
      // Ignore clicks on overlay UI elements (right panel, toolbar, etc.)
      const el = e.target as HTMLElement
      if (el.closest('[data-ui-panel]') || el.closest('button') || el.closest('input') || el.closest('label')) return

      // ── Left button: pan when on empty canvas (select) or always (marker) ─
      if (e.button === 0) {
        const t = currentToolRef.current
        if (t === 'marker') {
          // If clicking on a placed marker, let the marker handler take over (select/drag)
          if (markersOverlayRef.current?.contains(e.target as Node)) return
          // In marker mode always pan on left-drag (marker placement is a click, not drag)
          e.preventDefault()
          isPanning = true
          panButton = 0
          panLastX  = e.clientX
          panLastY  = e.clientY
          fc.defaultCursor = 'grabbing'
          return
        }
        if (t === 'select') {
          const target = fc.findTarget(e as any)
          if (!target || (target as any).selectable === false) {
            e.preventDefault()
            isPanning = true
            panButton = 0
            panLastX  = e.clientX
            panLastY  = e.clientY
            fc.defaultCursor = 'grabbing'
            fc.selection = false
          }
          return
        }
      }
      // ── Middle button: universal pan ──────────────────────────────────────
      if (e.button === 1) {
        e.preventDefault()
        isPanning = true
        panButton = 1
        panLastX  = e.clientX
        panLastY  = e.clientY
        fc.defaultCursor = 'grabbing'
        fc.selection = false
        return
      }
      // ── Right button: start selection drag tracking (select mode only) ────
      if (e.button === 2) {
        rightButtonPannedRef.current = false
        if (currentToolRef.current === 'select') {
          rightDragActive  = true
          rightDragMoved   = false
          rightDragStartCX = e.clientX
          rightDragStartCY = e.clientY
        }
      }
    }

    const onNativeMouseMove = (e: MouseEvent) => {
      // ── Pan movement ──────────────────────────────────────────────────────
      if (isPanning) {
        const dx = e.clientX - panLastX
        const dy = e.clientY - panLastY
        if (!panMoved && (Math.abs(dx) > 2 || Math.abs(dy) > 2)) {
          panMoved = true
          // Kaydırma sürerken Fabric her fare hareketinde piksel bazlı nesne
          // arıyordu (`perPixelTargetFind`); çok çizimli slaytta karelerin
          // büyük kısmı oraya gidiyordu. Basışta değil burada kapatılır:
          // seçilemeyen şekle tıklamak da kaydırmayı başlatır ve o şeklin
          // seçimi aynı basışın hedef aramasına bağlı.
          panSkipPrev = fc.skipTargetFind
          fc.skipTargetFind = true
        }
        const vpt = fc.viewportTransform!.slice() as [number, number, number, number, number, number]
        vpt[4] += dx
        vpt[5] += dy
        fc.setViewportTransform(vpt)
        panLastX = e.clientX
        panLastY = e.clientY
        syncOverlay()
        return
      }
      // ── Right-drag: update selection rectangle visual ───────────────────────
      if (rightDragActive) {
        const dx = e.clientX - rightDragStartCX
        const dy = e.clientY - rightDragStartCY
        if (!rightDragMoved && (Math.abs(dx) > 3 || Math.abs(dy) > 3)) {
          rightDragMoved = true
          rightButtonPannedRef.current = true  // suppress context menu
        }
        if (rightDragMoved) {
          const selRect = selectionRectRef.current
          if (selRect) {
            const contRect = container.getBoundingClientRect()
            const x1 = rightDragStartCX - contRect.left
            const y1 = rightDragStartCY - contRect.top
            const x2 = e.clientX - contRect.left
            const y2 = e.clientY - contRect.top
            selRect.style.left    = Math.min(x1, x2) + 'px'
            selRect.style.top     = Math.min(y1, y2) + 'px'
            selRect.style.width   = Math.abs(x2 - x1) + 'px'
            selRect.style.height  = Math.abs(y2 - y1) + 'px'
            selRect.style.display = 'block'
          }
        }
      }
    }

    const onNativeMouseUp = (e: MouseEvent) => {
      // ── Pan release ───────────────────────────────────────────────────────
      if (isPanning && e.button === panButton) {
        isPanning = false
        panButton = -1
        fc.defaultCursor = toolCursorRef.current
        if (panSkipPrev !== null) { fc.skipTargetFind = panSkipPrev; panSkipPrev = null }
      }
      // ── Right-drag selection release ──────────────────────────────────────
      if (e.button === 2 && rightDragActive) {
        rightDragActive = false
        const selRect = selectionRectRef.current
        if (selRect) selRect.style.display = 'none'
        if (rightDragMoved) {
          const { x: x1, y: y1 } = clientToScene(rightDragStartCX, rightDragStartCY)
          const { x: x2, y: y2 } = clientToScene(e.clientX, e.clientY)
          const minX = Math.min(x1, x2), maxX = Math.max(x1, x2)
          const minY = Math.min(y1, y2), maxY = Math.max(y1, y2)
          const inBounds = fc.getObjects().filter(obj => {
            if ((obj as any).selectable === false) return false
            const b = obj.getBoundingRect()
            return b.left >= minX && b.top >= minY &&
              b.left + b.width <= maxX && b.top + b.height <= maxY
          })
          if (inBounds.length === 1) {
            fc.setActiveObject(inBounds[0])
            fc.renderAll()
          } else if (inBounds.length > 1) {
            const sel = new ActiveSelection(inBounds, { canvas: fc })
            fc.setActiveObject(sel)
            fc.renderAll()
          }
          rightDragMoved = false
        }
      }
    }

    // ── Click-to-select before drag: lock movement on first click ────────────
    // mouse:down fires before Fabric updates the active object, so
    // getActiveObject() still holds the *previous* selection at that moment.
    let lockedFabricObj: any = null
    fc.on('mouse:down', (opt: any) => {
      if (currentToolRef.current !== 'select') return
      const target = opt.target
      if (!target || target.selectable === false) return

      // TextShape: marker-style selection — first click selects (locks movement),
      // second click on the same TextShape allows drag.
      if (target instanceof TextShape) {
        if (manuallySelectedTextShapeRef.current !== target) {
          // First click: select but lock movement
          target.lockMovementX = true
          target.lockMovementY = true
          manuallySelectedTextShapeRef.current = target
          // Don't set lockedFabricObj — we don't want mouse:up to unlock it
        } else {
          // Second click on same TextShape: allow drag
          target.lockMovementX = false
          target.lockMovementY = false
        }
        return
      }

      // Non-TextShape selectable objects: standard lock-on-first-click behavior
      const active = fc.getActiveObject()
      const inSelection = active && typeof (active as any).getObjects === 'function' &&
        (active as any).getObjects().includes(target)
      if (active !== target && !inSelection) {
        target.lockMovementX = true
        target.lockMovementY = true
        lockedFabricObj = target
      }
    })

    // Deselect active DOM marker on any canvas click; unlock any movement-locked object
    const handleFabricMouseUp = (opt: any) => {
      // If the mouse was dragged (panning), don't close any open panels
      if (panMoved) {
        panMoved = false
        if (lockedFabricObj) {
          lockedFabricObj.lockMovementX = false
          lockedFabricObj.lockMovementY = false
          lockedFabricObj = null
        }
        return
      }
      setActiveMarkerId(null)
      // Clear SP selection when clicking empty canvas (SP icons are DOM elements above canvas and stop propagation)
      if (!opt?.target) setSelectedSPId(null)
      if (lockedFabricObj) {
        lockedFabricObj.lockMovementX = false
        lockedFabricObj.lockMovementY = false
        lockedFabricObj = null
      }
      // Don't unlock TextShape here — it stays locked until second click or deselection
    }
    fc.on('mouse:up', handleFabricMouseUp)

    // ── Context menu — native listener on container so Fabric's upper-canvas is covered ──
    const onContainerContextMenu = (e: MouseEvent) => {
      e.preventDefault()
      if (rightButtonPannedRef.current) { rightButtonPannedRef.current = false; return }
      // Çokgen çizilirken sağ tık son köşeyi geri alır; altındaki şeklin
      // menüsünü açmak o hareketi ikiye bölerdi.
      if (currentToolRef.current === 'polygon' && polygonPointsRef.current.length > 0) return
      const fc2 = fabricRef.current
      if (!fc2) return
      const target = fc2.findTarget(e as any)
      if (!target) return
      // Allow context menu for selectable objects and custom shapes (Arrow/Polygon/Circle/TextShape)
      const isCustomShape = target instanceof ArrowGroup || target instanceof PolygonShape || target instanceof CircleShape || target instanceof TextShape
      if (!isCustomShape && (target as any).selectable === false) return
      // For custom shapes, manually select them for the sidebar UI
      if (isCustomShape && target instanceof ArrowGroup) {
        selectedArrowGroupRef.current = target
        setSelectedArrowState({
          pts: [...(target as any).__pts],
          color: (target as any).__clr,
          sw: (target as any).__sw,
          ls: (target as any).__ls,
          ah: (target as any).__ah,
          title: (target as any).__title ?? '',
          text: (target as any).__text ?? '',
          squads: (target as any).__squads ?? [],
          members: (target as any).__members ?? [],
          startMarkerSrc: (target as any).__startMarkerSrc ?? null,
          vehicle: (target as any).__vehicle ?? null,
        })
      } else if (isCustomShape && target instanceof PolygonShape) {
        selectedPolygonGroupRef.current = target
        setSelectedPolygonState({
          pts: [...(target as any).__pts],
          color: (target as any).__clr,
          sw: (target as any).__sw,
          fillOpacity: (target as any).__fo,
          ls: (target as any).__ls,
        })
      } else if (isCustomShape && target instanceof CircleShape) {
        selectedCircleGroupRef.current = target
        setSelectedCircleState({
          cx: (target as any).__cx,
          cy: (target as any).__cy,
          radius: (target as any).__r,
          color: (target as any).__clr,
          sw: (target as any).__sw,
          fillOpacity: (target as any).__fo,
          ls: (target as any).__ls,
        })
      } else if (!isCustomShape) {
        fc2.setActiveObject(target)
        fc2.renderAll()
      }
      setContextMenu({ x: e.clientX, y: e.clientY, type: 'fabric' })
    }

    const container = containerRef.current!
    container.addEventListener('mousedown',    onNativeMouseDown,   { capture: true })
    container.addEventListener('contextmenu',  onContainerContextMenu)
    window.addEventListener('mousemove', onNativeMouseMove)
    window.addEventListener('mouseup',   onNativeMouseUp)

    // ── Dokunma: tek parmak kaydırma, iki parmak pinch-zoom ──────────────────
    // Çizim dokunmayla yapılmaz (mobilde salt görüntüleme); bu yüzden dokunma
    // her zaman haritayı kaydırır/yakınlaştırır. viewportTransform doğrudan
    // sürülür, Fabric hedef bulması devrede değildir (skipTargetFind).
    let touchMode: 'none' | 'pan' | 'pinch' = 'none'
    let touchLastX = 0, touchLastY = 0
    let pinchStartDist = 1, pinchStartZoom = 1
    const touchDist = (a: Touch, b: Touch) => Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY)
    const clampZoom = (z: number) =>
      Math.max(fitScaleRef.current * MIN_ZOOM_FIT_FACTOR, Math.min(MAX_ZOOM, z))

    const onTouchStart = (e: TouchEvent) => {
      // Üstteki arayüz parçalarına (düğmeler, paneller) dokunmayı geçiştirme.
      const el = e.target as HTMLElement
      if (el.closest('[data-ui-panel]') || el.closest('button') || el.closest('input') || el.closest('label')) return
      if (e.touches.length === 1) {
        touchMode = 'pan'
        touchLastX = e.touches[0].clientX
        touchLastY = e.touches[0].clientY
      } else if (e.touches.length >= 2) {
        touchMode = 'pinch'
        pinchStartDist = touchDist(e.touches[0], e.touches[1]) || 1
        pinchStartZoom = fc.getZoom()
        e.preventDefault()
      }
    }
    const onTouchMove = (e: TouchEvent) => {
      if (touchMode === 'pan' && e.touches.length === 1) {
        const dx = e.touches[0].clientX - touchLastX
        const dy = e.touches[0].clientY - touchLastY
        const vpt = fc.viewportTransform!.slice() as [number, number, number, number, number, number]
        vpt[4] += dx
        vpt[5] += dy
        fc.setViewportTransform(vpt)
        touchLastX = e.touches[0].clientX
        touchLastY = e.touches[0].clientY
        syncOverlay()
        e.preventDefault()
      } else if (touchMode === 'pinch' && e.touches.length >= 2) {
        const dist = touchDist(e.touches[0], e.touches[1])
        const z = clampZoom(pinchStartZoom * (dist / pinchStartDist))
        const rect = container.getBoundingClientRect()
        const midX = (e.touches[0].clientX + e.touches[1].clientX) / 2 - rect.left
        const midY = (e.touches[0].clientY + e.touches[1].clientY) / 2 - rect.top
        fc.zoomToPoint(new Point(midX, midY), z)
        zoomTargetRef.current = z  // tekerlek/zoom kümesiyle aynı hedefte kal
        syncOverlay()
        e.preventDefault()
      }
    }
    const onTouchEnd = (e: TouchEvent) => {
      if (e.touches.length === 0) { touchMode = 'none'; return }
      // Pinch'ten tek parmağa düşünce kaydırmayı yeni parmaktan sürdür.
      touchMode = 'pan'
      touchLastX = e.touches[0].clientX
      touchLastY = e.touches[0].clientY
    }
    container.addEventListener('touchstart', onTouchStart, { passive: false })
    container.addEventListener('touchmove',  onTouchMove,  { passive: false })
    container.addEventListener('touchend',   onTouchEnd)
    container.addEventListener('touchcancel', onTouchEnd)

    // ── Resize — preserve zoom/pan, debounce to avoid glitch during CSS transitions ──
    let initialResize = true
    const resizeCanvas = () => {
      if (!containerRef.current) return
      const { width, height } = containerRef.current.getBoundingClientRect()
      const currentVpt = fc.viewportTransform!
      fc.setDimensions({ width, height })
      const fit = fitTransform(fitRectRef.current, width, height)
      fitScaleRef.current = fit.scale
      if (initialResize) {
        fc.setViewportTransform([fit.scale, 0, 0, fit.scale, fit.e, fit.f])
        zoomTargetRef.current = fit.scale
        initialResize = false
      } else {
        // Preserve current zoom and pan — only canvas dimensions changed
        fc.setViewportTransform([currentVpt[0], 0, 0, currentVpt[3], currentVpt[4], currentVpt[5]])
      }
      syncOverlay()
    }
    // Debounce non-initial resizes so sidebar CSS transitions (300ms) don't cause map drift
    let resizeTimer: ReturnType<typeof setTimeout> | null = null
    const scheduleResize = () => {
      if (initialResize) { resizeCanvas(); return }
      if (resizeTimer) clearTimeout(resizeTimer)
      resizeTimer = setTimeout(resizeCanvas, 350)
    }
    resizeCanvas()
    syncOverlay()  // Initial sync after first resize
    const ro = new ResizeObserver(scheduleResize)
    if (containerRef.current) ro.observe(containerRef.current)

    return () => {
      if (zoomRafRef.current !== null) cancelAnimationFrame(zoomRafRef.current)
      ro.disconnect()
      if (resizeTimer) clearTimeout(resizeTimer)
      container.removeEventListener('mousedown',   onNativeMouseDown,   { capture: true })
      container.removeEventListener('contextmenu', onContainerContextMenu)
      container.removeEventListener('touchstart',  onTouchStart)
      container.removeEventListener('touchmove',   onTouchMove)
      container.removeEventListener('touchend',    onTouchEnd)
      container.removeEventListener('touchcancel', onTouchEnd)
      window.removeEventListener('mousemove', onNativeMouseMove)
      window.removeEventListener('mouseup',   onNativeMouseUp)
      fc.off('mouse:down', onArrowClick)
      fc.off('mouse:down', onPolygonClick)
      fc.off('mouse:down', onCircleClick)
      fc.off('mouse:dblclick', onTextShapeDblClick)
      fc.off('selection:created')
      fc.off('selection:updated')
      fc.off('selection:cleared')
      fc.off('text:editing:exited', handleEditingExited)
      if (saveTimerRef.current) { clearTimeout(saveTimerRef.current); saveTimerRef.current = null; doSave() }
      fc.dispose()
      fabricRef.current = null
    }

  }, [])  // eslint-disable-line react-hooks/exhaustive-deps

  // ── Map metadata (tile pyramid config) ────────────────────────────────────
  // Zemin artık Fabric backgroundImage değil; canvas'ın altındaki MapTileLayer
  // aynı viewport transform'unu kullanarak tile'ları çiziyor.
  const [mapInfo, setMapInfo] = useState<MapInfo | null>(null)
  /**
   * Oynanabilir alan, editör dünyasında. Zemin bunun dışını hiç istemez ve
   * kırpar: piramit denizi ve harita kenarını da kapsıyor, oralarda plan çizilmez.
   * Kalibrasyonu olmayan harita kırpılmaz.
   */
  const playableClip = useMemo(() => {
    const cal = mapInfo?.game
    if (!cal) return null
    const x = gameXToWorld(cal.bounds.minX, cal, CANVAS_SIZE)
    const y = gameYToWorld(cal.bounds.maxY, cal, CANVAS_SIZE)
    return {
      x, y,
      w: gameXToWorld(cal.bounds.maxX, cal, CANVAS_SIZE) - x,
      h: gameYToWorld(cal.bounds.minY, cal, CANVAS_SIZE) - y,
    }
  }, [mapInfo])
  // Kalibrasyon gelince kadraj oynanabilir alana oturur. Editör `mapReady`'e
  // kadar gizli olduğu için bu bir zıplama olarak görünmez.
  useEffect(() => {
    fitRectRef.current = playableClip ?? { x: 0, y: 0, w: CANVAS_SIZE, h: CANVAS_SIZE }
    zoomFit()
  }, [playableClip, zoomFit])
  useEffect(() => {
    showGridRef.current = showGrid
    gridCalRef.current = mapInfo?.game ?? DEFAULT_CALIBRATION
    fabricRef.current?.requestRenderAll()
  }, [showGrid, mapInfo])
  useEffect(() => {
    if (!mapName) { setMapInfo(null); setMapReady(true); return }
    let cancelled = false
    setMapReady(false)
    fetchMaps()
      .then(maps => {
        if (cancelled) return
        setMapInfo(maps.find(m => m.name === mapName) ?? null)
      })
      .catch(() => { if (!cancelled) setMapInfo(null) })
      .finally(() => { if (!cancelled) setMapReady(true) })
    return () => { cancelled = true }
  }, [mapName])

  // ── Tool switching ─────────────────────────────────────────────────────────
  useEffect(() => {
    const fc = fabricRef.current
    if (!fc) return
    // Draw tool is handled manually (vector-like), not via PencilBrush
    fc.isDrawingMode = false
    fc.selection     = false  // rubber-band handled via right-drag natively
    // Clear custom shape selections when leaving select mode
    if (tool !== 'select') {
      selectedArrowGroupRef.current = null
      setSelectedArrowState(null)
      selectedPolygonGroupRef.current = null
      setSelectedPolygonState(null)
      selectedCircleGroupRef.current = null
      setSelectedCircleState(null)
      setSelectedTextShapeState(null)
      if (manuallySelectedTextShapeRef.current) { manuallySelectedTextShapeRef.current.lockMovementX = true; manuallySelectedTextShapeRef.current.lockMovementY = true; manuallySelectedTextShapeRef.current = null }
    }
    const cursorMap: Record<Tool, string> = {
      select: 'default', arrow: 'crosshair',
      circle: 'crosshair', polygon: 'crosshair', text: 'text', ping: 'crosshair', marker: 'default',
      ruler: 'crosshair', hotzone: 'crosshair',
    }
    fc.defaultCursor = cursorMap[tool]
    fc.hoverCursor   = 'pointer'
    toolCursorRef.current = cursorMap[tool]
    fc.forEachObject(obj => {
      if ((obj as any).__isArrow || (obj as any).__isPoly || (obj as any).__isCircleShape) {
        obj.selectable = false
        obj.evented = true
        return
      }
      obj.selectable = tool === 'select'
      obj.evented    = tool === 'select'
    })
    fc.renderAll()


  }, [tool, color])

  // ── Finalize / cancel polygon helpers ─────────────────────────────────────
  const finalizePolygon = useCallback(() => {
    const fc = fabricRef.current
    if (!fc || polygonPointsRef.current.length < 3) return
    polygonTempObjectsRef.current.forEach(obj => fc.remove(obj))
    polygonTempObjectsRef.current = []
    const pts = [...polygonPointsRef.current]
    const poly = buildPolygonShape(pts, color, strokeWidthRef.current, fillOpacityRef.current, lineStyleRef.current)
    fc.add(poly)
    polygonsToBack(fc)
    fc.renderAll()
    // Set polygon handle state
    selectedPolygonGroupRef.current = poly
    setSelectedPolygonState({
      pts,
      color,
      sw: strokeWidthRef.current,
      fillOpacity: fillOpacityRef.current,
      ls: lineStyleRef.current,
    })
    polygonPointsRef.current = []
    setTool('select')
    pushHistory(); broadcastCanvas()
  }, [color])

  const cancelPolygon = useCallback(() => {
    const fc = fabricRef.current
    if (!fc) return
    polygonTempObjectsRef.current.forEach(obj => fc.remove(obj))
    polygonTempObjectsRef.current = []
    polygonPointsRef.current = []
    selectedPolygonGroupRef.current = null
    setSelectedPolygonState(null)
    fc.renderAll()
  }, [])

  /**
   * Son köşeyi geri al. Sağ tık eskiden bütün çokgeni siliyordu: on köşelik
   * bir alanda tek bir yanlış tıklama hepsini baştan çizdiriyordu. Geçici
   * çizim (noktalar + kenarlar) kalan köşelerden yeniden kurulur; imleç
   * önizlemesi bir sonraki harekette kendini çizer.
   */
  const undoPolygonPoint = useCallback(() => {
    const fc = fabricRef.current
    if (!fc) return
    const pts = polygonPointsRef.current
    if (pts.length <= 1) { cancelPolygon(); return }
    pts.pop()
    polygonTempObjectsRef.current.forEach(obj => fc.remove(obj))
    const temp: any[] = []
    pts.forEach((p, i) => {
      const dot = new FabricCircle({
        left: p.x, top: p.y, radius: 4 * DRAW_SCALE,
        originX: 'center', originY: 'center',
        fill: color, stroke: '#fff', strokeWidth: DRAW_SCALE,
        selectable: false, evented: false,
      })
      fc.add(dot); temp.push(dot)
      if (i > 0) {
        const prev = pts[i - 1]
        const edge = new Line([prev.x, prev.y, p.x, p.y], {
          stroke: color, strokeWidth: strokeWidthRef.current * DRAW_SCALE, selectable: false, evented: false,
        })
        fc.add(edge); temp.push(edge)
      }
    })
    polygonTempObjectsRef.current = temp
    fc.renderAll()
  }, [color, cancelPolygon])

  // ── Polygon handle drag (destroy-and-recreate, like arrow) ──────────────
  const handlePolygonHandleDown = useCallback((
    e: React.PointerEvent,
    index: number,
    handleType: 'point' | 'ghost',
  ) => {
    e.preventDefault()
    e.stopPropagation()
    const fc = fabricRef.current
    if (!fc) return

    polygonDraggingRef.current = true
    let poly = selectedPolygonGroupRef.current
    if (!poly) return

    let pts = [...poly.__pts]
    const polyColor = poly.__clr
    const sw = poly.__sw
    const fillOpacity = poly.__fo
    const ls = poly.__ls

    // Ghost midpoint: insert a new intermediate point before dragging
    if (handleType === 'ghost') {
      const nextI = (index + 1) % pts.length
      const newPt = {
        x: (pts[index].x + pts[nextI].x) / 2,
        y: (pts[index].y + pts[nextI].y) / 2,
      }
      pts.splice(index + 1, 0, newPt)
      index = index + 1
    }

    const contRect = containerRef.current!.getBoundingClientRect()

    const onMove = (me: PointerEvent) => {
      const vpt = fc.viewportTransform!
      pts[index] = {
        x: (me.clientX - contRect.left - vpt[4]) / vpt[0],
        y: (me.clientY - contRect.top  - vpt[5]) / vpt[3],
      }
      // Destroy-and-recreate: remove old polygon, build fresh one with new pts
      isRemoteUpdateRef.current = true
      const fresh = buildPolygonShape(pts, polyColor, sw, fillOpacity, ls)
      replacePolygon(fc, poly, fresh)
      poly = fresh
      selectedPolygonGroupRef.current = fresh
      isRemoteUpdateRef.current = false
      setSelectedPolygonState({ pts: [...pts], color: polyColor, sw, fillOpacity, ls })
      fc.renderAll()
    }

    const onUp = () => {
      polygonDraggingRef.current = false
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      pushHistory()
      broadcastCanvas()
    }

    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
  }, [pushHistory, broadcastCanvas])

  // ── Circle handle drag (destroy-and-recreate, like arrow) ────────────────
  const handleCircleHandleDown = useCallback((
    e: React.PointerEvent,
    handleType: 'center' | 'edge-n' | 'edge-s' | 'edge-e' | 'edge-w',
  ) => {
    e.preventDefault()
    e.stopPropagation()
    const fc = fabricRef.current
    if (!fc) return

    circleDraggingRef.current = true
    let circle = selectedCircleGroupRef.current
    if (!circle) return

    let cx = circle.__cx
    let cy = circle.__cy
    let radius = circle.__r
    const clr = circle.__clr
    const sw = circle.__sw
    const fo = circle.__fo
    const ls = circle.__ls
    const hz = circle.__hz
    // Hotzone'un çapı sabit: kenar tutamaçları basılmaz, gelse de yok sayılır.
    if (hz && handleType !== 'center') { circleDraggingRef.current = false; return }

    const contRect = containerRef.current!.getBoundingClientRect()

    const onMove = (me: PointerEvent) => {
      const vpt = fc.viewportTransform!
      const worldX = (me.clientX - contRect.left - vpt[4]) / vpt[0]
      const worldY = (me.clientY - contRect.top  - vpt[5]) / vpt[3]

      if (handleType === 'center') {
        cx = worldX
        cy = worldY
      } else {
        // Edge handles — distance from center becomes the new radius
        radius = Math.max(5, Math.sqrt((worldX - cx) ** 2 + (worldY - cy) ** 2))
      }

      // Destroy-and-recreate
      isRemoteUpdateRef.current = true
      if (circle) fc.remove(circle)
      const fresh = buildCircleShape(cx, cy, radius, clr, sw, fo, ls, hz)
      fc.add(fresh)
      circle = fresh
      selectedCircleGroupRef.current = fresh
      isRemoteUpdateRef.current = false
      setSelectedCircleState({ cx, cy, radius, color: clr, sw, fillOpacity: fo, ls })
      fc.renderAll()
    }

    const onUp = () => {
      circleDraggingRef.current = false
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      pushHistory()
      broadcastCanvas()
    }

    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
  }, [pushHistory, broadcastCanvas])

  // ── TextShape handle drag (move via center handle) ──────────────────────────
  useEffect(() => {
    const fc = fabricRef.current
    if (!fc) return

    const handleMouseDown = (opt: any) => {
      if (tool === 'text') {
        const pointer = fc.getScenePoint(opt.e)
        const textShape = buildTextShape('Text', pointer.x, pointer.y, color, fontSizeRef.current)
        isRemoteUpdateRef.current = true
        fc.add(textShape)
        fc.setActiveObject(textShape)
        isRemoteUpdateRef.current = false
        manuallySelectedTextShapeRef.current = textShape
        textShape.lockMovementX = false
        textShape.lockMovementY = false
        textShape.enterEditing(); setTool('select')
        pushHistory(); broadcastCanvas()
      } else if (tool === 'ping') {
        const pointer = fc.getScenePoint(opt.e)
        const ws = wsRef.current
        if (ws && ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: 'ping', data: { x: pointer.x, y: pointer.y } }))
        }
        const pingId = `local-${Date.now()}`
        const myColor = connectedUsersRef.current.find(u => u.user_id === user?.id)?.color ?? '#f59e0b'
        setPings(prev => [...prev, { id: pingId, x: pointer.x, y: pointer.y, user_name: user?.name ?? 'Me', color: myColor }])
        setTimeout(() => setPings(prev => prev.filter(p => p.id !== pingId)), 3000)
      } else if (tool === 'marker' && selectedMarker) {
        const pointer = fc.getScenePoint(opt.e)
        const newMarker: PlacedMarker = {
          id: `local-${Date.now()}`,
          src: selectedMarker.src,
          worldX: pointer.x,
          worldY: pointer.y,
          // FOB'un üçgeni tarafın rengiyle gelir; alanın kenarıyla aynı renk.
          color: fobOf(selectedMarker.src)?.color ?? '#ffffff', title: '', text: '',
          squads: [], members: [],
        }
        updateCurrentMarkers(prev => [...prev, newMarker])
        setActiveMarkerId(newMarker.id)
        setSelectedMarker(null)
        scheduleSave()
        broadcastMarkerState()
      }
    }

    // ── Straight arrow (with custom handle system) ────────────────────────────
    let arrowStart: { x: number; y: number } | null = null
    let tempLine: Line | null = null
    const handleArrowDown = (opt: any) => {
      if (tool !== 'arrow') return
      const pointer = fc.getScenePoint(opt.e)
      arrowStart = { x: pointer.x, y: pointer.y }
      isRemoteUpdateRef.current = true
      tempLine = new Line([pointer.x, pointer.y, pointer.x, pointer.y], { stroke: color, strokeWidth: strokeWidthRef.current * DRAW_SCALE, selectable: false, evented: false })
      fc.add(tempLine)
      isRemoteUpdateRef.current = false
    }
    const handleArrowMove = (opt: any) => {
      if (tool !== 'arrow' || !arrowStart || !tempLine) return
      tempLine.set({ x2: fc.getScenePoint(opt.e).x, y2: fc.getScenePoint(opt.e).y }); fc.renderAll()
    }
    const handleArrowUp = () => {
      if (tool !== 'arrow' || !arrowStart || !tempLine) return
      const x1 = tempLine.x1!, y1 = tempLine.y1!, x2 = tempLine.x2!, y2 = tempLine.y2!
      isRemoteUpdateRef.current = true; fc.remove(tempLine); isRemoteUpdateRef.current = false
      // Eşikler ekran pikselinde: dünya biriminde Wardogs'ta onlarca metre ediyordu.
      if (Math.hypot(x2 - x1, y2 - y1) * fc.getZoom() < 5) { arrowStart = null; tempLine = null; return }
      // Build arrow group with custom handle system
      const pts = [{x:x1,y:y1},{x:x2,y:y2}]
      const sw = strokeWidthRef.current
      const meta: ArrowMeta = {
        ...emptyArrowMeta(),
        ...arrowMetaDefaultsRef.current,
        squads: arrowMetaDefaultsRef.current.squads.map(s => ({ ...s })),
        members: arrowMetaDefaultsRef.current.members.map(m => ({ ...m })),
        vehicle: arrowMetaDefaultsRef.current.vehicle ? { ...arrowMetaDefaultsRef.current.vehicle } : null,
      }
      const arrowGroup = buildArrowGroup(pts, color, sw, lineStyleRef.current, arrowHeadRef.current, meta)
      fc.add(arrowGroup)
      // Manually select — arrow is selectable:false so we bypass Fabric selection
      selectedArrowGroupRef.current = arrowGroup
      setSelectedArrowState({
        pts: [...pts], color, sw, ls: lineStyleRef.current, ah: arrowHeadRef.current,
        ...meta,
      })
      fc.renderAll(); arrowStart = null; tempLine = null
      setTool('select')
    }

    // ── Cetvel (sürükle → mesafe + açı) ──────────────────────────────────────
    // Tuvale yazmaz: ölçüm RulerLayer'ın state'inde, bırakınca ekranda kalır,
    // bir sonraki sürükleme onu değiştirir.
    let rulerStart: { x: number; y: number } | null = null
    const handleRulerDown = (opt: any) => {
      if (tool !== 'ruler') return
      const p = fc.getScenePoint(opt.e)
      rulerStart = { x: p.x, y: p.y }
      rulerRef.current?.set({ a: rulerStart, b: rulerStart })
    }
    const handleRulerMove = (opt: any) => {
      if (tool !== 'ruler' || !rulerStart) return
      const p = fc.getScenePoint(opt.e)
      rulerRef.current?.set({ a: rulerStart, b: { x: p.x, y: p.y } })
    }
    const handleRulerUp = (opt: any) => {
      if (tool !== 'ruler' || !rulerStart) return
      const p = fc.getScenePoint(opt.e)
      // Sürüklemeden bırakılan tıklama ölçümü temizler.
      if (Math.hypot(p.x - rulerStart.x, p.y - rulerStart.y) * fc.getZoom() < 3) rulerRef.current?.set(null)
      rulerStart = null
    }

    // ── Hotzone (tıkla → 170 m çaplı sabit çember) ───────────────────────────
    const handleHotzoneDown = (opt: any) => {
      if (tool !== 'hotzone') return
      const p = fc.getScenePoint(opt.e)
      const radius = (HOTZONE_DIAMETER_M / 2) / metersPerWorld(CANVAS_SIZE)
      isRemoteUpdateRef.current = true
      const zone = buildCircleShape(p.x, p.y, radius, color, 0, 0, 'solid', true)
      fc.add(zone)
      isRemoteUpdateRef.current = false
      selectedCircleGroupRef.current = zone
      setSelectedCircleState({ cx: p.x, cy: p.y, radius, color: zone.__clr, sw: zone.__sw, fillOpacity: zone.__fo, ls: zone.__ls })
      fc.renderAll()
      pushHistory(); broadcastCanvas()
      setTool('select')
    }

    // ── Circle tool (drag from center to edge) ───────────────────────────────
    let circleStart: { x: number; y: number } | null = null
    let tempCircle: FabricCircle | null = null
    const handleCircleDown = (opt: any) => {
      if (tool !== 'circle') return
      const pointer = fc.getScenePoint(opt.e)
      circleStart = { x: pointer.x, y: pointer.y }
      const fillHex = Math.round(fillOpacityRef.current * 255).toString(16).padStart(2, '0')
      tempCircle = new FabricCircle({
        left: pointer.x, top: pointer.y, radius: 0,
        originX: 'center', originY: 'center',
        stroke: color, strokeWidth: strokeWidthRef.current * DRAW_SCALE, fill: color + fillHex,
        selectable: false, evented: false,
      })
      fc.add(tempCircle)
    }
    const handleCircleMove = (opt: any) => {
      if (tool !== 'circle' || !circleStart || !tempCircle) return
      const pointer = fc.getScenePoint(opt.e)
      const radius = Math.sqrt((pointer.x - circleStart.x) ** 2 + (pointer.y - circleStart.y) ** 2)
      tempCircle.set({ radius })
      fc.renderAll()
    }
    const handleCircleUp = () => {
      if (tool !== 'circle' || !circleStart || !tempCircle) return
      if (tempCircle.radius! * fc.getZoom() < 5) { fc.remove(tempCircle); circleStart = null; tempCircle = null; return }
      // Replace temp circle with a proper CircleShape (custom handle system)
      const cx = circleStart.x
      const cy = circleStart.y
      const radius = tempCircle.radius!
      isRemoteUpdateRef.current = true
      fc.remove(tempCircle)
      const circleShape = buildCircleShape(cx, cy, radius, color, strokeWidthRef.current, fillOpacityRef.current, lineStyleRef.current)
      fc.add(circleShape)
      isRemoteUpdateRef.current = false
      // Manually select — circle is selectable:false so we bypass Fabric selection
      selectedCircleGroupRef.current = circleShape
      setSelectedCircleState({ cx, cy, radius, color, sw: strokeWidthRef.current, fillOpacity: fillOpacityRef.current, ls: lineStyleRef.current })
      fc.renderAll()
      pushHistory(); broadcastCanvas()
      circleStart = null; tempCircle = null
      setTool('select')
    }

    // ── Polygon tool (click to add points, click first point to close, right-click to cancel) ──
    const handlePolygonDown = (opt: any) => {
      if (tool !== 'polygon') return
      const pointer = fc.getScenePoint(opt.e)
      const pts = polygonPointsRef.current

      // Check if clicking on the first point to close the polygon (minimum 3 points required)
      if (pts.length >= 3) {
        const firstPt = pts[0]
        const dist = Math.hypot(pointer.x - firstPt.x, pointer.y - firstPt.y)
        if (dist * fc.getZoom() < 12) { // Tıklama toleransı — ekran pikseli
          finalizePolygon()
          return
        }
      }

      // Add new vertex
      pts.push({ x: pointer.x, y: pointer.y })
      // Draw vertex dot
      const dot = new FabricCircle({
        left: pointer.x, top: pointer.y, radius: 4 * DRAW_SCALE,
        originX: 'center', originY: 'center',
        fill: color, stroke: '#fff', strokeWidth: DRAW_SCALE,
        selectable: false, evented: false,
      })
      fc.add(dot)
      polygonTempObjectsRef.current.push(dot)
      // Draw edge line from previous point
      if (pts.length >= 2) {
        const p1 = pts[pts.length - 2], p2 = pts[pts.length - 1]
        const edge = new Line([p1.x, p1.y, p2.x, p2.y], {
          stroke: color, strokeWidth: strokeWidthRef.current * DRAW_SCALE, selectable: false, evented: false,
        })
        fc.add(edge)
        polygonTempObjectsRef.current.push(edge)
      }
      fc.renderAll()
    }

    const handlePolygonMove = (opt: any) => {
      if (tool !== 'polygon' || polygonPointsRef.current.length === 0) return
      const pointer = fc.getScenePoint(opt.e)
      const pts = polygonPointsRef.current
      const lastPt = pts[pts.length - 1]
      // Remove previous preview elements (line + dot = 2 elements)
      const tempObjs = polygonTempObjectsRef.current
      while (tempObjs.length > 0 && (tempObjs[tempObjs.length - 1] as any)._isPreview) {
        fc.remove(tempObjs.pop()!)
      }
      // Draw preview line to cursor
      const preview = new Line([lastPt.x, lastPt.y, pointer.x, pointer.y], {
        stroke: color, strokeWidth: strokeWidthRef.current * DRAW_SCALE,
        strokeDashArray: dashArray('dashed', strokeWidthRef.current * DRAW_SCALE),
        selectable: false, evented: false,
      }) as any
      preview._isPreview = true
      fc.add(preview)
      tempObjs.push(preview)
      // Draw preview dot at cursor
      const previewDot = new FabricCircle({
        left: pointer.x, top: pointer.y, radius: 4 * DRAW_SCALE,
        originX: 'center', originY: 'center',
        fill: color + '80', stroke: '#fff', strokeWidth: DRAW_SCALE,
        selectable: false, evented: false,
      }) as any
      previewDot._isPreview = true
      fc.add(previewDot)
      tempObjs.push(previewDot)
      fc.renderAll()
    }

    // Sağ tık son köşeyi geri alır (tümünü iptal Esc'te)
    const handlePolygonContextMenu = (opt: any) => {
      if (tool !== 'polygon') return
      opt.e.preventDefault()
      undoPolygonPoint()
    }

    fc.on('contextmenu', handlePolygonContextMenu)

    fc.on('mouse:down', handleMouseDown)
    fc.on('mouse:down', handleArrowDown)
    fc.on('mouse:move', handleArrowMove)
    fc.on('mouse:up',   handleArrowUp)
    fc.on('mouse:down', handleCircleDown)
    fc.on('mouse:move', handleCircleMove)
    fc.on('mouse:up',   handleCircleUp)
    fc.on('mouse:down', handlePolygonDown)
    fc.on('mouse:move', handlePolygonMove)
    fc.on('mouse:down', handleRulerDown)
    fc.on('mouse:move', handleRulerMove)
    fc.on('mouse:up',   handleRulerUp)
    fc.on('mouse:down', handleHotzoneDown)
    return () => {
      fc.off('mouse:down', handleRulerDown)
      fc.off('mouse:move', handleRulerMove)
      fc.off('mouse:up',   handleRulerUp)
      fc.off('mouse:down', handleHotzoneDown)
      fc.off('mouse:down', handleMouseDown)
      fc.off('mouse:down', handleArrowDown)
      fc.off('mouse:move', handleArrowMove)
      fc.off('mouse:up',   handleArrowUp)
      fc.off('mouse:down', handleCircleDown)
      fc.off('mouse:move', handleCircleMove)
      fc.off('mouse:up',   handleCircleUp)
      fc.off('mouse:down', handlePolygonDown)
      fc.off('mouse:move', handlePolygonMove)
      fc.off('contextmenu', handlePolygonContextMenu)
      // Clean up polygon temp objects on tool switch
      if (polygonPointsRef.current.length > 0) {
        polygonTempObjectsRef.current.forEach(obj => fc.remove(obj))
        polygonTempObjectsRef.current = []
        polygonPointsRef.current = []
        setSelectedPolygonState(null)
        fc.renderAll()
      }
    }
  }, [tool, color, user?.name, selectedMarker, finalizePolygon, undoPolygonPoint])

  // ── Keyboard shortcuts ─────────────────────────────────────────────────────
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      const fc = fabricRef.current
      if (!fc) return
      const tag = (e.target as HTMLElement)?.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return
      const activeObj = fc.getActiveObject()
      if (activeObj && activeObj instanceof IText && activeObj.isEditing) return

      /**
       * Araç kısayolları: F1–F7, araç rayındaki sırayla. Araç çubuğundaki ipucu
       * metni hangi tuş olduğunu söyler — kısayol, varlığı yazılı olmadığında
       * yok sayılır.
       *
       * F tuşlarının hepsinin tarayıcıda bir işi var (F1 yardım, F3 bul, F5
       * yenile, F6 adres çubuğu, F7 imleçle gezinme), o yüzden **yalnızca
       * karşılığı olan tuşta** `preventDefault` çağrılır: çizemeyen biri F5'e
       * bastığında sayfa yine yenilenir.
       *
       * Seçme ve işaret **çizemeyende de** çalışır: ikisi de tuvale bir şey
       * yazmaz, salt okunur bakan da ping atar ve nesne seçer.
       */
      if (!e.ctrlKey && !e.metaKey && !e.altKey && !e.shiftKey) {
        // Cetvel ve hotzone sonradan geldi: alıştırılmış F1–F7 kaymasın diye
        // F8/F9 aldılar, rayda ise anlamlarının yanında dururlar.
        const openTools: Record<string, Tool> = { F1: 'select', F2: 'ping', F8: 'ruler' }
        const drawTools: Record<string, Tool> = {
          F3: 'marker', F4: 'arrow', F5: 'polygon', F6: 'circle', F7: 'text', F9: 'hotzone',
        }
        if (openTools[e.key]) { e.preventDefault(); setTool(openTools[e.key]); return }
        if (e.key === 'Escape' && tool === 'ruler') { rulerRef.current?.set(null); return }
        if (canDraw && drawTools[e.key]) {
          e.preventDefault()
          const next = drawTools[e.key]
          setTool(next)
          if (next === 'marker') setRightPanelOpen(true)
          return
        }
      }

      if (!canDraw) return
      if (e.key === 'Backspace' && tool === 'polygon' && polygonPointsRef.current.length > 0) {
        e.preventDefault(); undoPolygonPoint()
      } else if (e.key === 'Delete' || e.key === 'Backspace') {
        // Delete active DOM marker first
        if (activeMarkerId) {
          updateCurrentMarkers(prev => prev.filter(m => m.id !== activeMarkerId))
          setActiveMarkerId(null)
          scheduleSave()
          broadcastMarkerState()
          return
        }
        // Delete selected arrow (tracked via ref, not Fabric active object)
        const selArrow = selectedArrowGroupRef.current
        if (selArrow) {
          fc.remove(selArrow)
          selectedArrowGroupRef.current = null
          setSelectedArrowState(null)
          fc.renderAll()
          return
        }
        // Delete selected polygon (tracked via ref, not Fabric active object)
        const selPoly = selectedPolygonGroupRef.current
        if (selPoly) {
          fc.remove(selPoly)
          selectedPolygonGroupRef.current = null
          setSelectedPolygonState(null)
          fc.renderAll()
          return
        }
        // Delete selected circle (tracked via ref, not Fabric active object)
        const selCircle = selectedCircleGroupRef.current
        if (selCircle) {
          fc.remove(selCircle)
          selectedCircleGroupRef.current = null
          setSelectedCircleState(null)
          fc.renderAll()
          return
        }
        // Delete any active Fabric objects (including TextShape)
        const active = fc.getActiveObjects()
        if (active.length) { active.forEach(o => fc.remove(o)); fc.discardActiveObject(); fc.renderAll() }
        // Clear TextShape manual selection if the deleted object was it
        if (manuallySelectedTextShapeRef.current && !fc.getObjects().includes(manuallySelectedTextShapeRef.current)) {
          manuallySelectedTextShapeRef.current = null
        }
      } else if (e.ctrlKey && e.key === 'z') { e.preventDefault(); undo() }
        else if (e.ctrlKey && e.key === 'y') { e.preventDefault(); redo() }
        else if (e.key === 'Enter' && tool === 'polygon') { e.preventDefault(); finalizePolygon() }
        else if (e.key === 'Escape' && tool === 'polygon') { e.preventDefault(); cancelPolygon() }
    }
    window.addEventListener('keydown', handleKey)
    return () => window.removeEventListener('keydown', handleKey)
  }, [undo, redo, tool, finalizePolygon, cancelPolygon, undoPolygonPoint, activeMarkerId, scheduleSave, canDraw])

  // ── Close marker search dropdown on outside click ──────────────────────────
  useEffect(() => {
    if (!showMarkerSearch) return
    const handler = (e: MouseEvent) => {
      const panel = document.getElementById('marker-search-panel')
      const btn   = document.getElementById('marker-search-btn')
      if (panel && !panel.contains(e.target as Node) && btn && !btn.contains(e.target as Node)) {
        setShowMarkerSearch(false)
        setMarkerSearchQuery('')
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [showMarkerSearch])



  // ── Arrow handle drag ─────────────────────────────────────────────────────
  const handleArrowHandleDown = useCallback((
    e: React.PointerEvent,
    index: number,
    handleType: 'point' | 'ghost',
  ) => {
    e.preventDefault()
    e.stopPropagation()
    const fc = fabricRef.current
    if (!fc) return

    arrowDraggingRef.current = true
    let group = selectedArrowGroupRef.current
    if (!group) return

    let pts = [...((group as any).__pts as {x:number;y:number}[])]
    const color = (group as any).__clr as string
    const sw    = (group as any).__sw  as number
    const ls    = (group as any).__ls  as LineStyle
    const ah    = (group as any).__ah  as ArrowHead
    const meta: ArrowMeta = {
      title: (group as any).__title ?? '',
      text: (group as any).__text ?? '',
      squads: (group as any).__squads ?? [],
      members: (group as any).__members ?? [],
      startMarkerSrc: (group as any).__startMarkerSrc ?? null,
      vehicle: (group as any).__vehicle ?? null,
    }

    // Ghost midpoint: insert a new intermediate point before dragging
    if (handleType === 'ghost') {
      const newPt = {
        x: (pts[index].x + pts[index + 1].x) / 2,
        y: (pts[index].y + pts[index + 1].y) / 2,
      }
      pts.splice(index + 1, 0, newPt)
      index = index + 1
    }

    const contRect = containerRef.current!.getBoundingClientRect()

    const onMove = (me: PointerEvent) => {
      const vpt = fc.viewportTransform!
      pts[index] = {
        x: (me.clientX - contRect.left - vpt[4]) / vpt[0],
        y: (me.clientY - contRect.top  - vpt[5]) / vpt[3],
      }
      // Destroy-and-recreate: remove old group, build fresh one with new pts
      isRemoteUpdateRef.current = true
      if (group) fc.remove(group)
      const fresh = buildArrowGroup(pts, color, sw, ls, ah, meta)
      fc.add(fresh)
      group = fresh
      selectedArrowGroupRef.current = fresh
      isRemoteUpdateRef.current = false
      setSelectedArrowState({ pts: [...pts], color, sw, ls, ah, ...meta })
      fc.renderAll()
    }


    const onUp = () => {
      arrowDraggingRef.current = false
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      pushHistory()
      broadcastCanvas()
    }

    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
  }, [pushHistory, broadcastCanvas])

  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; type: 'fabric' | 'marker'; markerId?: string } | null>(null)

  const handleContextDelete = () => {
    if (!contextMenu) return
    if (contextMenu.type === 'marker' && contextMenu.markerId) {
      const id = contextMenu.markerId
      updateCurrentMarkers(prev => prev.filter(m => m.id !== id))
      setActiveMarkerId(null)
      scheduleSave()
      broadcastMarkerState()
    } else if (contextMenu.type === 'fabric') {
      const fc = fabricRef.current
      if (fc) {
        // Delete selected arrow (tracked via ref, not Fabric active object)
        const selArrow = selectedArrowGroupRef.current
        if (selArrow) {
          fc.remove(selArrow)
          selectedArrowGroupRef.current = null
          setSelectedArrowState(null)
          fc.renderAll()
        } else {
          // Delete selected polygon (tracked via ref)
          const selPoly = selectedPolygonGroupRef.current
          if (selPoly) {
            fc.remove(selPoly)
            selectedPolygonGroupRef.current = null
            setSelectedPolygonState(null)
            fc.renderAll()
          } else {
            // Delete selected circle (tracked via ref)
            const selCircle = selectedCircleGroupRef.current
            if (selCircle) {
              fc.remove(selCircle)
              selectedCircleGroupRef.current = null
              setSelectedCircleState(null)
              fc.renderAll()
            } else {
              // Delete any active Fabric objects (including TextShape)
              const active = fc.getActiveObjects()
              if (active.length) { active.forEach(o => fc.remove(o)); fc.discardActiveObject(); fc.renderAll() }
              // Clear TextShape manual selection if the deleted object was it
              if (manuallySelectedTextShapeRef.current && !fc.getObjects().includes(manuallySelectedTextShapeRef.current)) {
                manuallySelectedTextShapeRef.current = null
              }
            }
          }
        }
      }
    }
    setContextMenu(null)
  }

  // ── Selected shape property update handlers (destroy-and-recreate) ──────
  const updateArrowProperties = useCallback((patch: Partial<{
    color: string; sw: number; ls: LineStyle; ah: ArrowHead
    title: string; text: string
    squads: { name: string; color: string }[]
    members: { id: string; name: string; avatar_url: string }[]
    startMarkerSrc: string | null
    vehicle: { id: number; name: string; speed: number } | null
  }>) => {
    const fc = fabricRef.current
    const group = selectedArrowGroupRef.current
    if (!fc || !group) return
    const pts = [...(group as any).__pts as {x:number;y:number}[]]
    const color = patch.color ?? ((group as any).__clr as string)
    const sw    = patch.sw    ?? ((group as any).__sw  as number)
    const ls    = patch.ls    ?? ((group as any).__ls  as LineStyle)
    const ah    = patch.ah    ?? ((group as any).__ah  as ArrowHead)
    const meta: ArrowMeta = {
      title: patch.title ?? ((group as any).__title ?? ''),
      text: patch.text ?? ((group as any).__text ?? ''),
      squads: patch.squads ?? ((group as any).__squads ?? []),
      members: patch.members ?? ((group as any).__members ?? []),
      startMarkerSrc: patch.startMarkerSrc !== undefined ? patch.startMarkerSrc : ((group as any).__startMarkerSrc ?? null),
      vehicle: patch.vehicle !== undefined ? patch.vehicle : ((group as any).__vehicle ?? null),
    }
    isRemoteUpdateRef.current = true
    fc.remove(group)
    const fresh = buildArrowGroup(pts, color, sw, ls, ah, meta)
    fc.add(fresh)
    isRemoteUpdateRef.current = false
    selectedArrowGroupRef.current = fresh
    setSelectedArrowState({ pts: [...pts], color, sw, ls, ah, ...meta })
    fc.renderAll()
    pushHistory()
    broadcastCanvas()
  }, [pushHistory, broadcastCanvas])

  const updatePolygonProperty = useCallback((prop: 'color' | 'sw' | 'fillOpacity' | 'ls', value: any) => {
    const fc = fabricRef.current
    const poly = selectedPolygonGroupRef.current
    if (!fc || !poly) return
    const pts = [...(poly as any).__pts as {x:number;y:number}[]]
    const color = prop === 'color' ? value : (poly as any).__clr as string
    const sw    = prop === 'sw'    ? value : (poly as any).__sw  as number
    const fo    = prop === 'fillOpacity' ? value : (poly as any).__fo  as number
    const ls    = prop === 'ls'    ? value : (poly as any).__ls  as LineStyle
    isRemoteUpdateRef.current = true
    const fresh = buildPolygonShape(pts, color, sw, fo, ls)
    replacePolygon(fc, poly, fresh)
    isRemoteUpdateRef.current = false
    selectedPolygonGroupRef.current = fresh
    setSelectedPolygonState({ pts: [...pts], color, sw, fillOpacity: fo, ls })
    fc.renderAll()
    pushHistory()
    broadcastCanvas()
  }, [pushHistory, broadcastCanvas])

  const updateCircleProperty = useCallback((prop: 'color' | 'sw' | 'fillOpacity' | 'ls', value: any) => {
    const fc = fabricRef.current
    const circle = selectedCircleGroupRef.current
    if (!fc || !circle) return
    if (circle.__hz) return   // görünüşü sabit
    const cx = (circle as any).__cx as number
    const cy = (circle as any).__cy as number
    const radius = (circle as any).__r as number
    const color = prop === 'color' ? value : (circle as any).__clr as string
    const sw    = prop === 'sw'    ? value : (circle as any).__sw  as number
    const fo    = prop === 'fillOpacity' ? value : (circle as any).__fo  as number
    const ls    = prop === 'ls'    ? value : (circle as any).__ls  as LineStyle
    isRemoteUpdateRef.current = true
    fc.remove(circle)
    const fresh = buildCircleShape(cx, cy, radius, color, sw, fo, ls, !!(circle as any).__hz)
    fc.add(fresh)
    isRemoteUpdateRef.current = false
    selectedCircleGroupRef.current = fresh
    setSelectedCircleState({ cx, cy, radius, color, sw, fillOpacity: fo, ls })
    fc.renderAll()
    pushHistory()
    broadcastCanvas()
  }, [pushHistory, broadcastCanvas])

  // ── TextShape property update (mutate-in-place, not destroy-and-recreate) ─
  const updateTextShapeProperty = useCallback((prop: 'color' | 'fontSize' | 'bg', value: any) => {
    const fc = fabricRef.current
    const textShape = fc?.getActiveObject()
    if (!fc || !textShape || !(textShape instanceof TextShape)) return
    isRemoteUpdateRef.current = true
    switch (prop) {
      case 'color':
        textShape.set('fill', value); textShape.__clr = value
        // Update shadow: if bg is none, keep shadow; otherwise no shadow
        if (textShape.__bg === 'none') {
          textShape.set('shadow', textShadow())
        }
        break
      case 'fontSize':
        textShape.set('fontSize', value * DRAW_SCALE); textShape.__fs = value; break
      case 'bg':
        textShape.__bg = value
        if (value === 'none') {
          textShape.set('shadow', textShadow())
        } else {
          textShape.set('shadow', undefined)
        }
        break
    }
    textShape.setCoords()
    textShape.dirty = true
    fc.renderAll()
    isRemoteUpdateRef.current = false
    setSelectedTextShapeState({
      color: textShape.__clr, fontSize: textShape.__fs, bg: textShape.__bg,
    })
    pushHistory()
    broadcastCanvas()
  }, [pushHistory, broadcastCanvas])

  /**
   * Araç düğmesi. İpucu metni kısayolu da söyler — yazılı olmayan bir kısayol
   * yok sayılır (bkz. klavye kısayolları).
   */
  const toolBtn = (t: Tool, icon: React.ReactNode, label: string, key: string) => (
    <button
      onClick={() => setTool(t)}
      title={`${label} · ${key}`}
      aria-label={label}
      className={editorIconButton(tool === t)}
    >
      {icon}
    </button>
  )

  // ── Topbar ─────────────────────────────────────────────────────────────────
  /**
   * Editörün üst çubuğu panelin `Header`'ıyla aynı hattır: **68px**, aynı cam
   * (`bg-black/25 backdrop-blur-sm`), aynı saç teli. Editöre girerken ana
   * gezinme çubuğu düştüğü için sayfanın üst satırı bu çubuktur; başka bir
   * yükseklik, panelden editöre geçişte hattın kaydığını gösterirdi.
   *
   * İki satır: üstte **nerede olduğun** (harita · kök strateji), altta
   * **neye baktığın** (slayt adı, tıklayınca düzenlenir). Harita adı bir
   * zamanlar editörde hiç yazmıyordu — hangi haritada olduğunu tuvale bakıp
   * tanımak gerekiyordu.
   */
  /** Bu haritanın bölgeleri ve seçili olan. Harita başına sabit veri. */
  const mapZones = zonesOf(mapName)
  const activeZone = findZone(mapName, controlZoneId)
  const mapBases = basesOf(mapName, activeZone)

  const savingLabel = saveState === 'saving' || saveState === 'pending'
    ? T.strategy.saving
    : saveState === 'saved' ? T.strategy.saved : null

  const topbar = (
    // Üç sütun: sol ve sağ eşit pay alır, böylece ortadaki görünüm grubu
    // başlığın uzunluğundan bağımsız olarak çubuğun tam ortasında durur.
    // Mutlak konumlu bir orta grup uzun bir başlığın üstüne binerdi.
    // Dar ekranda ızgara düz bir flex satırına döner (orta grup tam ortada
    // durması gerekmez, yer yoktur); kontrol bölgesi ve simge süzgeci gibi
    // düzenleme odaklı kontroller md altında düşer.
    <div className="relative z-50 flex h-[68px] shrink-0 items-center gap-3 border-b border-white/10 bg-black/25 px-4 backdrop-blur-sm md:grid md:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)]">

      {/* Sol: geri + kırıntı + başlık */}
      <div className="flex min-w-0 flex-1 items-center gap-3">
        {onNavigateHome && (
          <button
            onClick={onNavigateHome}
            className={editorIconButton()}
            title={T.strategy.home}
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
        )}
        <div className="min-w-0 flex-1">
          {/* Kırıntı — harita adı ve kök strateji **veridir**, büyütülmez. */}
          <div className="flex min-w-0 items-center gap-2 text-[11px] text-muted-foreground">
            <span aria-hidden className="h-px w-4 shrink-0 bg-primary" />
            <span className="shrink-0 font-display tracking-[0.06em]">{mapLabel}</span>
            {parentStrategyTitle && (
              <>
                <span className="shrink-0 text-muted-foreground/30">/</span>
                <span className="truncate" title={parentStrategyTitle}>{parentStrategyTitle}</span>
              </>
            )}
          </div>
          <div className="flex min-w-0 items-center gap-2">
            {editingTitle ? (
              <input
                ref={titleInputRef}
                autoFocus
                value={title}
                onChange={e => { setTitle(e.target.value); onTitleChange(e.target.value); scheduleSave() }}
                onBlur={() => setEditingTitle(false)}
                onKeyDown={e => { if (e.key === 'Enter' || e.key === 'Escape') setEditingTitle(false) }}
                className="min-w-0 flex-1 rounded-sm border border-white/20 bg-black/50 px-1.5 py-0.5 font-display text-sm font-bold focus:border-primary focus:outline-none"
              />
            ) : (
              <button
                onClick={() => canDraw && setEditingTitle(true)}
                className={`min-w-0 max-w-full truncate text-left font-display text-sm font-bold tracking-[0.04em] transition-colors ${canDraw ? 'cursor-text hover:text-white' : 'cursor-default'}`}
                title={title || T.strategy.untitled}
              >
                {title || T.strategy.untitled}
              </button>
            )}
            {/* Salt okunur rozeti: araç çubuğunun neden boş olduğunu söyleyen tek
                işaret. Yoksa çizemeyen kişi editörü bozuk sanıyor. */}
            {!canDraw && (
              <span className="shrink-0 rounded-sm border border-white/10 px-1.5 py-0.5 font-display text-[9px] font-bold uppercase tracking-[0.14em] text-muted-foreground/60">
                {T.strategy.readOnly}
              </span>
            )}
            {/* Kaydetme satırı — yalnızca çizebilende anlamlı. */}
            {canDraw && savingLabel && (
              <span className="hidden shrink-0 items-center gap-1 text-[10px] text-muted-foreground/60 sm:flex">
                {saveState === 'saved'
                  ? <Check className="h-3 w-3 text-emerald-400/70" />
                  : <Loader2 className="h-3 w-3 animate-spin" />}
                {savingLabel}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Orta: katmanlar ve zemin. Açıp kapatılan görünümler tek yerde. */}
      <div className="flex shrink-0 items-center gap-1.5">
        {mapBases.length > 0 && (
          <button
            title={T.strategy.basesToggle}
            aria-label={T.strategy.basesToggle}
            aria-pressed={showBases}
            className={editorIconButton(showBases)}
            onClick={toggleBases}
          >
            <Castle className="h-4 w-4" />
          </button>
        )}

        <button
          title={T.strategy.strategicPointsToggle}
          className={editorIconButton(showStrategicPoints)}
          onClick={() => setShowStrategicPoints(v => !v)}
        >
          <MapPin className="h-4 w-4" />
        </button>

        <button
          title={T.strategy.calloutsToggle}
          aria-label={T.strategy.calloutsToggle}
          aria-pressed={showCallouts}
          className={editorIconButton(showCallouts)}
          onClick={() => setShowCallouts(v => !v)}
        >
          <MapPinned className="h-4 w-4" />
        </button>

        <button
          title={T.strategy.gridToggle}
          aria-label={T.strategy.gridToggle}
          aria-pressed={showGrid}
          className={editorIconButton(showGrid)}
          onClick={toggleGrid}
        >
          <Grid3x3 className="h-4 w-4" />
        </button>

        {/* Harita zemini: taktiksel (gri, normalize) ↔ uydu (ham renk) */}
        <button
          title={mapView === 'tactical' ? T.strategy.mapView.tacticalHint : T.strategy.mapView.satelliteHint}
          aria-label={T.strategy.mapView.label}
          className={editorIconButton(mapView === 'tactical')}
          onClick={toggleMapView}
        >
          {mapView === 'tactical' ? <Crosshair className="h-4 w-4" /> : <Satellite className="h-4 w-4" />}
        </button>
      </div>

      {/* Sağ: kontrol bölgesi + simge süzgeci + bakanlar */}
      <div className="flex min-w-0 items-center justify-end gap-1.5">
        {/* Kontrol bölgesi — haritanın o rotasyonda oynanan çemberi.
            Seçim **slaytta** durur, bakan kişide değil (bkz. controlZoneId),
            o yüzden çizemeyen kişi seçiliyi görür ama değiştiremez. */}
        {mapZones.length > 0 && (
          <div className="relative hidden md:block">
            <select
              value={controlZoneId ?? ''}
              onChange={e => applyControlZone(e.target.value || null)}
              disabled={!canDraw}
              aria-label={T.strategy.controlZone}
              title={T.strategy.controlZone}
              className="h-8 cursor-pointer appearance-none rounded-sm border border-white/10 bg-white/5 pl-2.5 pr-7 text-xs text-muted-foreground transition-colors hover:bg-white/10 hover:text-foreground focus:border-primary focus:outline-none disabled:cursor-default disabled:opacity-50"
            >
              <option value="">{T.strategy.controlZoneOff}</option>
              {/* Bölge adları oyunun verisi — çevrilmez. */}
              {mapZones.map(z => (
                <option key={z.id} value={z.id}>{z.name}</option>
              ))}
            </select>
            <ChevronDown className="pointer-events-none absolute right-2 top-1/2 h-3 w-3 -translate-y-1/2 text-muted-foreground/60" />
          </div>
        )}


        {/* Simgeleri oyuncuya / takıma göre süz — dar ekranda düşer */}
        <div className="relative hidden md:block">
          {markerFilter ? (
            <div className="flex h-8 max-w-[160px] items-center gap-1.5 rounded-sm border border-primary/60 bg-primary/15 px-2.5 text-xs">
              {markerFilter.type === 'squad' ? (
                <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: markerFilter.color }} />
              ) : markerFilter.avatar_url ? (
                <img src={markerFilter.avatar_url} alt="" className="h-4 w-4 shrink-0 rounded-full object-cover" />
              ) : (
                <div className="h-4 w-4 shrink-0 rounded-full bg-white/20" />
              )}
              <span className="flex-1 truncate">{markerFilter.name}</span>
              <button
                onClick={() => setMarkerFilter(null)}
                className="shrink-0 cursor-pointer text-muted-foreground transition-colors hover:text-foreground"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ) : (
            <button
              id="marker-search-btn"
              onClick={() => { setShowMarkerSearch(v => !v); setMarkerSearchQuery('') }}
              title={T.strategy.filterMarkers}
              className={editorIconButton(showMarkerSearch)}
            >
              <Search className="h-4 w-4" />
            </button>
          )}

          {showMarkerSearch && !markerFilter && (
            <div
              id="marker-search-panel"
              className={`absolute right-0 top-full z-50 mt-1.5 w-60 p-2 ${editorMenu}`}
            >
              <input
                ref={markerSearchRef}
                autoFocus
                value={markerSearchQuery}
                onChange={e => setMarkerSearchQuery(e.target.value)}
                onKeyDown={e => e.key === 'Escape' && (setShowMarkerSearch(false), setMarkerSearchQuery(''))}
                placeholder={T.strategyEditor.filterPlaceholder}
                className={editorInput}
              />
              {filteredSquads.length > 0 || filteredMembers.length > 0 ? (
                <div className="mt-2 max-h-56 space-y-0.5 overflow-y-auto">
                  {filteredSquads.length > 0 && (
                    <>
                      <div className={`px-1 py-1 ${editorLabel}`}>{T.strategyEditor.squadsSection}</div>
                      {filteredSquads.map(sq => (
                        <button
                          key={sq.name}
                          onClick={() => {
                            setMarkerFilter({ type: 'squad', name: sq.name, color: sq.color })
                            setShowMarkerSearch(false)
                            setMarkerSearchQuery('')
                          }}
                          className="flex w-full cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-left text-xs transition-colors hover:bg-white/10"
                        >
                          <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: sq.color }} />
                          <span className="truncate">{sq.name}</span>
                        </button>
                      ))}
                    </>
                  )}
                  {filteredMembers.length > 0 && (
                    <>
                      <div className={`mt-1 px-1 py-1 ${editorLabel}`}>{T.strategyEditor.playersSection}</div>
                      {filteredMembers.map(mem => (
                        <button
                          key={mem.id}
                          onClick={() => {
                            setMarkerFilter({ type: 'member', id: mem.id, name: mem.name, avatar_url: mem.avatar_url })
                            setShowMarkerSearch(false)
                            setMarkerSearchQuery('')
                          }}
                          className="flex w-full cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-left text-xs transition-colors hover:bg-white/10"
                        >
                          {mem.avatar_url ? (
                            <img src={mem.avatar_url} alt="" className="h-4 w-4 shrink-0 rounded-full object-cover" />
                          ) : (
                            <div className="h-4 w-4 shrink-0 rounded-full bg-white/20" />
                          )}
                          <span className="truncate">{mem.name}</span>
                        </button>
                      ))}
                    </>
                  )}
                </div>
              ) : (
                <p className="px-1 pb-1 pt-2 text-[11px] text-muted-foreground/60">
                  {markerSearchQuery ? T.strategy.noMatchesFound : T.strategy.noAssignments}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Bakanlar — üstüne gelince değil, tıklayınca açılır: dokunmatik
            ekranda hover diye bir şey yok, o yüzden liste hiç açılmıyordu. */}
        <div className="relative">
          <button
            onClick={() => setShowViewers(v => !v)}
            className={`${editorIconButton(showViewers, 'h-8 px-2.5')} gap-1.5`}
            title={T.strategy.whoIsViewing}
          >
            <Eye className="h-4 w-4" />
            <span className="text-xs tabular-nums">{connectedUsers.length}</span>
          </button>
          {showViewers && connectedUsers.length > 0 && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setShowViewers(false)} />
              <div className={`absolute right-0 top-full z-50 mt-1.5 min-w-[170px] p-1.5 ${editorMenu}`}>
                {connectedUsers.map(u => (
                  <div key={u.user_id} className="flex items-center gap-2 px-1 py-1">
                    {u.avatar ? (
                      <img src={u.avatar} alt="" className="h-5 w-5 shrink-0 rounded-full"
                        style={{ boxShadow: `0 0 0 2px ${u.color}` }} />
                    ) : (
                      <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[9px] font-bold text-white"
                        style={{ backgroundColor: u.color }}>
                        {u.user_name.charAt(0).toLocaleUpperCase('en-US')}
                      </div>
                    )}
                    <span className="flex-1 truncate text-xs" style={{ color: u.color }}>{u.user_name}</span>
                    {u.user_id === user?.id && (
                      <span className="shrink-0 text-[9px] text-muted-foreground/50">{T.strategy.you}</span>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
  // Squad presets — loaded once and passed to marker properties panel
  const [squadPresets, setSquadPresets] = useState<{ name: string; color: string }[]>([])
  useEffect(() => {
    fetch('/api/squad-presets', { credentials: 'include' })
      .then(r => r.ok ? r.json() : [])
      .then((data: { name: string; color: string }[]) => setSquadPresets(data))
      .catch(() => {})
  }, [])

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {renderTopbar ? renderTopbar(topbar) : topbar}

      {/* Canvas area + optional right sidebar */}
      <div className="flex-1 flex overflow-hidden">

      {/* Canvas area with floating toolbar */}
      <div ref={containerRef} data-vpt-host className="relative flex-1 touch-none overflow-hidden" style={{ visibility: mapReady ? 'visible' : 'hidden' }}>

        {/* ── Araç rayı — sol kenar ──────────────────────────────────────────
            Panelin yüzeyiyle aynı dil (bkz. editorUi): kare simge düğmeleri,
            etkin olan kızıl kenar alır. Geri/ileri alma da buraya girdi;
            eskiden yalnızca Ctrl+Z ile vardı, yani el araçların yanındayken
            klavyeye gitmek gerekiyordu. */}
        <div className={`absolute left-3 top-1/2 z-10 hidden -translate-y-1/2 flex-col gap-1.5 p-1.5 md:flex ${editorSurface}`}>
          {toolBtn('select',  <MousePointer2 className="h-4 w-4" />, T.strategy.tools.select, 'F1')}
          {toolBtn('ping',    <Goal          className="h-4 w-4" />, T.strategy.tools.ping,   'F2')}
          {toolBtn('ruler',   <Ruler         className="h-4 w-4" />, T.strategy.tools.ruler,  'F8')}
          {canDraw && (
            <>
              <button
                onClick={() => { setTool('marker'); setRightPanelOpen(true) }}
                title={`${T.strategy.tools.markers} · F3`}
                aria-label={T.strategy.tools.markers}
                className={editorIconButton(tool === 'marker')}
              >
                <LandPlot className="h-4 w-4" />
              </button>
              <div className="mx-1 h-px bg-white/10" />
              {toolBtn('arrow',   <MoveUpRight className="h-4 w-4" />, T.strategy.tools.arrow,   'F4')}
              {toolBtn('polygon', <Hexagon     className="h-4 w-4" />, T.strategy.tools.polygon, 'F5')}
              {toolBtn('circle',  <CircleIcon  className="h-4 w-4" />, T.strategy.tools.circle,  'F6')}
              {toolBtn('hotzone', <span className="font-display text-[11px] font-bold leading-none tracking-wide">HZ</span>, T.strategy.tools.hotzone, 'F9')}
              {toolBtn('text',    <Type        className="h-4 w-4" />, T.strategy.tools.text,    'F7')}
              <div className="mx-1 h-px bg-white/10" />
              <button
                onClick={undo}
                disabled={!historyState.canUndo}
                title={`${T.strategy.undo} · Ctrl+Z`}
                aria-label={T.strategy.undo}
                className={editorIconButton()}
              >
                <Undo2 className="h-4 w-4" />
              </button>
              <button
                onClick={redo}
                disabled={!historyState.canRedo}
                title={`${T.strategy.redo} · Ctrl+Y`}
                aria-label={T.strategy.redo}
                className={editorIconButton()}
              >
                <Redo2 className="h-4 w-4" />
              </button>
            </>
          )}
        </div>

        {/* ── Yakınlaştırma kümesi — sol alt ──────────────────────────────────
            Tekerlek zaten yakınlaştırıyor, ama "tüm haritayı gör" hareketinin
            karşılığı yoktu: açılış kadrajına dönmek için elle uzaklaştırmak
            gerekiyordu. */}
        <div className={`absolute bottom-3 left-3 z-10 hidden flex-col gap-1.5 p-1.5 md:flex ${editorSurface}`}>
          <button onClick={() => zoomBy(1.6)} title={T.strategy.zoomIn} aria-label={T.strategy.zoomIn} className={editorIconButton()}>
            <ZoomIn className="h-4 w-4" />
          </button>
          <button onClick={() => zoomBy(1 / 1.6)} title={T.strategy.zoomOut} aria-label={T.strategy.zoomOut} className={editorIconButton()}>
            <ZoomOut className="h-4 w-4" />
          </button>
          <button onClick={zoomFit} title={T.strategy.zoomFit} aria-label={T.strategy.zoomFit} className={editorIconButton()}>
            <Maximize2 className="h-4 w-4" />
          </button>
        </div>

        {/* Right-drag rectangle selection overlay */}
        <div
          ref={selectionRectRef}
          className="absolute pointer-events-none border border-primary bg-primary/10 z-6"
          style={{ display: 'none', left: 0, top: 0, width: 0, height: 0 }}
        />

        {/* Harita zemini — canvas'ın altında, aynı viewport transform'uyla */}
        <MapTileLayer
          tiles={mapInfo?.tiles ?? null}
          base={mapInfo?.base ?? null}
          vpt={markerVpt}
          worldSize={CANVAS_SIZE}
          filter={mapFilter(mapInfo?.luma, mapView)}
          clip={playableClip}
          live
          subscribe={subscribeVpt}
        />

        {/* Izgara — zemin ile tuval arasında: çizimler üstünde kalır. */}
        {/* Izgaranın çizgileri tuvalde; burada yalnızca imleç koordinatı. */}
        {showGrid && <GridLayer worldSize={CANVAS_SIZE} cal={mapInfo?.game ?? DEFAULT_CALIBRATION} />}

        {/* Fabric `<canvas>`'ı kendi `div`'ine taşır, yani canvas React'in
            sandığı yerde durmaz. Önüne koşullu bir kardeş (ızgara) eklenince
            React onu canvas'ın önüne sokmaya çalışıyor ve `insertBefore`
            patlıyordu. Sabit sarmalayıcı Fabric'in taşımasını içine hapseder. */}
        <div>
          <canvas ref={canvasRef} />
        </div>

        <RulerLayer
          ref={rulerRef}
          worldSize={CANVAS_SIZE}
          cal={mapInfo?.game ?? DEFAULT_CALIBRATION}
          active={tool === 'ruler'}
          hint={T.strategy.rulerHint}
        />

        {/* ── Şekil tutamaçları ──────────────────────────────────────────────
            İğne (`HandleDot`): konum canlı viewport'tan, boy sabit. Zoom
            sırasında render olmaz. */}
        {selectedArrowState && (() => {
          const pts = selectedArrowState.pts
          const clr = selectedArrowState.color
          return (
            <div className="pointer-events-none absolute inset-0" style={{ zIndex: 20 }}>
              {/* Ghost midpoints — drag to create a breakpoint */}
              {pts.slice(0, -1).map((_, i) => (
                <HandleDot key={`ghost-${i}`}
                  x={(pts[i].x + pts[i + 1].x) / 2} y={(pts[i].y + pts[i + 1].y) / 2}
                  r={5} fill={clr} stroke="white" sw={1.5} opacity={0.55} cursor="crosshair"
                  onPointerDown={e => handleArrowHandleDown(e, i, 'ghost')} />
              ))}
              {/* Point handles — endpoints (white fill) vs intermediate (color fill) */}
              {pts.map((p, i) => {
                const isEnd = i === 0 || i === pts.length - 1
                return (
                  <HandleDot key={`pt-${i}`} x={p.x} y={p.y} r={isEnd ? 7 : 6}
                    fill={isEnd ? 'white' : clr} stroke={isEnd ? clr : 'white'} sw={2} cursor="move"
                    onPointerDown={e => handleArrowHandleDown(e, i, 'point')} />
                )
              })}
            </div>
          )
        })()}

        {selectedPolygonState && tool === 'select' && (() => {
          const pts = selectedPolygonState.pts
          const clr = selectedPolygonState.color
          return (
            <div className="pointer-events-none absolute inset-0" style={{ zIndex: 20 }}>
              {/* Ghost midpoints — drag to add a new vertex */}
              {pts.map((_, i) => {
                const n = (i + 1) % pts.length
                return (
                  <HandleDot key={`poly-ghost-${i}`}
                    x={(pts[i].x + pts[n].x) / 2} y={(pts[i].y + pts[n].y) / 2}
                    r={5} fill={clr} stroke="white" sw={1.5} opacity={0.55} cursor="crosshair"
                    onPointerDown={e => handlePolygonHandleDown(e, i, 'ghost')} />
                )
              })}
              {/* Point handles — first point larger + white fill, others color fill + white stroke */}
              {pts.map((p, i) => {
                const isFirst = i === 0
                return (
                  <HandleDot key={`poly-pt-${i}`} x={p.x} y={p.y} r={isFirst ? 7 : 6}
                    fill={isFirst ? 'white' : clr} stroke={isFirst ? clr : 'white'} sw={2} cursor="move"
                    onPointerDown={e => handlePolygonHandleDown(e, i, 'point')} />
                )
              })}
            </div>
          )
        })()}

        {selectedCircleState && tool === 'select' && (() => {
          const { cx, cy, radius, color } = selectedCircleState
          // Hotzone'un çapı sabit: yalnızca merkez tutamacı basılır.
          const hz = !!selectedCircleGroupRef.current?.__hz
          const edges = [
            { k: 'edge-n', x: cx, y: cy - radius, cursor: 'ns-resize' },
            { k: 'edge-s', x: cx, y: cy + radius, cursor: 'ns-resize' },
            { k: 'edge-e', x: cx + radius, y: cy, cursor: 'ew-resize' },
            { k: 'edge-w', x: cx - radius, y: cy, cursor: 'ew-resize' },
          ] as const
          return (
            <div className="pointer-events-none absolute inset-0" style={{ zIndex: 20 }}>
              {/* Center handle — white fill + color stroke (move) */}
              <HandleDot x={cx} y={cy} r={7} fill="white" stroke={color} sw={2} cursor="move"
                onPointerDown={e => handleCircleHandleDown(e, 'center')} />
              {/* Edge handles — color fill + white stroke (resize) */}
              {!hz && edges.map(h => (
                <HandleDot key={h.k} x={h.x} y={h.y} r={6} fill={color} stroke="white" sw={2} cursor={h.cursor}
                  onPointerDown={e => handleCircleHandleDown(e, h.k)} />
              ))}
            </div>
          )
        })()}

        {/* ── Markers overlay (DOM-based, above Fabric canvas) ─────────────── */}
        <div
          ref={markersOverlayRef}
          className="absolute inset-0 pointer-events-none"
          style={{ zIndex: 7, willChange: 'transform', transform: 'translateZ(0)' }}
        >
          {visibleMarkers.map(marker => {
            const iconSize = 24
            const triangleTop = iconSize + 2   // 26px — triangle starts here
            const triangleW = 18               // triangle top-edge width
            const triangleH = 10               // triangle height
            const triangleTipY = triangleTop + triangleH  // 40px — triangle tip = pivot
            const circleW = 18                 // oval width
            const circleH = 11                 // oval height (shorter = flatter)
            const markerColor = marker.color || '#ef4444'
            const fob = fobOf(marker.src)

            // Basış hem seçer hem sürükler — önce seçip ikinci basışta sürüklemek
            // "tuttum ama gelmiyor" hissi veriyordu.
            const handlePointerDown = (tool === 'select' || tool === 'marker') ? (e: React.PointerEvent) => {
              e.preventDefault(); e.stopPropagation()
              if (activeMarkerId !== marker.id) {
                setActiveMarkerId(marker.id)
                // Clear marker-type selection so properties panel shows instead of MarkersSidebar
                if (tool === 'marker') setSelectedMarker(null)
              }
              const fc = fabricRef.current
              const overlayEl = markersOverlayRef.current
              if (!fc || !overlayEl) return
              const sx = e.clientX, sy = e.clientY
              const wx = marker.worldX, wy = marker.worldY
              // İmlecin dünya koordinatı, o anki viewport'la. Sürükleme sırasında
              // tekerlekle yakınlaştırılabilir; basış anındaki zoom'la hesaplanan
              // hedef o zaman imlecin altından kayıyordu.
              const toWorld = (cx: number, cy: number) => {
                const r = overlayEl.getBoundingClientRect()
                const [a,,,, ve, vf] = fc.viewportTransform!
                return { x: (cx - r.left - ve) / a, y: (cy - r.top - vf) / a }
              }
              // Tutulan nokta ile marker'ın ucu arasındaki fark korunur: simge
              // gövdesinden tutulur, bırakınca uca zıplamamalı.
              const grab = toWorld(sx, sy)
              const gx = grab.x - wx, gy = grab.y - wy
              // Sürükleme sırasında state'e yazılmaz: her piksel ~4.5k satırlık
              // editörü yeniden çizdiriyordu. Marker ve baloncuğu DOM'da taşınır,
              // state bırakınca tek seferde yazılır.
              //
              // Taşıma CSS'in ayrı `translate` özelliğiyle yapılır, `left/top` ile
              // değil: o ikisini React yazıyor. Sarmalayıcının `left: 0`'ı hiç
              // değişmediği için React elle yazılan değeri bırakınca sıfırlamıyordu;
              // yeni konum transform'a girerken eski kaydırma da yerinde kalıyor,
              // marker sürüklenen mesafenin iki katına gidiyor ve her sürüklemede
              // bu birikiyordu. `translate`'e React dokunmaz, bırakınca biz sileriz.
              const overlayRoot = overlayEl.parentElement
              const els = overlayRoot
                ? Array.from(overlayRoot.querySelectorAll<HTMLElement>(`[data-marker-drag="${marker.id}"]`))
                : []
              let moved = false
              let target = { x: wx, y: wy }
              let raf: number | null = null
              const onMove = (me: PointerEvent) => {
                // Eşik ekran pikselinde: dünya biriminde olunca yakınlaştıkça
                // onlarca piksellik ölü bölgeye dönüşüyordu.
                if (!moved && (Math.abs(me.clientX - sx) > 3 || Math.abs(me.clientY - sy) > 3)) moved = true
                if (!moved) return
                const p = toWorld(me.clientX, me.clientY)
                target = { x: p.x - gx, y: p.y - gy }
                if (raf !== null) return
                raf = requestAnimationFrame(() => {
                  raf = null
                  // Öğeler hâlâ eski dünya konumunda çiziliyor; fark o anki
                  // ölçekle ekrana çevrilir.
                  const a = fc.getZoom()
                  const dx = (target.x - wx) * a, dy = (target.y - wy) * a
                  for (const el of els) el.style.translate = `${dx}px ${dy}px`
                })
              }
              const onUp = (ue: PointerEvent) => {
                window.removeEventListener('pointermove', onMove)
                window.removeEventListener('pointerup', onUp)
                window.removeEventListener('pointercancel', onUp)
                if (raf !== null) cancelAnimationFrame(raf)
                const commit = moved && ue.type === 'pointerup'
                if (commit) {
                  const p = toWorld(ue.clientX, ue.clientY)
                  target = { x: p.x - gx, y: p.y - gy }
                  // Yeni konum DOM'a yazılmadan geçici kaydırma silinmez, yoksa
                  // marker bir kare eski yerine dönüp geri gelir.
                  flushSync(() => {
                    updateCurrentMarkers(prev => prev.map(m =>
                      m.id === marker.id ? { ...m, worldX: target.x, worldY: target.y } : m))
                  })
                }
                for (const el of els) el.style.translate = ''
                if (!commit) return
                scheduleSave(); broadcastMarkerState()
              }
              window.addEventListener('pointermove', onMove)
              window.addEventListener('pointerup', onUp)
              window.addEventListener('pointercancel', onUp)
            } : undefined

            const handleContextMenu = (e: React.MouseEvent) => {
              e.preventDefault(); e.stopPropagation()
              setContextMenu({ x: e.clientX, y: e.clientY, type: 'marker', markerId: marker.id })
            }

            const hitStyle = {
              pointerEvents: (canDraw && (tool === 'select' || tool === 'marker') ? 'auto' : 'none') as React.CSSProperties['pointerEvents'],
              cursor: (canDraw && (tool === 'select' || tool === 'marker')) ? 'grab' : 'default',
            }

            return (
            <div
              key={marker.id}
              data-marker-drag={marker.id}
              className="absolute pointer-events-none"
              style={{
                // Konumu left/top yerine translate3d'de tut: 3D dönüşüm katmanı
                // GPU'da compose eder, yani yakınlaştırırken simge taşınır ama
                // drop-shadow her karede yeniden boyanmaz. left/top ile taşınınca
                // mobilde gölge iz bırakıyordu.
                left: 0, top: 0,
                transform: `${pinAt(marker.worldX, marker.worldY)} translate(-12px, -${triangleTipY}px)`,
              }}
            >
              {/* FOB alanı — 120 × 120 m, işaretçinin ucunda ortalanır ve
                  döner. `zIndex: -1`: bindirme katmanı kendi yığın bağlamı
                  olduğu için alanlar bütün simgelerin **altına** düşer, haritanın
                  üstünde kalır. Sürüklenirken sarmalayıcıyla birlikte taşınır. */}
              {fob && (() => {
                // Alan haritayla birlikte büyür: boyu canlı (`liveLen`), "çok
                // küçük, basma" kararı kaydedilen zoom'la.
                const sideW = (FOB_ZONE_M / MAP_WORLD_M) * CANVAS_SIZE
                if (sideW * markerVpt.a < 4) return null
                const side = liveLen(sideW)
                const halfUp = (px: number) => `calc(${sideW} * var(--va, 1) * -0.5px - ${px}px)`
                const rot = marker.rotation ?? 0
                const isActive = activeMarkerId === marker.id && canDraw

                // Tutamaç: merkeze göre imlecin açısı; 0° = kuzey. Sürükleme
                // sırasında yalnızca bu katmanın transform'u yazılır, state
                // bırakınca tek seferde (marker sürüklemesiyle aynı kural).
                const handleRotateDown = (e: React.PointerEvent) => {
                  e.preventDefault(); e.stopPropagation()
                  const layer = (e.currentTarget as HTMLElement).parentElement
                  const square = layer?.firstElementChild as HTMLElement | null
                  if (!layer || !square) return
                  const r = square.getBoundingClientRect()
                  const cx = r.left + r.width / 2, cy = r.top + r.height / 2
                  let next = rot
                  const onMove = (me: PointerEvent) => {
                    let deg = (Math.atan2(me.clientY - cy, me.clientX - cx) * 180 / Math.PI + 90 + 360) % 360
                    if (me.shiftKey) deg = (Math.round(deg / 15) * 15) % 360
                    next = deg
                    layer.style.transform = `rotate(${deg}deg)`
                  }
                  const onUp = () => {
                    window.removeEventListener('pointermove', onMove)
                    window.removeEventListener('pointerup', onUp)
                    window.removeEventListener('pointercancel', onUp)
                    if (next === rot) return
                    updateCurrentMarkers(prev => prev.map(m =>
                      m.id === marker.id ? { ...m, rotation: Math.round(next * 10) / 10 } : m))
                    scheduleSave(); broadcastMarkerState()
                  }
                  window.addEventListener('pointermove', onMove)
                  window.addEventListener('pointerup', onUp)
                  window.addEventListener('pointercancel', onUp)
                }

                return (
                  <div
                    className="pointer-events-none absolute"
                    style={{ left: 12, top: triangleTipY, width: 0, height: 0, zIndex: -1, transform: `rotate(${rot}deg)` }}
                  >
                    <div
                      className="absolute"
                      style={{
                        left: halfUp(0), top: halfUp(0), width: side, height: side,
                        border: `2px solid ${fob.color}`,
                        background: `${fob.color}14`,
                        boxShadow: '0 0 0 1px rgba(0,0,0,0.45), inset 0 0 0 1px rgba(0,0,0,0.45)',
                      }}
                    />
                    {isActive && (
                      <>
                        <div
                          className="absolute"
                          style={{ left: -1, top: halfUp(18), width: 2, height: 18, background: fob.color, opacity: 0.8 }}
                        />
                        <div
                          title={T.strategy.fobRotateHint}
                          onPointerDown={handleRotateDown}
                          onWheel={handleMarkerWheel}
                          className="absolute rounded-full"
                          style={{
                            left: -7, top: halfUp(32), width: 14, height: 14,
                            background: fob.color, border: '2px solid #fff',
                            boxShadow: '0 0 4px rgba(0,0,0,0.8)',
                            cursor: 'grab', pointerEvents: 'auto',
                          }}
                        />
                      </>
                    )}
                  </div>
                )
              })()}

              {/* Icon */}
              <div
                style={{ width: iconSize, height: iconSize, ...hitStyle }}
                onPointerDown={handlePointerDown}
                onContextMenu={handleContextMenu}
                onWheel={handleMarkerWheel}
              >
                <img
                  src={marker.src}
                  alt="marker"
                  draggable={false}
                  style={{
                    width: `${iconSize}px`, height: `${iconSize}px`,
                    objectFit: 'contain',
                    filter: 'drop-shadow(0 0 3px #000) drop-shadow(1px 2px 5px #000)',
                    display: 'block', pointerEvents: 'none', imageRendering: 'auto',
                  }}
                />
              </div>

              {/* Pivot circle — visual drag handle, rendered before triangle */}
              {activeMarkerId === marker.id && (
                <div
                  style={{
                    position: 'absolute', left: 12, top: triangleTipY,
                    transform: 'translate(-50%, -50%)',
                    width: circleW, height: circleH, borderRadius: '50%',
                    background: 'transparent', border: '2px solid #000',
                    boxShadow: '0 0 4px rgba(0,0,0,0.5)',
                    cursor: 'grab', pointerEvents: 'auto',
                  }}
                  onPointerDown={handlePointerDown}
                  onWheel={handleMarkerWheel}
                />
              )}

              {/* Inverted triangle */}
              <svg
                width={triangleW} height={triangleH}
                style={{
                  position: 'absolute', left: '50%', top: triangleTop,
                  transform: 'translateX(-50%)', overflow: 'visible',
                  ...hitStyle,
                }}
                onPointerDown={handlePointerDown}
                onContextMenu={handleContextMenu}
                onWheel={handleMarkerWheel}
              >
                <polygon
                  points={`0,0 ${triangleW},0 ${triangleW / 2},${triangleH}`}
                  fill={markerColor} stroke="black" strokeWidth="1.5" strokeLinejoin="round"
                />
              </svg>
            </div>
          )}
        )}
        </div>

        {/* ── Arrow start-marker overlay (DOM-based, above Fabric canvas) ──── */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ zIndex: 5, willChange: 'transform', transform: 'translateZ(0)' }}
        >
          {fabricRef.current?.getObjects()
            .filter((o): o is ArrowGroup => o instanceof ArrowGroup && !!(o as any).__startMarkerSrc)
            .map((arrow, i) => {
              const pts = (arrow as any).__pts as { x: number; y: number }[]
              if (!pts || pts.length === 0) return null
              const src = (arrow as any).__startMarkerSrc as string
              const color = (arrow as any).__clr as string
              const iconSize = 24
              const triangleTop = iconSize + 2   // 26px
              const triangleW = 18
              const triangleH = 10
              const triangleTipY = triangleTop + triangleH  // 36px — pivot point

              const handleMarkerClick = (e: React.PointerEvent) => {
                if (tool !== 'select') return
                e.preventDefault(); e.stopPropagation()
                // Select this arrow
                selectedPolygonGroupRef.current = null; setSelectedPolygonState(null)
                selectedCircleGroupRef.current = null; setSelectedCircleState(null)
                setSelectedTextShapeState(null)
                if (manuallySelectedTextShapeRef.current) { manuallySelectedTextShapeRef.current.lockMovementX = true; manuallySelectedTextShapeRef.current.lockMovementY = true; manuallySelectedTextShapeRef.current = null }
                selectedArrowGroupRef.current = arrow
                setSelectedArrowState({
                  pts: [...(arrow as any).__pts],
                  color: (arrow as any).__clr,
                  sw: (arrow as any).__sw,
                  ls: (arrow as any).__ls,
                  ah: (arrow as any).__ah,
                  title: (arrow as any).__title ?? '',
                  text: (arrow as any).__text ?? '',
                  squads: (arrow as any).__squads ?? [],
                  members: (arrow as any).__members ?? [],
                  startMarkerSrc: (arrow as any).__startMarkerSrc ?? null,
                  vehicle: (arrow as any).__vehicle ?? null,
                })
              }

              const hitStyle = {
                pointerEvents: (tool === 'select' ? 'auto' : 'none') as React.CSSProperties['pointerEvents'],
                cursor: tool === 'select' ? 'pointer' : 'default',
              }

              return (
                <div
                  key={i}
                  className="absolute pointer-events-none"
                  style={{
                    left: 0, top: 0,
                    transform: `${pinAt(pts[0].x, pts[0].y)} translate(-${iconSize / 2}px, -${triangleTipY}px)`,
                  }}
                >
                  {/* Icon */}
                  <div style={{ width: iconSize, height: iconSize, ...hitStyle }} onPointerDown={handleMarkerClick}>
                    <img
                      src={src}
                      alt=""
                      draggable={false}
                      style={{
                        width: `${iconSize}px`, height: `${iconSize}px`,
                        objectFit: 'contain',
                        filter: 'drop-shadow(0 0 3px #000) drop-shadow(1px 2px 5px #000)',
                        display: 'block', pointerEvents: 'none', imageRendering: 'auto',
                      }}
                    />
                  </div>

                  {/* Inverted triangle */}
                  <svg
                    width={triangleW} height={triangleH}
                    style={{
                      position: 'absolute', left: '50%', top: triangleTop,
                      transform: 'translateX(-50%)', overflow: 'visible',
                      ...hitStyle,
                    }}
                    onPointerDown={handleMarkerClick}
                  >
                    <polygon
                      points={`0,0 ${triangleW},0 ${triangleW / 2},${triangleH}`}
                      fill={color} stroke="black" strokeWidth="1.5" strokeLinejoin="round"
                    />
                  </svg>
                </div>
              )
            })}
        </div>

        {/* ── Arrow tooltips overlay ─────────────────────────────────────── */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ zIndex: 8, willChange: 'transform', transform: 'translateZ(0)' }}
        >
          {fabricRef.current?.getObjects()
            .filter((o): o is ArrowGroup => o instanceof ArrowGroup)
            .sort((a, b) => {
              const ay = ((a as any).__pts as { x: number; y: number }[])?.[0]?.y ?? 0
              const by = ((b as any).__pts as { x: number; y: number }[])?.[0]?.y ?? 0
              return ay - by
            })
            .map((arrow, i) => {
              const pts = (arrow as any).__pts as { x: number; y: number }[]
              if (!pts || pts.length === 0) return null
              const title = (arrow as any).__title as string
              const text = (arrow as any).__text as string
              const squads = (arrow as any).__squads as { name: string; color: string }[]
              const members = (arrow as any).__members as { id: string; name: string; avatar_url: string }[]
              const vehicle = (arrow as any).__vehicle as { id: number; name: string; speed: number } | null
              const hasStartMarker = !!(arrow as any).__startMarkerSrc
              const color = (arrow as any).__clr as string
              const hasTooltip = !!(title || text || squads.length || members.length || vehicle)
              if (!hasTooltip) return null

              // Anchor tooltip above the start marker icon (if present) or above the point itself
              // Icon (24) + gap (2) + triangle (10) + small padding (2) = 38 above the point
              const offsetAbove = hasStartMarker ? 38 : 6

              return (
                <div
                  key={i}
                  className="pointer-events-none"
                  style={{
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    transform: `${pinAt(pts[0].x, pts[0].y)} translate(-50%, calc(-100% - ${offsetAbove}px))`,
                    minWidth: 80,
                    maxWidth: 180,
                    border: `2px solid ${color}`,
                    borderRadius: 6,
                    background: 'rgba(0, 0, 0, 0.6)',
                    filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.5))',
                  }}
                >
                  {title && (
                    <div
                      style={{
                        background: color,
                        padding: '2px 8px',
                        fontSize: 11,
                        fontWeight: 600,
                        color: '#fff',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        borderRadius: '4px 4px 0 0',
                        marginLeft: -2,
                        marginRight: -2,
                        marginTop: -2,
                      }}
                    >
                      {title}
                    </div>
                  )}
                  {vehicle && (
                    <div style={{ padding: '3px 6px 0', fontSize: 10, color: '#fff', display: 'flex', alignItems: 'center', gap: 4 }}>
                      <span style={{ fontWeight: 600 }}>{vehicle.name}</span>
                      <span style={{ color: '#bbb' }}>· {vehicle.speed} km/h</span>
                    </div>
                  )}
                  {squads.length > 0 && (
                    <div style={{ padding: '4px 6px 0', display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                      {squads.map(squad => (
                        <span
                          key={squad.name}
                          style={{
                            background: squad.color + '99',
                            border: `1px solid ${squad.color}`,
                            borderRadius: 3,
                            padding: '0 4px',
                            fontSize: 9,
                            fontWeight: 600,
                            color: '#fff',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {squad.name}
                        </span>
                      ))}
                    </div>
                  )}
                  {members.length > 0 && (
                    <div style={{ padding: '4px 6px', display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                      {members.map(member => (
                        <div
                          key={member.id}
                          style={{ position: 'relative', width: 20, height: 20, pointerEvents: 'auto', cursor: 'default' }}
                          onWheel={handleMarkerWheel}
                          className="group/amember"
                        >
                          <img
                            src={member.avatar_url}
                            alt={member.name}
                            style={{ width: 20, height: 20, borderRadius: '50%', objectFit: 'cover', display: 'block' }}
                            onError={e => { (e.target as HTMLImageElement).src = '/default-avatar.png' }}
                          />
                          <div
                            className="opacity-0 group-hover/amember:opacity-100 transition-opacity pointer-events-none"
                            style={{
                              position: 'absolute', bottom: '100%', left: '50%', transform: 'translateX(-50%)',
                              marginBottom: 4, background: 'rgba(0,0,0,0.9)', color: '#fff',
                              padding: '2px 6px', borderRadius: 3, fontSize: 9, fontWeight: 600,
                              whiteSpace: 'nowrap', zIndex: 10,
                            }}
                          >
                            {member.name}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {text && (
                    <div style={{ padding: '2px 8px 4px', fontSize: 10, color: '#ddd', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {text.length > 100 ? text.slice(0, 100) + '...' : text}
                    </div>
                  )}
                </div>
              )
            })}
        </div>

        {/* ── Marker tooltips overlay — ayrı katmanda render edilir, marker ikonlarının üstünde ── */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ zIndex: 9, willChange: 'transform', transform: 'translateZ(0)' }}
        >
          {[...visibleMarkers].sort((a, b) => a.worldY - b.worldY).map(marker => {
            const iconSize = 24
            const triangleTop = iconSize + 2
            const triangleH = 10
            const triangleTipY = triangleTop + triangleH
            const markerColor = marker.color || '#ef4444'
            const hasTooltip = !!(marker.title || marker.text || marker.squads.length || marker.members.length)
            if (!hasTooltip) return null

            // Tooltip'in alt kenarı ekran koordinatında:
            // wrapper_top = screenY - triangleTipY
            // wrapper_bottom = wrapper_top + iconSize
            // tooltip_bottom = wrapper_bottom - (triangleTop + 4)
            const tooltipLift = triangleTipY - iconSize + (triangleTop + 4)

            return (
              <div
                key={marker.id}
                data-marker-drag={marker.id}
                className="pointer-events-none"
                style={{
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  transform: `${pinAt(marker.worldX, marker.worldY)} translate(-50%, calc(-100% - ${tooltipLift}px))`,
                  minWidth: 80,
                  maxWidth: 160,
                  border: `2px solid ${markerColor}`,
                  borderRadius: 6,
                  background: 'rgba(0, 0, 0, 0.6)',
                  filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.5))',
                }}
              >
                {/* Title bar */}
                {marker.title && (
                  <div
                    style={{
                      background: markerColor,
                      padding: '2px 8px',
                      fontSize: 11,
                      fontWeight: 600,
                      color: '#fff',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      borderRadius: '4px 4px 0 0',
                      marginLeft: -2,
                      marginRight: -2,
                      marginTop: -2,
                    }}
                  >
                    {marker.title}
                  </div>
                )}
                {/* Squads */}
                {marker.squads.length > 0 && (
                  <div style={{ padding: '4px 6px 0', display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                    {marker.squads.map(squad => (
                      <span
                        key={squad.name}
                        style={{
                          background: squad.color + '99',
                          border: `1px solid ${squad.color}`,
                          borderRadius: 3,
                          padding: '0 4px',
                          fontSize: 9,
                          fontWeight: 600,
                          color: '#fff',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {squad.name}
                      </span>
                    ))}
                  </div>
                )}
                {/* Members */}
                {marker.members.length > 0 && (
                  <div style={{ padding: '4px 6px', display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                    {marker.members.map(member => (
                      <div
                        key={member.id}
                        style={{ position: 'relative', width: 20, height: 20, pointerEvents: 'auto', cursor: 'default' }}
                        className="group/member"
                        onWheel={handleMarkerWheel}
                      >
                        <img
                          src={member.avatar_url}
                          alt={member.name}
                          style={{
                            width: 20, height: 20,
                            borderRadius: '50%',
                            objectFit: 'cover',
                            display: 'block',
                          }}
                          onError={e => { (e.target as HTMLImageElement).src = '/default-avatar.png' }}
                        />
                        <div
                          className="opacity-0 group-hover/member:opacity-100 transition-opacity pointer-events-none"
                          style={{
                            position: 'absolute',
                            bottom: '100%',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            marginBottom: 4,
                            background: 'rgba(0,0,0,0.9)',
                            color: '#fff',
                            padding: '2px 6px',
                            borderRadius: 3,
                            fontSize: 9,
                            fontWeight: 600,
                            whiteSpace: 'nowrap',
                            zIndex: 10,
                          }}
                        >
                          {member.name}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {/* Description */}
                {marker.text && (
                  <div style={{ padding: '2px 8px 4px', fontSize: 10, color: '#ddd', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {marker.text.length > 100 ? marker.text.slice(0, 100) + '...' : marker.text}
                  </div>
                )}
              </div>
            )
          })}
        </div>

        {/* Marker preview — follows cursor when marker tool is active */}
        {tool === 'marker' && selectedMarker && (() => {
          const pvIconSize = 24
          const pvTriangleTop = pvIconSize + 2
          const pvTriangleW = 18
          const pvTriangleH = 10
          return (
            <div
              // Konum ve görünürlük `placeMarkerPreview`'den (uç: 24 + 2 + 10 = 36 px).
              ref={el => { markerPreviewRef.current = el; placeMarkerPreview() }}
              className="absolute pointer-events-none z-9"
              style={{ left: 0, top: 0, opacity: 0.85 }}
            >
              <img
                src={selectedMarker.src}
                alt="marker preview"
                draggable={false}
                style={{
                  width: pvIconSize,
                  height: pvIconSize,
                  objectFit: 'contain',
                  filter: 'drop-shadow(0 0 3px #000) drop-shadow(1px 2px 5px #000)',
                  display: 'block',
                  pointerEvents: 'none',
                  imageRendering: 'auto',
                }}
              />
              <svg
                width={pvTriangleW}
                height={pvTriangleH}
                style={{
                  position: 'absolute',
                  left: '50%',
                  top: pvTriangleTop,
                  transform: 'translateX(-50%)',
                  overflow: 'visible',
                  pointerEvents: 'none',
                }}
              >
                <polygon
                  points={`0,0 ${pvTriangleW},0 ${pvTriangleW / 2},${pvTriangleH}`}
                  fill="#ffffff"
                  stroke="black"
                  strokeWidth="1.5"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          )
        })()}

        {/* ── Mevkiler ── bkz. `showCallouts`. Katman tıklanmaz
            (`pointer-events: none`), altındaki tuval çalışmaya devam eder. */}
        {showCallouts && (
          <CalloutLayer callouts={callouts} vpt={markerVpt} zIndex={3} live />
        )}

        {/* ── Kule ve kontrol bölgesi katmanı ───────────────────────────────
            Haritanın kendi unsurları: nerede kule var ve o rotasyonda savaş
            hangi çemberin içinde geçiyor. Veri `lib/mapZones.ts`'te.

            **Tek renk: beyaz.** Çember, kenar yıkaması, bölge adı ve kule
            simgesi aynı renkte; hepsi aynı şeyin parçası (bölge ve içindeki
            kuleler). Simge dosyanın kendi beyazıyla basılır
            (bkz. `TOWER_ICON`), gerisi `ZONE_COLOR`. Çizgi iki kez basılır
            (altta siyah halo, üstünde renk) — tek kat ince çizgi parlak
            tarlada kayboluyor.

            **Çember kesik değil düz.** Kesik çizginin aralığı ekran pikselinde
            sabit, çevresi ise yakınlaştıkça büyüyor: aynı çember bir kadrajda
            12, diğerinde 90 parçaya bölünüyor ve her zoom'da başka bir şey gibi
            görünüyordu. İç kenardaki çok hafif lacivert yıkama (`zone-wash`)
            dolgunun tamamını boyamadan çemberin içini belli eder.

            Bölge kapalıyken (varsayılan) katmanın tamamı düşer: kuleler de
            bölgeyle birlikte gelir, çünkü hangi kulelerin oynandığını söyleyen
            şey bölgedir. */}
        {activeZone && (() => {
          // Konum, boy ve "göster" kararları CSS'te canlı viewport'tan
          // (bkz. `lib/liveViewport.ts`): zoom sırasında bu katman render olmaz.
          const towers = towersOf(mapName)
          const C = CANVAS_SIZE
          const cx = activeZone.x * C, cy = activeZone.y * C
          const r = (activeZone.radiusM / MAP_WORLD_M) * C
          const pairs = towers.flatMap((t, i) => towers.slice(i + 1).map(o => [t, o] as const))
          /**
           * Kule adları ancak birbirine girmeyecek kadar açılmışken basılır —
           * üst üste binen dört etiket, etiketsiz dört kuleden kötüdür. Ölçüt
           * kaynaktakinin aynısı, simge 48 px olduğu için gevşetilmiş hâli:
           * her çift ya yatayda 96 px ya da dikeyde 66 px ayrık olmalı. Çift
           * başına gereken en küçük ölçek, hepsinin en büyüğü eşiktir.
           */
          const labelsFrom = pairs.reduce((m, [t, o]) => Math.max(m, Math.min(
            96 / (Math.abs(t.x - o.x) * C || 1e-9), 66 / (Math.abs(t.y - o.y) * C || 1e-9))), 0)
          /**
           * Kuleler birbirine **girmeyecek kadar** yakınlaşılmışsa basılır.
           * Simge ekran boyunda sabit (48 px), kuleler ise birbirine 2 km'den
           * yakın: tüm harita ekrandayken beş kule tek bir lekeye dönüşüyor.
           * O kadrajda söylenecek şey zaten çemberin kendisi.
           */
          const towersFrom = pairs.reduce((m, [t, o]) => Math.max(m,
            52 / (Math.hypot(t.x - o.x, t.y - o.y) * C || 1e-9)), 0)

          return (
            <div className="pointer-events-none absolute inset-0" style={{ zIndex: 3 }} aria-hidden>
              {/* Çember: düz beyaz çizgi + altında siyah halo, içte çok hafif
                  kenar yıkaması. Bir div: boyu `liveLen`, yıkama `closest-side`
                  radyal gradyan olduğu için çemberle birlikte ölçeklenir. */}
              <div
                className="absolute rounded-full"
                style={{
                  left: 0, top: 0,
                  width: liveLen(2 * r), height: liveLen(2 * r),
                  transform: `${pinAt(cx, cy)} translate(-50%, -50%)`,
                  border: `2px solid ${ZONE_COLOR}f2`,
                  boxShadow: '0 0 0 1.5px rgba(0,0,0,0.55), inset 0 0 0 1.5px rgba(0,0,0,0.55)',
                  background: `radial-gradient(circle closest-side, transparent 65%, ${ZONE_COLOR}0d 90%, ${ZONE_COLOR}1f 100%)`,
                }}
              />

              {/* Bölge adı — çemberin tepesinde. Ad **veridir**, büyütülmez. */}
              <span
                className="absolute whitespace-nowrap font-display text-[11px] font-bold tracking-[0.12em]"
                style={{
                  color: ZONE_COLOR,
                  left: 0, top: 0,
                  transform: `${pinAt(cx, cy)} translate(-50%, calc(-100% - ${liveLen(r)} - 6px))`,
                  textShadow: '0 1px 2px #000, 1px 0 2px #000, -1px 0 2px #000, 0 -1px 2px #000',
                }}
              >
                {activeZone.name}
              </span>

              <div className="absolute inset-0" style={{ opacity: visibleFrom(towersFrom) }}>
                {towers.map((t, i) => {
                  const inZone = activeZone.towers.includes(i)
                  return (
                    <div
                      key={t.label}
                      className="absolute"
                      style={{ left: 0, top: 0, transform: `${pinAt(t.x * C, t.y * C)} translate(-50%, -50%)`, opacity: inZone ? 1 : 0.4 }}
                    >
                      {/*
                        Kutu yok, maske yok: çizimin **kendisi** basılır. Dosya
                        zaten şeffaf, içeriğine kırpılmış ve rengi katmanın
                        rengiyle aynı (bkz. `TOWER_ICON`) — böylece hem beyaz
                        dolgu hem siyah kontur yerinde kalır, 48 px gerçekten
                        48 px olur. Gölge silueti zeminden ayırır.
                      */}
                      <img
                        src={TOWER_ICON}
                        alt=""
                        draggable={false}
                        className="block h-12 w-12"
                        style={{
                          filter: 'drop-shadow(0 0 3px rgba(0,0,0,0.95)) drop-shadow(0 2px 5px rgba(0,0,0,0.85))',
                        }}
                      />
                      <span
                        className="absolute left-1/2 top-[50px] -translate-x-1/2 whitespace-nowrap text-[11px] font-semibold text-foreground"
                        style={{
                          opacity: visibleFrom(labelsFrom),
                          textShadow: '0 1px 2px #000, 1px 0 2px #000, -1px 0 2px #000, 0 -1px 2px #000',
                        }}
                      >
                        {towerLabel(t.label)}
                      </span>
                    </div>
                  )
                })}
              </div>
            </div>
          )
        })()}

        {/* ── Taraf üsleri (HQ) ─────────────────────────────────────────────
            Rotasyonda oynanan üç üs, tarafın amblemiyle. Veri
            `lib/mapZones.ts` → `MAP_BASES`. Bölge seçili değilken de basılır:
            rotasyon haritanın varsayılan bölgesinden okunur.

            Üsler haritanın kenarlarında, birbirinden kilometrelerce uzakta —
            tüm harita kadrajında bile ayrık durur, bu yüzden simge her zaman
            basılır; yalnızca adlar birbirine (ya da kulelere) girecekse düşer. */}
        {showBases && mapBases.length > 0 && (() => {
          const worldPx = CANVAS_SIZE * markerVpt.a
          const towers = activeZone ? towersOf(mapName) : []
          const showLabels = mapBases.every((b, i) =>
            mapBases.slice(i + 1).every(o =>
              Math.abs(b.x - o.x) * worldPx >= 108 || Math.abs(b.y - o.y) * worldPx >= 56) &&
            towers.every(t =>
              Math.abs(b.x - t.x) * worldPx >= 96 || Math.abs(b.y - t.y) * worldPx >= 72))
          return (
            <div className="pointer-events-none absolute inset-0" style={{ zIndex: 3 }}>
              {mapBases.map(b => {
                const f = factionOf(b.faction)
                if (!f) return null
                return (
                  <div
                    key={b.town}
                    role="img"
                    aria-label={`${f.label} HQ`}
                    className="absolute h-9 w-9"
                    style={{
                      left: 0, top: 0,
                      transform: `${pinAt(b.x * CANVAS_SIZE, b.y * CANVAS_SIZE)} translate(-50%, -50%)`,
                    }}
                  >
                    <img
                      src={f.icon}
                      alt=""
                      aria-hidden
                      draggable={false}
                      className="block h-9 w-9 object-contain"
                      style={{ filter: 'drop-shadow(0 0 2px rgba(0,0,0,0.95)) drop-shadow(0 2px 5px rgba(0,0,0,0.85))' }}
                    />
                    {/* Yalnızca taraf adı: şehir adı oyuncunun üssü tanıdığı ad değil. Özel isim — büyütülmez. */}
                    {showLabels && (
                      <span
                        aria-hidden
                        className="absolute left-1/2 top-[40px] -translate-x-1/2 whitespace-nowrap text-center leading-[14px]"
                        style={{ textShadow: '0 1px 2px #000, 1px 0 2px #000, -1px 0 2px #000, 0 -1px 2px #000' }}
                      >
                        <span className="block text-[11px] font-semibold text-foreground">{f.label} HQ</span>
                      </span>
                    )}
                  </div>
                )
              })}
            </div>
          )
        })()}

        {/* ── Strategic Points overlay ─────────────────────────────────────── */}
        {strategicPoints.length > 0 && (() => {
          const SP_ICON_SIZE = 36
          const SP_TRI_W = 24
          const SP_TRI_H = 12
          const SP_TRI_TOP = SP_ICON_SIZE + 2   // 38
          const SP_PIVOT_Y = SP_TRI_TOP + SP_TRI_H  // 50
          return (
            <div
              className="absolute inset-0 pointer-events-none"
              style={{ zIndex: 4 }}
            >
              {strategicPoints.map(sp => {
                const isPinned = pinnedSPIds.includes(sp.id)
                if (!showStrategicPoints && !isPinned) return null
                const iconEntry = POINT_ICONS[sp.icon_type] || POINT_ICONS.other
                const IconComp = iconEntry.icon
                const color = iconEntry.color
                const isSelected = selectedSPId === sp.id

                return (
                  <div
                    key={sp.id}
                    data-ui-panel
                    className="absolute"
                    style={{
                      left: 0, top: 0,
                      transform: `${pinAt(sp.x, sp.y)} translate(-${SP_ICON_SIZE / 2}px, -${SP_PIVOT_Y}px)`,
                      pointerEvents: 'auto',
                      cursor: 'pointer',
                      opacity: isPinned ? 1 : 0.75,
                      zIndex: isPinned ? 5 : 4,
                    }}
                    onWheel={handleMarkerWheel}
                    onClick={e => { e.stopPropagation(); setSelectedSPId(sp.id); setRightPanelOpen(true); setSpEditMode(false); setSpMemberSearch('') }}
                  >
                    {/* Icon — same as SPEditor: no background, drop-shadow */}
                    <div
                      className="flex items-center justify-center"
                      style={{
                        width: SP_ICON_SIZE,
                        height: SP_ICON_SIZE,
                        filter: 'drop-shadow(0 0 3px #000) drop-shadow(1px 2px 5px #000)',
                        outline: isSelected ? '2px solid #000' : 'none',
                        outlineOffset: -1,
                        borderRadius: 4,
                      }}
                    >
                      <IconComp className="w-5 h-5" style={{ color }} strokeWidth={2.5} />
                    </div>
                    {/* Inverted triangle — same as SPEditor */}
                    <svg
                      width={SP_TRI_W}
                      height={SP_TRI_H}
                      viewBox={`0 0 ${SP_TRI_W} ${SP_TRI_H}`}
                      style={{
                        position: 'absolute',
                        left: '50%',
                        top: SP_TRI_TOP,
                        transform: 'translateX(-50%)',
                        filter: 'drop-shadow(0 2px 3px rgba(0,0,0,0.7))',
                        overflow: 'visible',
                      }}
                    >
                      <polygon
                        points={`0,0 ${SP_TRI_W},0 ${SP_TRI_W / 2},${SP_TRI_H}`}
                        fill={color}
                        stroke="#000"
                        strokeWidth={1.5}
                      />
                    </svg>
                    {/* Label */}
                    {showStrategicPoints && sp.label && (
                      <div
                        className="absolute left-1/2 -translate-x-1/2 whitespace-nowrap rounded px-1.5 py-0.5 text-[10px] font-bold text-white pointer-events-none"
                        style={{
                          top: SP_PIVOT_Y + 4,
                          background: 'rgba(0,0,0,0.65)',
                          border: `1px solid ${color}66`,
                        }}
                      >
                        {sp.label}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )
        })()}

        {/* ── SP annotation tooltips ───────────────────────────────────────── */}
        {strategicPoints.length > 0 && (() => {
          const SP_ICON_SIZE = 36
          const SP_TRI_TOP = SP_ICON_SIZE + 2
          const SP_TRI_H = 12
          const SP_PIVOT_Y = SP_TRI_TOP + SP_TRI_H
          return (
            <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 6 }}>
              {strategicPoints.map(sp => {
                const isPinned = pinnedSPIds.includes(sp.id)
                if (!showStrategicPoints && !isPinned) return null
                const ann = spAnnotations[sp.id]
                if (!ann || (!ann.note && ann.players.length === 0)) return null
                const iconEntry = POINT_ICONS[sp.icon_type] || POINT_ICONS.other
                const color = iconEntry.color
                return (
                  <div
                    key={sp.id}
                    className="pointer-events-none"
                    style={{
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      transform: `${pinAt(sp.x, sp.y)} translate(-50%, calc(-100% - ${SP_PIVOT_Y + 6}px))`,
                      minWidth: 80,
                      maxWidth: 180,
                      border: `2px solid ${color}`,
                      borderRadius: 6,
                      background: 'rgba(0, 0, 0, 0.6)',
                      filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.6))',
                    }}
                  >
                    {/* Label bar */}
                    <div style={{
                      background: color,
                      padding: '2px 8px',
                      fontSize: 11,
                      fontWeight: 600,
                      color: '#fff',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      borderRadius: '4px 4px 0 0',
                      marginLeft: -2, marginRight: -2, marginTop: -2,
                    }}>
                      {sp.label || iconEntry.label}
                    </div>
                    {/* Members */}
                    {ann.players.length > 0 && (
                      <div style={{ padding: '4px 6px', display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                        {ann.players.map(member => (
                          <div
                            key={member.id}
                            style={{ position: 'relative', width: 22, height: 22, pointerEvents: 'auto', cursor: 'default' }}
                            className="group/spmember"
                            onWheel={handleMarkerWheel}
                          >
                            <img
                              src={member.avatar_url}
                              alt={member.name}
                              style={{ width: 22, height: 22, borderRadius: '50%', objectFit: 'cover', display: 'block', border: `1.5px solid ${color}44` }}
                              onError={e => { (e.target as HTMLImageElement).src = '/default-avatar.png' }}
                            />
                            <div
                              className="opacity-0 group-hover/spmember:opacity-100 transition-opacity pointer-events-none"
                              style={{
                                position: 'absolute', bottom: '100%', left: '50%',
                                transform: 'translateX(-50%)', marginBottom: 4,
                                background: 'rgba(0,0,0,0.9)', color: '#fff',
                                padding: '2px 6px', borderRadius: 3,
                                fontSize: 9, fontWeight: 600, whiteSpace: 'nowrap', zIndex: 10,
                              }}
                            >
                              {member.name}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    {/* Note */}
                    {ann.note && (
                      <div style={{ padding: '2px 8px 5px', fontSize: 10, color: '#ddd', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {ann.note.length > 120 ? ann.note.slice(0, 120) + '…' : ann.note}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )
        })()}

        {/* Ping overlays */}
        {pings.map(ping => {
          const c = ping.color
          return (
            <div key={ping.id} className="absolute pointer-events-none z-20 animate-ping-fadeout"
              style={{ left: 0, top: 0, transform: `${pinAt(ping.x, ping.y)} translate(-50%, -50%)` }}>
              <div className="h-10 w-10 rounded-full border-2 animate-ping opacity-80" style={{ borderColor: c }} />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="h-2 w-2 rounded-full" style={{ backgroundColor: c }} />
              </div>
              <span className="absolute top-11 left-1/2 -translate-x-1/2 text-[10px] font-semibold
                whitespace-nowrap bg-black/70 px-1.5 py-0.5 rounded" style={{ color: c }}>
                {ping.user_name}
              </span>
            </div>
          )
        })}

        {/* Right panel — floating like left toolbar */}
        {(() => {
          const TOOL_LABELS: Record<Tool, string> = {
            select: T.strategyEditor.toolSelect,
            ping: T.strategyEditor.toolPing,
            arrow: T.strategyEditor.toolArrow,
            circle: T.strategyEditor.toolCircle,
            polygon: T.strategyEditor.toolPolygon,
            text: T.strategyEditor.toolText,
            marker: T.strategyEditor.toolMarker,
            ruler: T.strategy.tools.ruler,
            hotzone: T.strategy.tools.hotzone,
          }
          // Find active marker data for the properties panel
          const activeMarkerData = activeMarkerId ? currentMarkers.find(m => m.id === activeMarkerId) : null
          // When a shape is selected in select mode, show its type as the panel label
          const selectedSP = selectedSPId !== null ? strategicPoints.find(s => s.id === selectedSPId) : null
          const panelLabel = selectedSP
            ? pointIconLabel(selectedSP.icon_type, T)
            : activeMarkerData ? T.strategyEditor.toolMarkerSingle
            : tool === 'select'
              ? (selectedArrowState ? T.strategyEditor.toolArrow : selectedPolygonState ? T.strategyEditor.toolPolygon : selectedCircleState ? (selectedCircleGroupRef.current?.__hz ? T.strategy.tools.hotzone : T.strategyEditor.toolCircle) : selectedTextShapeState ? T.strategyEditor.toolText : T.strategyEditor.toolSelect)
              : TOOL_LABELS[tool]
          const drawingTools: Tool[] = ['arrow', 'circle', 'polygon', 'text']
          const strokeTools:  Tool[] = ['arrow', 'circle', 'polygon']
          const fillTools:    Tool[] = ['circle', 'polygon']
          const lsOptions = lineStyleOptions(T.strategy)
          const bgOptions: { value: TextBg; label: string }[] = [
            { value: 'none',   label: T.strategy.bgNone },
            { value: 'solid',  label: T.strategy.bgSolid },
            { value: 'speech', label: T.strategy.bgBubble },
          ]
          return (
            <div
              className={`absolute right-3 top-1/2 z-10 hidden -translate-y-1/2 flex-col overflow-hidden transition-[width] duration-300 md:flex ${editorSurface}`}
              data-ui-panel
              style={{ width: rightPanelOpen ? 340 : 40, maxHeight: 'calc(100% - 24px)' }}
            >
              {/* Başlık satırı — panel kapalıyken de durur.
                  Etiket `uppercase` ile büyütülür, JS ile değil: `toUpperCase`
                  sayfanın dilini bilmez ve Türkçede 'i' harfini bozar. */}
              <div className="flex h-10 shrink-0 items-center gap-2 border-b border-white/10 px-1.5">
                <button
                  onClick={() => setRightPanelOpen(v => !v)}
                  className={editorIconButton(false, 'h-7 w-7')}
                  title={rightPanelOpen ? T.strategyEditor.collapsePanel : T.strategyEditor.expandPanel}
                >
                  <ChevronRight className={`h-4 w-4 transition-transform duration-200 ${rightPanelOpen ? '' : 'rotate-180'}`} />
                </button>
                {rightPanelOpen && (
                  <>
                    <span className={`flex-1 truncate uppercase ${editorLabel}`}>
                      {panelLabel}
                    </span>
                    {selectedSP && (
                      <button
                        onClick={() => { setSpEditMode(v => !v); setSpMemberSearch('') }}
                        className={editorIconButton(spEditMode, 'h-7 w-7')}
                        title={T.strategyEditor.noteAssignPlayers}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </>
                )}
              </div>

              {/* Panel content */}
              {rightPanelOpen && (
                selectedSP ? (
                  <div className="flex-1 overflow-y-auto overflow-x-hidden p-3 space-y-3 min-w-0">
                    {/* Icon + label */}
                    {(() => {
                      const iconEntry = POINT_ICONS[selectedSP.icon_type] || POINT_ICONS.other
                      const IconComp = iconEntry.icon
                      const color = iconEntry.color
                      return (
                        <div className="flex items-center gap-2 pb-1">
                          <IconComp className="w-5 h-5 shrink-0" style={{ color }} strokeWidth={2.5} />
                          <span className="text-sm font-semibold truncate">{selectedSP.label || iconEntry.label}</span>
                        </div>
                      )
                    })()}
                    {/* Description */}
                    {selectedSP.description && (
                      <p className="text-xs text-foreground/70 leading-relaxed whitespace-pre-wrap">{selectedSP.description}</p>
                    )}
                    {/* Media */}
                    {selectedSP.media.length > 0 && (
                      <div className="space-y-2">
                        {selectedSP.media.map(m => {
                          const ytId = getYouTubeId(m.filename ?? '') ?? getYouTubeId(m.url)
                          return (
                            <div key={m.id} className="rounded overflow-hidden border border-white/10 bg-black/20">
                              {ytId ? (
                                // YouTube — thumbnail with play button, click opens lightbox
                                <div
                                  className="relative w-full cursor-pointer group"
                                  style={{ aspectRatio: '16/9' }}
                                  onClick={() => { setSpLightboxUrl(ytId); setSpLightboxType('youtube') }}
                                >
                                  <img
                                    src={`https://img.youtube.com/vi/${ytId}/mqdefault.jpg`}
                                    alt=""
                                    className="w-full h-full object-cover block"
                                  />
                                  <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/50 transition-colors">
                                    <div className="w-12 h-12 rounded-full bg-red-600/90 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                                      <svg className="w-6 h-6 text-white translate-x-[2px]" fill="currentColor" viewBox="0 0 24 24">
                                        <path d="M8 5v14l11-7z" />
                                      </svg>
                                    </div>
                                  </div>
                                </div>
                              ) : m.media_type === 'image' ? (
                                // Image — click opens lightbox
                                <img
                                  src={m.url}
                                  alt=""
                                  className="w-full h-auto block cursor-zoom-in"
                                  onClick={() => { setSpLightboxUrl(m.url); setSpLightboxType('image') }}
                                />
                              ) : (
                                // Video — inline player + expand button
                                <div className="relative group/vid">
                                  <video src={m.url} controls className="w-full h-auto block" />
                                  <button
                                    onClick={() => { setSpLightboxUrl(m.url); setSpLightboxType('video') }}
                                    className="absolute top-1.5 right-1.5 h-6 w-6 rounded bg-black/60 hover:bg-black/80 flex items-center justify-center opacity-0 group-hover/vid:opacity-100 transition-opacity"
                                    title={T.strategicPoints.media.expand}
                                  >
                                    <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" d="M4 8V4h4M20 8V4h-4M4 16v4h4M20 16v4h-4" />
                                    </svg>
                                  </button>
                                </div>
                              )}
                            </div>
                          )
                        })}
                      </div>
                    )}
                    {selectedSP.media.length === 0 && !selectedSP.description && (
                      <p className="text-xs text-muted-foreground/40 text-center py-2">{T.strategyEditor.noContent}</p>
                    )}
                    {/* Annotations — view mode */}
                    {!spEditMode && (() => {
                      const ann = spAnnotations[selectedSP.id]
                      if (!ann || (!ann.note && ann.players.length === 0)) return null
                      return (
                        <div className="space-y-2 border-t border-white/8 pt-2">
                          {ann.players.length > 0 && (
                            <div className="flex flex-wrap gap-1.5">
                              {ann.players.map(p => (
                                <div key={p.id} className="inline-flex items-center gap-1.5 px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-white/10">
                                  <img src={p.avatar_url} alt={p.name} className="h-4 w-4 rounded-full shrink-0"
                                    onError={e => { (e.target as HTMLImageElement).src = '/default-avatar.png' }} />
                                  <span className="max-w-[72px] truncate">{p.name}</span>
                                </div>
                              ))}
                            </div>
                          )}
                          {ann.note && (
                            <p className="text-xs text-foreground/70 leading-relaxed whitespace-pre-wrap break-words bg-white/5 rounded px-2 py-1.5">{ann.note}</p>
                          )}
                        </div>
                      )
                    })()}

                    {/* Annotations edit form */}
                    {spEditMode && (() => {
                      const spId = selectedSP.id
                      const ann = spAnnotations[spId]
                      const assignedPlayers = ann?.players ?? []
                      const filteredMembers = memberOptions.filter(m =>
                        !assignedPlayers.some(p => p.id === m.id) &&
                        (m.display_name.toLowerCase().includes(spMemberSearch.toLowerCase()) ||
                          m.username.toLowerCase().includes(spMemberSearch.toLowerCase()))
                      )
                      const updateAnnotation = (patch: { note?: string; players?: { id: string; name: string; avatar_url: string }[] }) => {
                        const cur = spAnnotationsRef.current[spId] ?? { note: '', players: [] }
                        const updated = { ...spAnnotationsRef.current, [spId]: { ...cur, ...patch } }
                        spAnnotationsRef.current = updated
                        setSpAnnotations(updated)
                        scheduleSave()
                        broadcastSPState()
                      }
                      return (
                        <div className="space-y-2 border-t border-white/8 pt-2 min-w-0">
                          <p className="text-[10px] font-semibold text-foreground/40 tracking-wider uppercase">{T.strategyEditor.note}</p>
                          <textarea
                            rows={3}
                            placeholder={T.strategyEditor.notePlaceholder}
                            value={ann?.note ?? ''}
                            onChange={e => updateAnnotation({ note: e.target.value })}
                            className="w-full min-w-0 rounded bg-white/5 border border-white/10 px-2 py-1.5 text-xs text-foreground/90 resize-none focus:outline-none focus:border-primary/40 placeholder:text-muted-foreground/40"
                          />
                          <p className="text-[10px] font-semibold text-foreground/40 tracking-wider uppercase">{T.strategyEditor.players}</p>
                          {assignedPlayers.length > 0 && (
                            <div className="flex flex-wrap gap-1 mb-1">
                              {assignedPlayers.map(p => (
                                <div key={p.id} className="inline-flex items-center gap-1.5 px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-white/10">
                                  <img src={p.avatar_url} alt={p.name} className="h-4 w-4 rounded-full shrink-0"
                                    onError={e => { (e.target as HTMLImageElement).src = '/default-avatar.png' }} />
                                  <span className="max-w-[64px] truncate">{p.name}</span>
                                  <button onClick={() => updateAnnotation({ players: assignedPlayers.filter(x => x.id !== p.id) })}
                                    className="hover:text-red-400 transition-colors">
                                    <X className="h-2.5 w-2.5" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                          <div className="relative">
                            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-foreground/40 pointer-events-none" />
                            <input
                              ref={spMemberInputRef}
                              type="text"
                              value={spMemberSearch}
                              onChange={e => { setSpMemberSearch(e.target.value); setShowSpMemberDropdown(true) }}
                              onFocus={() => {
                                setShowSpMemberDropdown(true)
                                if (spMemberInputRef.current) {
                                  const rect = spMemberInputRef.current.getBoundingClientRect()
                                  const dropH = 220
                                  const flipUp = rect.bottom + dropH > window.innerHeight
                                  setSpMemberDropdownPos({ top: flipUp ? rect.top - dropH - 4 : rect.bottom + 4, left: rect.left, width: Math.max(rect.width, 200) })
                                }
                              }}
                              onBlur={() => setTimeout(() => setShowSpMemberDropdown(false), 150)}
                              placeholder={T.strategyEditor.searchMember}
                              className="w-full h-7 pl-7 pr-2 rounded bg-white/5 border border-white/10 text-xs text-foreground placeholder:text-foreground/30 focus:outline-none focus:border-primary"
                            />
                          </div>
                          {showSpMemberDropdown && filteredMembers.length > 0 && createPortal(
                            <div
                              style={{ position: 'fixed', top: spMemberDropdownPos.top, left: spMemberDropdownPos.left, width: spMemberDropdownPos.width, maxHeight: 220, zIndex: 9999 }}
                              className={`overflow-y-auto ${editorMenu}`}
                            >
                              {filteredMembers.map(m => (
                                <button
                                  key={m.id}
                                  onMouseDown={e => {
                                    e.preventDefault()
                                    updateAnnotation({ players: [...assignedPlayers, { id: m.id, name: m.display_name || m.username, avatar_url: m.avatar_url }] })
                                    setSpMemberSearch('')
                                    setShowSpMemberDropdown(false)
                                  }}
                                  className="w-full flex items-center gap-2 px-2 py-1.5 text-xs text-foreground hover:bg-white/10 transition-colors"
                                >
                                  <img src={m.avatar_url} alt={m.display_name || m.username} className="h-5 w-5 rounded-full shrink-0"
                                    onError={e => { (e.target as HTMLImageElement).src = '/default-avatar.png' }} />
                                  <span className="truncate">{m.display_name || m.username}</span>
                                  <span className="text-foreground/30 ml-auto shrink-0">{m.team}</span>
                                </button>
                              ))}
                            </div>,
                            document.body
                          )}
                        </div>
                      )
                    })()}

                    {/* Slayta ekleme — noktayı bu slaytta kalıcı kılar */}
                    {(() => {
                      const isPinned = pinnedSPIds.includes(selectedSP.id)
                      return (
                        <button
                          onClick={() => {
                            const cur = pinnedSPIdsRef.current
                            const next = isPinned ? cur.filter(id => id !== selectedSP.id) : [...cur, selectedSP.id]
                            pinnedSPIdsRef.current = next
                            setPinnedSPIds(next)
                            scheduleSave()
                            broadcastSPState()
                          }}
                          className={`flex h-8 w-full cursor-pointer items-center justify-center gap-1.5 rounded-sm border font-display text-[11px] font-bold uppercase tracking-[0.14em] transition-colors ${
                            isPinned
                              ? 'border-primary/60 bg-primary/15 text-foreground'
                              : 'border-white/10 bg-white/5 text-muted-foreground hover:bg-white/10 hover:text-foreground'
                          }`}
                        >
                          {isPinned ? <Check className="h-3.5 w-3.5" /> : <MapPin className="h-3.5 w-3.5" />}
                          {isPinned ? T.strategy.addedToSlide : T.strategy.addToSlide}
                        </button>
                      )
                    })()}
                  </div>
                ) : (tool === 'marker' && !activeMarkerData) ? (
                  <MarkersSidebar
                    selectedMarkerId={selectedMarker?.id ?? null}
                    onSelect={(src, id) => { setSelectedMarker({ src, id }) }}
                  />
                ) : activeMarkerData ? (
                  <MarkerPropertiesPanel
                    marker={activeMarkerData}
                    squadPresets={squadPresets}
                    onUpdate={(updated) => {
                      updateCurrentMarkers(prev => prev.map(m => m.id === updated.id ? updated : m))
                      scheduleSave()
                      broadcastMarkerState()
                    }}
                  />
                ) : (tool === 'select' && selectedArrowState) ? (
                  <ArrowPropertiesPanel
                    arrow={selectedArrowState}
                    squadPresets={squadPresets}
                    onUpdate={patch => updateArrowProperties(patch)}
                  />
                ) : (tool === 'arrow') ? (
                  <ArrowPropertiesPanel
                    arrow={{
                      color,
                      sw: strokeWidth,
                      ls: lineStyle,
                      ah: arrowHead,
                      ...arrowMetaDefaults,
                    }}
                    squadPresets={squadPresets}
                    onUpdate={patch => {
                      if (patch.color !== undefined) setColor(patch.color)
                      if (patch.sw !== undefined) setStrokeWidth(patch.sw)
                      if (patch.ls !== undefined) { setLineStyle(patch.ls); lineStyleRef.current = patch.ls }
                      if (patch.ah !== undefined) { setArrowHead(patch.ah); arrowHeadRef.current = patch.ah }
                      const metaPatch: Partial<ArrowMeta> = {}
                      if (patch.title !== undefined) metaPatch.title = patch.title
                      if (patch.text !== undefined) metaPatch.text = patch.text
                      if (patch.squads !== undefined) metaPatch.squads = patch.squads
                      if (patch.members !== undefined) metaPatch.members = patch.members
                      if (patch.startMarkerSrc !== undefined) metaPatch.startMarkerSrc = patch.startMarkerSrc
                      if (patch.vehicle !== undefined) metaPatch.vehicle = patch.vehicle
                      if (Object.keys(metaPatch).length > 0) {
                        setArrowMetaDefaults(prev => ({ ...prev, ...metaPatch }))
                      }
                    }}
                  />
                ) : (
                  <div className="min-w-0 flex-1 space-y-4 overflow-y-auto p-3">
                    {/* ── Aracın varsayılanları — bundan sonra çizilecek şekil ──
                        Dört ayrı yerde (araç, çokgen, çember, yazı) satır satır
                        kopyalanmış renk/kalınlık/stil blokları tek takıma indi
                        (`editorUi`): biri değiştiğinde diğer üçü eskimiyor ve
                        İngilizce kalmış etiketler çeviriden geliyor. */}
                    {drawingTools.includes(tool) && (
                      <ColorField
                        label={T.strategy.color}
                        custom={T.strategy.custom}
                        value={color}
                        onChange={setColor}
                      />
                    )}
                    {strokeTools.includes(tool) && (
                      <>
                        <SliderField
                          label={T.strategy.strokeWidth}
                          display={`${strokeWidth}px`}
                          value={strokeWidth} min={0.5} max={8} step={0.5}
                          onChange={setStrokeWidth}
                        />
                        <ChoiceField
                          label={T.strategy.lineStyle}
                          value={lineStyle}
                          options={lsOptions}
                          onChange={v => { setLineStyle(v); lineStyleRef.current = v }}
                        />
                      </>
                    )}
                    {fillTools.includes(tool) && (
                      <SliderField
                        label={T.strategy.fillOpacity}
                        display={`${Math.round(fillOpacity * 100)}%`}
                        value={Math.round(fillOpacity * 100)} min={0} max={100}
                        onChange={v => setFillOpacity(v / 100)}
                      />
                    )}
                    {tool === 'text' && (
                      <SliderField
                        label={T.strategy.fontSize}
                        display={`${fontSize}px`}
                        value={fontSize} min={10} max={72}
                        onChange={setFontSize}
                      />
                    )}

                    {/* ── Seçim modu: ya bir şeklin ayarları ya da ipucu ────── */}
                    {tool === 'select' && !selectedArrowState && !selectedPolygonState && !selectedCircleState && !selectedTextShapeState && (
                      <p className="text-xs leading-relaxed text-muted-foreground/70">
                        {T.strategy.selectHint}
                      </p>
                    )}

                    {tool === 'select' && selectedPolygonState && (() => {
                      const s = selectedPolygonState
                      return (
                        <>
                          <ColorField
                            label={T.strategy.color} custom={T.strategy.custom}
                            value={s.color} onChange={v => updatePolygonProperty('color', v)}
                          />
                          <SliderField
                            label={T.strategy.strokeWidth} display={`${s.sw}px`}
                            value={s.sw} min={0.5} max={8} step={0.5}
                            onChange={v => updatePolygonProperty('sw', v)}
                          />
                          <SliderField
                            label={T.strategy.fillOpacity} display={`${Math.round(s.fillOpacity * 100)}%`}
                            value={Math.round(s.fillOpacity * 100)} min={0} max={100}
                            onChange={v => updatePolygonProperty('fillOpacity', v / 100)}
                          />
                          <ChoiceField
                            label={T.strategy.lineStyle} value={s.ls} options={lsOptions}
                            onChange={v => updatePolygonProperty('ls', v)}
                          />
                        </>
                      )
                    })()}

                    {(tool === 'hotzone' || (tool === 'select' && selectedCircleState && selectedCircleGroupRef.current?.__hz)) && (
                      <p className="text-xs leading-relaxed text-white/60">{T.strategy.hotzoneNote}</p>
                    )}

                    {tool === 'select' && selectedCircleState && !selectedCircleGroupRef.current?.__hz && (() => {
                      const s = selectedCircleState
                      return (
                        <>
                          <ColorField
                            label={T.strategy.color} custom={T.strategy.custom}
                            value={s.color} onChange={v => updateCircleProperty('color', v)}
                          />
                          <SliderField
                            label={T.strategy.strokeWidth} display={`${s.sw}px`}
                            value={s.sw} min={0.5} max={8} step={0.5}
                            onChange={v => updateCircleProperty('sw', v)}
                          />
                          <SliderField
                            label={T.strategy.fillOpacity} display={`${Math.round(s.fillOpacity * 100)}%`}
                            value={Math.round(s.fillOpacity * 100)} min={0} max={100}
                            onChange={v => updateCircleProperty('fillOpacity', v / 100)}
                          />
                          <ChoiceField
                            label={T.strategy.lineStyle} value={s.ls} options={lsOptions}
                            onChange={v => updateCircleProperty('ls', v)}
                          />
                        </>
                      )
                    })()}

                    {tool === 'select' && selectedTextShapeState && (() => {
                      const s = selectedTextShapeState
                      return (
                        <>
                          <ColorField
                            label={T.strategy.color} custom={T.strategy.custom}
                            value={s.color} onChange={v => updateTextShapeProperty('color', v)}
                          />
                          <SliderField
                            label={T.strategy.fontSize} display={`${s.fontSize}px`}
                            value={s.fontSize} min={10} max={72}
                            onChange={v => updateTextShapeProperty('fontSize', v)}
                          />
                          <ChoiceField
                            label={T.strategy.background} value={s.bg} options={bgOptions}
                            onChange={v => updateTextShapeProperty('bg', v)}
                          />
                        </>
                      )
                    })()}

                    {tool === 'ping' && (
                      <p className="text-xs leading-relaxed text-muted-foreground/70">
                        {T.strategy.pingHint}
                      </p>
                    )}
                  </div>
                )
              )}
            </div>
          )
        })()}

      </div>

      </div>

      {/* ── SP media lightbox ──────────────────────────────────────────────── */}
      {spLightboxUrl && createPortal(
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/90 backdrop-blur-sm cursor-zoom-out"
          onClick={() => setSpLightboxUrl(null)}
        >
          <button
            onClick={() => setSpLightboxUrl(null)}
            className="absolute top-4 right-4 h-9 w-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
          {spLightboxType === 'youtube' ? (
            <div
              className="overflow-hidden rounded-sm shadow-2xl"
              style={{ width: 'min(85vw, 1280px)', aspectRatio: '16/9' }}
              onClick={e => e.stopPropagation()}
            >
              <iframe
                src={`https://www.youtube.com/embed/${spLightboxUrl}?autoplay=1`}
                className="w-full h-full"
                style={{ border: 'none' }}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          ) : spLightboxType === 'video' ? (
            <video
              src={spLightboxUrl}
              controls
              autoPlay
              className="rounded-sm shadow-2xl"
              style={{ maxWidth: 'min(85vw, 1280px)', maxHeight: '85vh' }}
              onClick={e => e.stopPropagation()}
            />
          ) : (
            <img
              src={spLightboxUrl}
              alt=""
              className="rounded-sm object-contain shadow-2xl"
              style={{ maxWidth: 'min(85vw, 1280px)', maxHeight: '85vh' }}
              onClick={e => e.stopPropagation()}
            />
          )}
        </div>,
        document.body,
      )}

      {/* Context menu */}
      {canDraw && contextMenu && (
        <>
          <div className="fixed inset-0 z-[9998]" onMouseDown={() => setContextMenu(null)} />
          <div
            className={`fixed z-[9999] min-w-[150px] py-1 ${editorMenu}`}
            style={{ left: contextMenu.x, top: contextMenu.y }}
          >
            {contextMenu.type === 'marker' && (
              <>
                <button
                  className={editorMenuItem}
                  onMouseDown={e => {
                    e.stopPropagation()
                    if (contextMenu.markerId) {
                      setActiveMarkerId(contextMenu.markerId)
                      setTool('select')
                    }
                    setContextMenu(null)
                  }}
                >
                  <Pencil className="h-3.5 w-3.5" />
                  {T.strategy.edit}
                </button>
                <button
                  className={editorMenuItem}
                  onMouseDown={e => {
                    e.stopPropagation()
                    if (contextMenu.markerId) {
                      const original = placedMarkersRef.current.find(m => m.id === contextMenu.markerId)
                      if (original) {
                        const dupe: PlacedMarker = {
                          id: crypto.randomUUID(),
                          src: original.src,
                          worldX: original.worldX + 20,
                          worldY: original.worldY + 20,
                          color: original.color,
                          title: '', text: '', squads: [], members: [],
                        }
                        updateCurrentMarkers(prev => [...prev, dupe])
                        setActiveMarkerId(dupe.id)
                        scheduleSave()
                        broadcastMarkerState()
                      }
                    }
                    setContextMenu(null)
                  }}
                >
                  <Copy className="h-3.5 w-3.5" />
                  {T.strategy.duplicate}
                </button>
              </>
            )}
            <button
              className={editorMenuItemDanger}
              onMouseDown={e => { e.stopPropagation(); handleContextDelete() }}
            >
              <Trash2 className="h-3.5 w-3.5" />
              {T.strategy.delete}
            </button>
          </div>
        </>
      )}
    </div>
  )
}
